import type { ReactNode } from 'react';
import {
  ClipboardCheck,
  BookOpen,
  Rocket,
  Shield,
  TreePine,
  Camera,
  Upload,
  Map,
  Smartphone,
  Settings,
  User,
  Server,
  HelpCircle,
  ExternalLink,
  Film,
  Copy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  InfoBox,
  WarningBox,
  SectionHeading,
  Paragraph,
  Code,
  DocTable,
  BulletList,
  NumberedList,
} from './docs-components';
import { useQuery } from '@tanstack/react-query';
import type { VideoInstructionDto } from '@forest/contracts';
import { api } from '@/lib/api-client';
import { Spinner } from '@/components/ui/Spinner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DocSection = {
  id: string;
  title: string;
  icon: LucideIcon;
  content: ReactNode;
};

type Lang = 'ro' | 'ru' | 'en';

// ---------------------------------------------------------------------------
// Platform link (shared across languages)
// ---------------------------------------------------------------------------

function PlatformLink() {
  return (
    <a
      href="https://www.pnerp.org"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium text-forest-600 underline decoration-forest-300 hover:text-forest-700"
    >
      www.pnerp.org <ExternalLink className="h-3 w-3" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Architecture diagram (shared)
// ---------------------------------------------------------------------------

function ArchitectureDiagram() {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-4">
      <pre className="text-xs leading-relaxed text-slate-700">
{`  ┌──────────────┐     ┌──────────────────┐     ┌──────────────┐
  │  Aplicatie    │     │   Aplicatie Web   │     │  Aplicatie    │
  │  Mobila       │     │   (React SPA)     │     │  Mobila       │
  │  (Expo)       │     │   Vercel          │     │  (Expo)       │
  └──────┬───────┘     └────────┬──────────┘     └──────┬───────┘
         │                      │                        │
         └──────────────────────┼────────────────────────┘
                                │ HTTPS
                      ┌─────────▼─────────┐
                      │   API Server      │
                      │   (Fastify)       │
                      │   Fly.io          │
                      └────┬─────────┬────┘
                           │         │
                ┌──────────▼──┐  ┌───▼──────────┐
                │ PostgreSQL  │  │ Cloudflare   │
                │ (date,      │  │ R2 (fisiere, │
                │  GeoJSON)   │  │  fotografii) │
                └─────────────┘  └──────────────┘`}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section builders per language
// ---------------------------------------------------------------------------

function overviewSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          <strong>PNERP</strong> (Programul National de Extindere a Resurselor de Paduri) este
          platforma digitala destinata gestionarii parcelelor forestiere si a fotografiilor
          georeferentiate pentru serviciul silvic al Republicii Moldova. Sistemul permite
          inregistrarea, monitorizarea si analiza resurselor forestiere intr-un mod centralizat
          si eficient.
        </Paragraph>
        <SectionHeading>Scopul platformei</SectionHeading>
        <Paragraph>
          Platforma PNERP a fost creata pentru a digitaliza procesul de gestionare a parcelelor
          forestiere. Lucratorii silvici pot inregistra parcele, atasa fotografii georeferentiate,
          importa date din fisiere Excel si vizualiza informatiile pe harta interactiva. Toate
          datele sunt centralizate si accesibile in functie de rolul utilizatorului.
        </Paragraph>
        <SectionHeading>Functionalitati principale</SectionHeading>
        <BulletList
          items={[
            <><strong>Gestionarea parcelelor</strong> — crearea, editarea si vizualizarea parcelelor forestiere cu date geometrice (poligoane GeoJSON)</>,
            <><strong>Baza de date fotografii</strong> — incarcarea si gestionarea fotografiilor georeferentiate cu atribuire automata la parcele bazata pe coordonate GPS</>,
            <><strong>Import date</strong> — import in masa din fisiere Excel (<Code>meta_3.xlsx</Code> pentru parcele, <Code>meta_3_photo.xlsx</Code> pentru fotografii)</>,
            <><strong>Filtrare si harta</strong> — filtrarea avansata a datelor si vizualizarea pe harta interactiva cu straturi de poligoane si markere; filtrele dropdown sunt <strong>multi-select</strong> (de la v0.9.3) — selectati simultan mai multe raioane / ani / status-uri / ocoale silvice</>,
            <><strong>Comentarii</strong> — adaugarea de comentarii la parcele si fotografii pentru comunicare intre echipe</>,
            <><strong>Aplicatia mobila</strong> — aplicatie dedicata pentru capturarea fotografiilor in teren cu suport offline</>,
            <><strong>Administrare</strong> — gestionarea utilizatorilor, rolurilor, entitatilor silvice (acum <strong>liste multiple per utilizator</strong>) si setarilor sistemului; pagina noua <strong>Regiuni</strong> (de la v0.9.3) importa tabelele canonice de Primarii / Entitati Silvice / Ocoale Silvice din regions.xlsx</>,
            <><strong>Autocomplete pentru entitati de referinta</strong> (de la v0.9.3) — formularele de creare parcela / fotografie sugereaza valorile canonice pentru primarie / entitate silvica / ocol silvic, cu fallback la text liber</>,
          ]}
        />
        <SectionHeading>Acces la platforma</SectionHeading>
        <Paragraph>
          Platforma este accesibila la adresa <PlatformLink />. Toti utilizatorii trebuie sa fie
          autentificati pentru a accesa functionalitatile sistemului.
        </Paragraph>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          <strong>PNERP</strong> (Национальная программа расширения лесных ресурсов) — это
          цифровая платформа для управления лесными участками и геореференцированными
          фотографиями лесной службы Республики Молдова. Система позволяет регистрировать,
          отслеживать и анализировать лесные ресурсы централизованно и эффективно.
        </Paragraph>
        <SectionHeading>Назначение платформы</SectionHeading>
        <Paragraph>
          Платформа PNERP создана для цифровизации процесса управления лесными участками.
          Лесные работники могут регистрировать участки, прикреплять геореференцированные
          фотографии, импортировать данные из файлов Excel и просматривать информацию на
          интерактивной карте. Все данные централизованы и доступны в зависимости от роли
          пользователя.
        </Paragraph>
        <SectionHeading>Основные возможности</SectionHeading>
        <BulletList
          items={[
            <><strong>Управление участками</strong> — создание, редактирование и просмотр лесных участков с геометрическими данными (полигоны GeoJSON)</>,
            <><strong>База данных фотографий</strong> — загрузка и управление геореференцированными фотографиями с автоматической привязкой к участкам на основе GPS-координат</>,
            <><strong>Импорт данных</strong> — массовый импорт из файлов Excel (<Code>meta_3.xlsx</Code> для участков, <Code>meta_3_photo.xlsx</Code> для фотографий)</>,
            <><strong>Фильтрация и карта</strong> — расширенная фильтрация данных и отображение на интерактивной карте со слоями полигонов и маркерами; выпадающие фильтры теперь <strong>multi-select</strong> (с v0.9.3) — выбирайте несколько районов / лет / статусов / лесничеств одновременно</>,
            <><strong>Комментарии</strong> — добавление комментариев к участкам и фотографиям для коммуникации между командами</>,
            <><strong>Мобильное приложение</strong> — специализированное приложение для съёмки фотографий в полевых условиях с поддержкой офлайн-режима</>,
            <><strong>Администрирование</strong> — управление пользователями, ролями, лесными организациями (теперь <strong>несколько на одного пользователя</strong>) и системными настройками; новая страница <strong>Регионы</strong> (с v0.9.3) импортирует канонические таблицы Примэрий / Лесных организаций / Лесничеств из regions.xlsx</>,
            <><strong>Автозаполнение справочных значений</strong> (с v0.9.3) — формы создания участка / фотографии предлагают канонические значения для примэрии / лесной организации / лесничества с возможностью свободного ввода</>,
          ]}
        />
        <SectionHeading>Доступ к платформе</SectionHeading>
        <Paragraph>
          Платформа доступна по адресу <PlatformLink />. Все пользователи должны
          авторизоваться для доступа к функциям системы.
        </Paragraph>
      </>
    ),
    en: (
      <>
        <Paragraph>
          <strong>PNERP</strong> (National Program for Forest Resource Expansion) is a digital
          platform for managing forest parcels and georeferenced photographs for the forestry
          service of the Republic of Moldova. The system enables centralized and efficient
          registration, monitoring, and analysis of forest resources.
        </Paragraph>
        <SectionHeading>Platform Purpose</SectionHeading>
        <Paragraph>
          The PNERP platform was created to digitize the management of forest parcels. Forestry
          workers can register parcels, attach georeferenced photographs, import data from Excel
          files, and view information on an interactive map. All data is centralized and
          accessible based on the user's role.
        </Paragraph>
        <SectionHeading>Key Features</SectionHeading>
        <BulletList
          items={[
            <><strong>Parcel management</strong> — creating, editing, and viewing forest parcels with geometric data (GeoJSON polygons)</>,
            <><strong>Photo database</strong> — uploading and managing georeferenced photos with automatic parcel assignment based on GPS coordinates</>,
            <><strong>Data import</strong> — bulk import from Excel files (<Code>meta_3.xlsx</Code> for parcels, <Code>meta_3_photo.xlsx</Code> for photos)</>,
            <><strong>Filters & map</strong> — advanced data filtering and interactive map visualization with polygon layers and markers; dropdown filters are now <strong>multi-select</strong> (since v0.9.3) — pick several districts / years / statuses / forest districts at once</>,
            <><strong>Comments</strong> — adding comments to parcels and photos for team communication</>,
            <><strong>Mobile app</strong> — dedicated app for capturing field photographs with offline support</>,
            <><strong>Administration</strong> — managing users, roles, forestry entities (now <strong>multi-value per user</strong>), and system settings; new <strong>Regions</strong> page (since v0.9.3) imports the canonical Mayoralties / Forestry Entities / Forest Districts tables from regions.xlsx</>,
            <><strong>Reference autocomplete</strong> (since v0.9.3) — parcel / photo creation forms suggest canonical values for mayoralty / forestry entity / forest district, with free-text fallback</>,
          ]}
        />
        <SectionHeading>Platform Access</SectionHeading>
        <Paragraph>
          The platform is available at <PlatformLink />. All users must be authenticated to
          access system features.
        </Paragraph>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Prezentare generala',
    ru: 'Обзор',
    en: 'Overview',
  };

  return { id: 'overview', title: titles[lang], icon: BookOpen, content: content[lang] };
}

// ---------------------------------------------------------------------------

function gettingStartedSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Acest ghid va ajuta sa incepceti lucrul cu platforma PNERP. Urmati pasii de mai jos
          pentru a va configura contul si a intelege interfata.
        </Paragraph>
        <SectionHeading>Inregistrarea contului</SectionHeading>
        <NumberedList
          items={[
            <>Accesati pagina de inregistrare de la adresa <Code>/register</Code>.</>,
            <>Introduceti un <strong>nume de utilizator</strong> unic si o <strong>parola</strong> sigura (minim 8 caractere, cel putin o litera mare, o litera mica, o cifra si un caracter special).</>,
            <>Completati <strong>numele complet</strong> care va fi afisat in sistem.</>,
            <>Apasati butonul <strong>Inregistrare</strong>. Contul va fi creat imediat.</>,
          ]}
        />
        <InfoBox>
          Dupa inregistrare, un administrator trebuie sa va atribuie o <strong>entitate silvica</strong> pentru
          a putea crea sau importa date. Contactati administratorul dumneavoastra pentru aceasta configurare.
        </InfoBox>
        <SectionHeading>Autentificarea</SectionHeading>
        <NumberedList
          items={[
            <>Accesati pagina de autentificare de la adresa <Code>/login</Code>.</>,
            <>Introduceti <strong>numele de utilizator</strong> si <strong>parola</strong>.</>,
            <>Apasati <strong>Autentificare</strong>. Veti fi redirectionati catre panoul de lucru.</>,
          ]}
        />
        <SectionHeading>Configurarea initiala a profilului</SectionHeading>
        <Paragraph>
          Dupa prima autentificare, recomandam sa va configurati profilul accesand pagina
          <strong> Profil</strong> (click pe numele dumneavoastra din bara laterala):
        </Paragraph>
        <BulletList
          items={[
            <><strong>Date personale</strong> — verificati si actualizati numele complet, adresa de email si limba preferata.</>,
            <><strong>Metadate implicite</strong> — setati valorile implicite pentru <em>Raion</em>, <em>Primarie</em>, <em>UCF</em> si <em>Ocol silvic</em>. Acestea vor fi folosite automat la incarcarea fotografiilor.</>,
            <><strong>Entitate silvica</strong> — verificati ca aveti o entitate silvica atribuita. Aceasta este necesara pentru crearea datelor.</>,
          ]}
        />
        <SectionHeading>Structura interfetei</SectionHeading>
        <Paragraph>
          Interfata platformei este organizata in urmatoarele zone:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Bara laterala (stanga)</strong> — meniul de navigare principal cu link-uri catre: Parcele, Baza de date (fotografii), Import, Filtrare si harta. Administratorii vad si sectiunea de administrare.</>,
            <><strong>Antetul</strong> — contine selectorul de limba si informatii despre sesiune.</>,
            <><strong>Zona de continut (centru)</strong> — afiseaza pagina activa cu tabelele, formularele sau hartile corespunzatoare.</>,
            <><strong>Profilul utilizatorului</strong> — in partea de jos a barei laterale se afiseaza numele, rolul si butonul de deconectare.</>,
          ]}
        />
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Это руководство поможет вам начать работу с платформой PNERP. Следуйте приведённым
          ниже шагам для настройки учётной записи и знакомства с интерфейсом.
        </Paragraph>
        <SectionHeading>Регистрация учётной записи</SectionHeading>
        <NumberedList
          items={[
            <>Перейдите на страницу регистрации по адресу <Code>/register</Code>.</>,
            <>Введите уникальное <strong>имя пользователя</strong> и надёжный <strong>пароль</strong> (минимум 8 символов, хотя бы одна заглавная и строчная буква, цифра и специальный символ).</>,
            <>Заполните <strong>полное имя</strong>, которое будет отображаться в системе.</>,
            <>Нажмите кнопку <strong>Регистрация</strong>. Учётная запись будет создана немедленно.</>,
          ]}
        />
        <InfoBox>
          После регистрации администратор должен назначить вам <strong>лесную организацию</strong>,
          чтобы вы могли создавать или импортировать данные. Свяжитесь с вашим администратором
          для этой настройки.
        </InfoBox>
        <SectionHeading>Авторизация</SectionHeading>
        <NumberedList
          items={[
            <>Перейдите на страницу авторизации по адресу <Code>/login</Code>.</>,
            <>Введите <strong>имя пользователя</strong> и <strong>пароль</strong>.</>,
            <>Нажмите <strong>Войти</strong>. Вы будете перенаправлены на рабочую панель.</>,
          ]}
        />
        <SectionHeading>Начальная настройка профиля</SectionHeading>
        <Paragraph>
          После первой авторизации рекомендуем настроить профиль, перейдя на страницу
          <strong> Профиль</strong> (нажмите на своё имя в боковой панели):
        </Paragraph>
        <BulletList
          items={[
            <><strong>Личные данные</strong> — проверьте и обновите полное имя, адрес электронной почты и предпочитаемый язык.</>,
            <><strong>Метаданные по умолчанию</strong> — задайте значения по умолчанию для <em>Района</em>, <em>Примэрии</em>, <em>UCF</em> и <em>Лесничества</em>. Они будут автоматически подставляться при загрузке фотографий.</>,
            <><strong>Лесная организация</strong> — убедитесь, что вам назначена лесная организация. Это необходимо для создания данных.</>,
          ]}
        />
        <SectionHeading>Структура интерфейса</SectionHeading>
        <Paragraph>
          Интерфейс платформы организован в следующие зоны:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Боковая панель (слева)</strong> — главное навигационное меню со ссылками на: Участки, Базу данных (фотографии), Импорт, Фильтрацию и карту. Администраторы также видят раздел администрирования.</>,
            <><strong>Заголовок</strong> — содержит переключатель языка и информацию о сессии.</>,
            <><strong>Область контента (центр)</strong> — отображает активную страницу с соответствующими таблицами, формами или картами.</>,
            <><strong>Профиль пользователя</strong> — в нижней части боковой панели отображаются имя, роль и кнопка выхода.</>,
          ]}
        />
      </>
    ),
    en: (
      <>
        <Paragraph>
          This guide will help you get started with the PNERP platform. Follow the steps below
          to set up your account and understand the interface.
        </Paragraph>
        <SectionHeading>Account Registration</SectionHeading>
        <NumberedList
          items={[
            <>Go to the registration page at <Code>/register</Code>.</>,
            <>Enter a unique <strong>username</strong> and a secure <strong>password</strong> (minimum 8 characters, at least one uppercase letter, one lowercase letter, one digit, and one special character).</>,
            <>Fill in your <strong>full name</strong> that will be displayed in the system.</>,
            <>Click the <strong>Register</strong> button. Your account will be created immediately.</>,
          ]}
        />
        <InfoBox>
          After registration, an administrator must assign you a <strong>forestry entity</strong> so
          you can create or import data. Contact your administrator for this setup.
        </InfoBox>
        <SectionHeading>Authentication</SectionHeading>
        <NumberedList
          items={[
            <>Go to the login page at <Code>/login</Code>.</>,
            <>Enter your <strong>username</strong> and <strong>password</strong>.</>,
            <>Click <strong>Log in</strong>. You will be redirected to the work panel.</>,
          ]}
        />
        <SectionHeading>Initial Profile Setup</SectionHeading>
        <Paragraph>
          After your first login, we recommend configuring your profile by visiting the
          <strong> Profile</strong> page (click your name in the sidebar):
        </Paragraph>
        <BulletList
          items={[
            <><strong>Personal data</strong> — verify and update your full name, email address, and preferred language.</>,
            <><strong>Default metadata</strong> — set default values for <em>District</em>, <em>Municipality</em>, <em>UCF</em>, and <em>Forestry office</em>. These will be used automatically when uploading photos.</>,
            <><strong>Forestry entity</strong> — verify that you have an assigned forestry entity. This is required for creating data.</>,
          ]}
        />
        <SectionHeading>Interface Structure</SectionHeading>
        <Paragraph>
          The platform interface is organized into the following areas:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Sidebar (left)</strong> — main navigation menu with links to: Parcels, Database (photos), Import, Filters & map. Administrators also see the administration section.</>,
            <><strong>Header</strong> — contains the language selector and session information.</>,
            <><strong>Content area (center)</strong> — displays the active page with corresponding tables, forms, or maps.</>,
            <><strong>User profile</strong> — at the bottom of the sidebar, displays the name, role, and logout button.</>,
          ]}
        />
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Primii pasi',
    ru: 'Начало работы',
    en: 'Getting Started',
  };

  return { id: 'getting-started', title: titles[lang], icon: Rocket, content: content[lang] };
}

// ---------------------------------------------------------------------------

function rolesSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Sistemul PNERP utilizeaza un model de 4 roluri ierarhice. Fiecare rol defineste
          nivelul de acces la date si functionalitati. Rolul este atribuit de un administrator
          la crearea sau modificarea contului de utilizator.
        </Paragraph>
        <DocTable
          headers={['Rol', 'Vizualizare', 'Adaugare / Editare', 'Stergere', 'Gestionare utilizatori']}
          rows={[
            ['Super Admin', 'Toate datele', 'Da', 'Da', 'Da (toate rolurile)'],
            ['Controlor', 'Toate datele', 'Da, adauga/editeaza/comenteaza', 'NU', 'Nu'],
            ['Administrator', 'Propriile date', 'Da', 'Da', 'Da (doar Ajutor Administrator)'],
            ['Ajutor Administrator', 'Datele administratorului parinte', 'Da', 'Da', 'Nu'],
          ]}
        />
        <SectionHeading>Super Admin</SectionHeading>
        <Paragraph>
          Rolul cu cel mai inalt nivel de acces. <strong>Super Admin</strong> poate vizualiza,
          edita si sterge orice date din sistem, indiferent de proprietar. Are acces la toate
          paginile de administrare, inclusiv jurnalul de audit, setarile sistemului si
          gestionarea tuturor utilizatorilor. Poate atribui orice rol, inclusiv alte conturi de
          Super Admin.
        </Paragraph>
        <SectionHeading>Controlor</SectionHeading>
        <Paragraph>
          Rolul de <strong>Controlor</strong> este destinat personalului de supraveghere si
          control. Controlorii pot vizualiza toate datele din sistem (parcele, fotografii,
          comentarii) si pot adauga sau edita inregistrari. De asemenea, pot adauga comentarii
          la parcele si fotografii. <strong>Nu pot sterge</strong> date si nu pot gestiona
          utilizatori.
        </Paragraph>
        <WarningBox>
          Controlorii au acces de vizualizare la toate datele, dar nu pot efectua operatiuni
          de stergere. Aceasta restrictie asigura integritatea datelor in procesul de verificare.
        </WarningBox>
        <SectionHeading>Administrator</SectionHeading>
        <Paragraph>
          <strong>Administratorul</strong> gestioneaza propriile date si poate crea conturi de
          tip <em>Ajutor Administrator</em>. Vede si gestioneaza doar datele create de el insusi
          si de ajutorii sai. Are dreptul de a adauga, edita si sterge parcele si fotografii
          in cadrul entitatii silvice atribuite.
        </Paragraph>
        <SectionHeading>Ajutor Administrator</SectionHeading>
        <Paragraph>
          <strong>Ajutor Administrator</strong> lucreaza sub un Administrator parinte si are
          acces la datele acestuia. Poate adauga si edita parcele si fotografii, dar nu poate
          gestiona utilizatori. Datele create de un Ajutor Administrator sunt vizibile si
          pentru Administratorul parinte.
        </Paragraph>
        <SectionHeading>Editarea fotografiilor (din 2026-09-24)</SectionHeading>
        <Paragraph>
          Pentru fotografii exista o regula separata de editare, care urmeaza entitatea silvica:
        </Paragraph>
        <DocTable
          headers={['Rol', 'Poate edita o fotografie cand']}
          rows={[
            ['Super Admin, Controlor', 'intotdeauna'],
            ['Administrator', 'fotografia apartine uneia dintre entitatile silvice atribuite (entitatea proprie a fotografiei sau a poligonului ei), sau a incarcat-o el insusi'],
            ['Ajutor Administrator', 'a incarcat-o el insusi (isi vede intotdeauna propriile incarcari)'],
          ]}
        />
        <Paragraph>
          Actiunile <strong>Editare</strong> si <strong>Asigneaza</strong>, precum si mutarea
          marcatorului pe harta fotografiei, apar doar acolo unde sunt permise. Regulile de
          stergere nu s-au schimbat.
        </Paragraph>
        <SectionHeading>Lantul de proprietate</SectionHeading>
        <Paragraph>
          Modelul de proprietate functioneaza astfel: datele create de un <em>Ajutor
          Administrator</em> sunt asociate Administratorului parinte. Administratorul poate
          vedea si gestiona toate datele ajutorilor sai. <em>Super Admin</em> si
          <em> Controlor</em> pot vedea datele tuturor utilizatorilor.
        </Paragraph>
        <InfoBox>
          Fiecare utilizator care creeaza date (parcele, fotografii) trebuie sa aiba o
          <strong> Entitate silvica</strong> atribuita. Fara aceasta, operatiunile de creare
          si import vor fi blocate.
        </InfoBox>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Система PNERP использует модель из 4 иерархических ролей. Каждая роль определяет
          уровень доступа к данным и функциям. Роль назначается администратором при создании
          или изменении учётной записи пользователя.
        </Paragraph>
        <DocTable
          headers={['Роль', 'Просмотр', 'Добавление / Редактирование', 'Удаление', 'Управление пользователями']}
          rows={[
            ['Super Admin', 'Все данные', 'Да', 'Да', 'Да (все роли)'],
            ['Controlor', 'Все данные', 'Да, добавляет/редактирует/комментирует', 'НЕТ', 'Нет'],
            ['Administrator', 'Собственные данные', 'Да', 'Да', 'Да (только Ajutor Administrator)'],
            ['Ajutor Administrator', 'Данные родительского администратора', 'Да', 'Да', 'Нет'],
          ]}
        />
        <SectionHeading>Super Admin</SectionHeading>
        <Paragraph>
          Роль с наивысшим уровнем доступа. <strong>Super Admin</strong> может просматривать,
          редактировать и удалять любые данные в системе независимо от владельца. Имеет доступ
          ко всем страницам администрирования, включая журнал аудита, системные настройки и
          управление всеми пользователями. Может назначать любую роль, включая другие учётные
          записи Super Admin.
        </Paragraph>
        <SectionHeading>Controlor</SectionHeading>
        <Paragraph>
          Роль <strong>Controlor</strong> предназначена для персонала по надзору и контролю.
          Контролёры могут просматривать все данные в системе (участки, фотографии, комментарии)
          и могут добавлять или редактировать записи. Также могут добавлять комментарии к участкам
          и фотографиям. <strong>Не могут удалять</strong> данные и не могут управлять
          пользователями.
        </Paragraph>
        <WarningBox>
          Контролёры имеют доступ на просмотр всех данных, но не могут выполнять операции
          удаления. Это ограничение обеспечивает целостность данных в процессе проверки.
        </WarningBox>
        <SectionHeading>Administrator</SectionHeading>
        <Paragraph>
          <strong>Administrator</strong> управляет собственными данными и может создавать учётные
          записи типа <em>Ajutor Administrator</em>. Видит и управляет только данными, созданными
          им самим и его помощниками. Имеет право добавлять, редактировать и удалять участки и
          фотографии в рамках назначенной лесной организации.
        </Paragraph>
        <SectionHeading>Ajutor Administrator</SectionHeading>
        <Paragraph>
          <strong>Ajutor Administrator</strong> работает под родительским Администратором и имеет
          доступ к его данным. Может добавлять и редактировать участки и фотографии, но не может
          управлять пользователями. Данные, созданные Ajutor Administrator, видны и родительскому
          Администратору.
        </Paragraph>
        <SectionHeading>Редактирование фотографий (с 2026-09-24)</SectionHeading>
        <Paragraph>
          Для фотографий действует отдельное правило редактирования, которое следует лесной организации:
        </Paragraph>
        <DocTable
          headers={['Роль', 'Может редактировать фотографию, когда']}
          rows={[
            ['Super Admin, Controlor', 'всегда'],
            ['Administrator', 'фотография относится к одной из назначенных ему лесных организаций (собственная организация фотографии или её полигона), или он загрузил её сам'],
            ['Ajutor Administrator', 'он загрузил её сам (свои загрузки он видит всегда)'],
          ]}
        />
        <Paragraph>
          Действия <strong>Редактировать</strong> и <strong>Привязать</strong>, а также
          перемещение маркера на карте фотографии, показываются только там, где они разрешены.
          Правила удаления не изменились.
        </Paragraph>
        <SectionHeading>Цепочка владения</SectionHeading>
        <Paragraph>
          Модель владения работает следующим образом: данные, созданные <em>Ajutor
          Administrator</em>, привязаны к родительскому Администратору. Администратор может
          просматривать и управлять всеми данными своих помощников. <em>Super Admin</em> и
          <em> Controlor</em> могут видеть данные всех пользователей.
        </Paragraph>
        <InfoBox>
          Каждый пользователь, создающий данные (участки, фотографии), должен иметь назначенную
          <strong> лесную организацию</strong>. Без неё операции создания и импорта будут
          заблокированы.
        </InfoBox>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The PNERP system uses a hierarchical model with 4 roles. Each role defines the level
          of access to data and features. The role is assigned by an administrator when creating
          or modifying a user account.
        </Paragraph>
        <DocTable
          headers={['Role', 'View', 'Add / Edit', 'Delete', 'Manage Users']}
          rows={[
            ['Super Admin', 'All data', 'Yes', 'Yes', 'Yes (all roles)'],
            ['Controlor', 'All data', 'Yes, add/edit/comment', 'NO', 'No'],
            ['Administrator', 'Own data', 'Yes', 'Yes', 'Yes (only Ajutor Administrator)'],
            ['Ajutor Administrator', 'Parent administrator data', 'Yes', 'Yes', 'No'],
          ]}
        />
        <SectionHeading>Super Admin</SectionHeading>
        <Paragraph>
          The role with the highest access level. <strong>Super Admin</strong> can view, edit,
          and delete any data in the system regardless of ownership. Has access to all
          administration pages, including the audit log, system settings, and management of all
          users. Can assign any role, including other Super Admin accounts.
        </Paragraph>
        <SectionHeading>Controlor</SectionHeading>
        <Paragraph>
          The <strong>Controlor</strong> role is intended for supervisory and inspection staff.
          Controllers can view all data in the system (parcels, photos, comments) and can add or
          edit records. They can also add comments to parcels and photos. <strong>They cannot
          delete</strong> data and cannot manage users.
        </Paragraph>
        <WarningBox>
          Controllers have view access to all data but cannot perform delete operations. This
          restriction ensures data integrity during the verification process.
        </WarningBox>
        <SectionHeading>Administrator</SectionHeading>
        <Paragraph>
          The <strong>Administrator</strong> manages their own data and can create accounts of
          type <em>Ajutor Administrator</em>. They can see and manage only data created by
          themselves and their assistants. They have the right to add, edit, and delete parcels
          and photos within their assigned forestry entity.
        </Paragraph>
        <SectionHeading>Ajutor Administrator</SectionHeading>
        <Paragraph>
          <strong>Ajutor Administrator</strong> works under a parent Administrator and has access
          to their data. Can add and edit parcels and photos but cannot manage users. Data
          created by an Ajutor Administrator is also visible to the parent Administrator.
        </Paragraph>
        <SectionHeading>Editing photos (since 2026-09-24)</SectionHeading>
        <Paragraph>
          Photos have their own edit rule, which follows the forestry entity:
        </Paragraph>
        <DocTable
          headers={['Role', 'Can edit a photo when']}
          rows={[
            ['Super Admin, Controlor', 'always'],
            ['Administrator', 'the photo belongs to one of their assigned forestry entities (the photo\'s own entity or its polygon\'s), or they uploaded it'],
            ['Ajutor Administrator', 'they uploaded it themselves (they always see their own uploads)'],
          ]}
        />
        <Paragraph>
          The <strong>Edit</strong> and <strong>Assign</strong> actions, and dragging the marker
          on the photo map, appear only where they are allowed. Delete rules are unchanged.
        </Paragraph>
        <SectionHeading>Ownership Chain</SectionHeading>
        <Paragraph>
          The ownership model works as follows: data created by an <em>Ajutor Administrator</em>
          is associated with the parent Administrator. The Administrator can view and manage all
          data of their assistants. <em>Super Admin</em> and <em>Controlor</em> can see data of
          all users.
        </Paragraph>
        <InfoBox>
          Every user who creates data (parcels, photos) must have an assigned
          <strong> forestry entity</strong>. Without one, create and import operations will be
          blocked.
        </InfoBox>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Roluri si permisiuni',
    ru: 'Роли и разрешения',
    en: 'Roles & Permissions',
  };

  return { id: 'roles', title: titles[lang], icon: Shield, content: content[lang] };
}

// ---------------------------------------------------------------------------

function parcelsSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Pagina <strong>Poligoane</strong> (numita anterior <em>Parcele</em> — rebrand din
          2026-04-14; adresa URL ramane <Code>/app/parcels</Code>) este suprafata principala
          de lucru pentru gestionarea parcelelor forestiere. Fiecare parcela contine informatii
          despre locatie (poligon GeoJSON), suprafata, categoria de teren, anul plantarii si
          alte metadate.
        </Paragraph>
        <SectionHeading>Vizualizarea parcelelor</SectionHeading>
        <Paragraph>
          Tabelul principal afiseaza toate parcelele la care aveti acces, in functie de rolul
          dumneavoastra. Coloanele includ: numar cadastral, raion, primarie, ocol silvic, UCF,
          suprafata, an si actiuni disponibile.
        </Paragraph>
        <SectionHeading>Filtrare in tabel</SectionHeading>
        <Paragraph>
          Utilizati filtrele de deasupra tabelului pentru a restringe rezultatele:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Cautare text</strong> — filtreaza dupa numar cadastral sau denumire</>,
            <><strong>Raion</strong> — selectati raionul dorit din lista derulanta</>,
            <><strong>Ocol silvic</strong> — filtrati dupa ocolul silvic</>,
            <><strong>An</strong> — selectati anul plantarii</>,
          ]}
        />
        <SectionHeading>Actiuni disponibile</SectionHeading>
        <Paragraph>
          Pentru fiecare parcela, meniul de actiuni (butonul cu trei puncte) ofera urmatoarele
          optiuni:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Imagine (SVG)</strong> — vizualizarea poligonului parcelei ca imagine vectoriala</>,
            <><strong>Harta</strong> — deschiderea parcelei pe harta interactiva</>,
            <><strong>Editare</strong> — modificarea datelor parcelei (disponibil in functie de rol)</>,
            <><strong>Stergere</strong> — stergerea parcelei (numai Super Admin si Administrator pentru propriile parcele)</>,
            <><strong>Restaurare</strong> — recuperarea unei parcele sterse anterior</>,
          ]}
        />
        <SectionHeading>Crearea manuala a unei parcele</SectionHeading>
        <NumberedList
          items={[
            <>Apasati butonul <strong>Adauga parcela</strong> din partea de sus a paginii.</>,
            <>Completati campurile obligatorii: numar cadastral, raion, primarie, ocol silvic, UCF, suprafata, an.</>,
            <>Optional, adaugati coordonatele poligonului in format GeoJSON.</>,
            <>Apasati <strong>Salveaza</strong> pentru a inregistra parcela.</>,
          ]}
        />
        <SectionHeading>Autocomplete pentru primarie / entitate / ocol (v0.9.3)</SectionHeading>
        <Paragraph>
          Incepand cu versiunea 0.9.3, campurile <strong>Primarie</strong>,
          <strong> Entitate silvica</strong> si <strong>Ocol silvic</strong> din
          dialogul de creare/editare parcela folosesc o componenta
          <em> autocomplete</em> care sugereaza valorile canonice din tabelele de
          referinta importate prin pagina <em>Regiuni</em> (vezi sectiunea
          Administrare). Pe masura ce tastati, o lista derulanta nativa
          (<Code>&lt;datalist&gt;</Code>) afiseaza optiunile potrivite —
          de ex. la entitate apare <em>"BL — ÎSÎS Bălți"</em>, iar la primarie
          apare <em>"Anenii_Noi — Anenii_Noi · 1001"</em> (nume — raion · cod).
        </Paragraph>
        <Paragraph>
          Daca tabelele de referinta nu sunt inca importate, sau daca valoarea pe
          care doriti sa o introduceti nu exista in lista, puteti tasta liber
          orice text — autocomplete-ul nu va impiedica introducerea valorilor in
          afara listei (Faza A, fara constrangeri FK).
        </Paragraph>
        <SectionHeading>Fotografii asociate</SectionHeading>
        <Paragraph>
          De pe pagina de detalii a unei parcele, puteti vizualiza toate fotografiile
          georeferentiate asociate automat sau manual. Fotografiile sunt afisate ca miniaturi
          cu informatii despre coordonate si data capturii.
        </Paragraph>
        <Paragraph>
          Harta poligonului arata si fotografiile lui; lista de fotografii afiseaza autorul,
          anul, specia de baza (SB), anul culturii si ultima lucrare. Apasand pe o fotografie se
          deschide fisa ei in Baza de date, iar butonul <em>Inapoi</em> revine la harta
          poligonului.
        </Paragraph>
        <SectionHeading>UCF complet</SectionHeading>
        <Paragraph>
          Campul <strong>UCF</strong> contine denumirea completa a unitatii (numarul parcelei plus
          litera, de ex. <Code>3A</Code>), nu doar litera. Poligoanele importate anterior au fost
          convertite automat.
        </Paragraph>
        <SectionHeading>Subpoligoane (din 2026-09-26)</SectionHeading>
        <Paragraph>
          Un <strong>subpoligon</strong> este o subdiviziune a unui poligon, cu contur si suprafata
          proprii si cu UCF-ul complet: <Code>1A1</Code> inseamna poligonul <Code>1A</Code>,
          subpoligonul <Code>1</Code>. Subpoligoanele se adauga prin import: dintr-un shapefile de
          subpoligoane sau din arhivele de receptie tehnica (vezi sectiunile Import si
          Receptii tehnice). Receptiile tehnice sunt acum subpoligoane.
        </Paragraph>
        <BulletList
          items={[
            <><strong>In lista Poligoane</strong>: prima coloana arata o sageata portocalie si numarul de subpoligoane pe randurile care au. Un clic deschide randul si afiseaza subpoligoanele lui: UCF, suprafata, campania de receptie si parcelele. Pe telefon, acelasi buton apare pe cardul poligonului.</>,
            <><strong>Cautare si filtru</strong>: cautarea gaseste si UCF-ul poligonului si UCF-ul unui subpoligon, parcelele sau arhiva lui. In panoul de filtre, <em>Subpoligoane: toate / Cu subpoligoane / Fara subpoligoane</em> restrange lista.</>,
            <><strong>Pe harta</strong>: stratul portocaliu <em>Subpoligoane</em> apare pe toate hartile, in comutatorul de straturi din dreapta-sus, activ implicit, peste poligoanele verzi; de la zoom 15 fiecare are eticheta cu UCF-ul complet. Un clic arata UCF-ul, poligonul parinte (sau <em>fara poligon</em>), suprafata, suprafata calculata cand difera, primaria, raionul, campania de receptie si parcelele.</>,
            <><strong>Subpoligoane fara poligon</strong>: o receptie care nu se afla in niciun poligon importat ramane subpoligon neatasat, desenat <em>punctat</em> pe harta. Butonul <em>Fara poligon (N)</em> din bara paginii Poligoane deschide lista lor, cu harta, pentru verificare. Se ataseaza automat dupa ce poligonul lor este importat si arhiva receptiei este importata din nou.</>,
            <><strong>In fereastra Harta a poligonului</strong>: lista subpoligoanelor, cu suprafata fiecaruia si totalul. Cel care le-a importat sau un Super Admin poate sterge un subpoligon (stergerea se confirma cu un al doilea clic); la fel si in randul deschis din lista.</>,
            <><strong>La stergerea definitiva a poligonului</strong> dispar si subpoligoanele lui.</>,
          ]}
        />
        <SectionHeading>Comentarii</SectionHeading>
        <Paragraph>
          Fiecare parcela poate avea comentarii adaugate de utilizatori autorizati. Comentariile
          sunt afisate cronologic si includ numele autorului si data adaugarii. Aceasta
          functionalitate faciliteaza comunicarea intre membrii echipei despre o parcela
          specifica.
        </Paragraph>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Страница <strong>Полигоны</strong> (ранее <em>Участки</em> — переименование от
          2026-04-14; URL остаётся <Code>/app/parcels</Code>) — это основная рабочая область
          для управления лесными участками. Каждый участок содержит информацию о
          местоположении (полигон GeoJSON), площади, категории земель, годе посадки и другие
          метаданные.
        </Paragraph>
        <SectionHeading>Просмотр участков</SectionHeading>
        <Paragraph>
          Основная таблица отображает все участки, к которым у вас есть доступ в зависимости
          от вашей роли. Столбцы включают: кадастровый номер, район, примэрию, лесничество,
          UCF, площадь, год и доступные действия.
        </Paragraph>
        <SectionHeading>Фильтрация в таблице</SectionHeading>
        <Paragraph>
          Используйте фильтры над таблицей для сужения результатов:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Текстовый поиск</strong> — фильтрация по кадастровому номеру или названию</>,
            <><strong>Район</strong> — выберите нужный район из выпадающего списка</>,
            <><strong>Лесничество</strong> — фильтрация по лесничеству</>,
            <><strong>Год</strong> — выберите год посадки</>,
          ]}
        />
        <SectionHeading>Доступные действия</SectionHeading>
        <Paragraph>
          Для каждого участка меню действий (кнопка с тремя точками) предлагает следующие
          возможности:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Изображение (SVG)</strong> — просмотр полигона участка как векторного изображения</>,
            <><strong>Карта</strong> — открытие участка на интерактивной карте</>,
            <><strong>Редактирование</strong> — изменение данных участка (доступно в зависимости от роли)</>,
            <><strong>Удаление</strong> — удаление участка (только Super Admin и Administrator для собственных участков)</>,
            <><strong>Восстановление</strong> — восстановление ранее удалённого участка</>,
          ]}
        />
        <SectionHeading>Создание участка вручную</SectionHeading>
        <NumberedList
          items={[
            <>Нажмите кнопку <strong>Добавить участок</strong> в верхней части страницы.</>,
            <>Заполните обязательные поля: кадастровый номер, район, примэрия, лесничество, UCF, площадь, год.</>,
            <>При необходимости добавьте координаты полигона в формате GeoJSON.</>,
            <>Нажмите <strong>Сохранить</strong> для регистрации участка.</>,
          ]}
        />
        <SectionHeading>Автозаполнение для примэрии / организации / лесничества (v0.9.3)</SectionHeading>
        <Paragraph>
          Начиная с версии 0.9.3 поля <strong>Примэрия</strong>, <strong>Лесная
          организация</strong> и <strong>Лесничество</strong> в диалоге создания/
          редактирования участка используют компонент <em>автозаполнения</em>,
          предлагающий канонические значения из справочных таблиц, импортированных
          через страницу <em>Регионы</em> (см. раздел Администрирование). По мере
          ввода натуральный список (<Code>&lt;datalist&gt;</Code>) отображает
          подходящие варианты — например, для организации появляется
          <em> «BL — ÎSÎS Bălți»</em>, а для примэрии — <em>«Anenii_Noi —
          Anenii_Noi · 1001»</em> (имя — район · код).
        </Paragraph>
        <Paragraph>
          Если справочные таблицы ещё не импортированы или значение, которое вы
          хотите ввести, отсутствует в списке, можно ввести любой текст вручную —
          автозаполнение не препятствует вводу значений вне списка (Фаза A, без
          ограничений FK).
        </Paragraph>
        <SectionHeading>Связанные фотографии</SectionHeading>
        <Paragraph>
          На странице деталей участка можно просмотреть все геореференцированные фотографии,
          привязанные автоматически или вручную. Фотографии отображаются в виде миниатюр
          с информацией о координатах и дате съёмки.
        </Paragraph>
        <Paragraph>
          Карта полигона показывает и его фотографии; список фотографий отображает автора, год,
          основную породу (SB), год создания культуры и последнюю работу. Нажатие на фотографию
          открывает её карточку в Базе данных, а кнопка <em>Назад</em> возвращает к карте
          полигона.
        </Paragraph>
        <SectionHeading>Полный UCF</SectionHeading>
        <Paragraph>
          Поле <strong>UCF</strong> содержит полное обозначение единицы (номер парцеллы плюс буква,
          напр. <Code>3A</Code>), а не только букву. Ранее импортированные полигоны были
          преобразованы автоматически.
        </Paragraph>
        <SectionHeading>Подполигоны (с 2026-09-26)</SectionHeading>
        <Paragraph>
          <strong>Подполигон</strong> это часть полигона со своим контуром, площадью и полным UCF:
          <Code> 1A1</Code> означает полигон <Code>1A</Code>, подполигон <Code>1</Code>.
          Подполигоны добавляются импортом: из shapefile подполигонов или из архивов технических
          приёмок (см. разделы Импорт и Технические приёмки). Технические приёмки теперь являются
          подполигонами.
        </Paragraph>
        <BulletList
          items={[
            <><strong>В списке Полигоны</strong>: в первой колонке оранжевая стрелка и число подполигонов у строк, где они есть. Щелчок раскрывает строку и показывает её подполигоны: UCF, площадь, кампанию приёмки и участки. На телефоне та же кнопка есть на карточке полигона.</>,
            <><strong>Поиск и фильтр</strong>: поиск находит также UCF полигона и UCF подполигона, его участки или архив. В панели фильтров <em>Подполигоны: все / С подполигонами / Без подполигонов</em> сужает список.</>,
            <><strong>На карте</strong>: оранжевый слой <em>Подполигоны</em> есть на всех картах, в переключателе слоёв справа вверху, включён по умолчанию и рисуется поверх зелёных полигонов; с масштаба 15 у каждого подполигона подпись с полным UCF. Щелчок показывает UCF, родительский полигон (или <em>без полигона</em>), площадь, расчётную площадь, если она отличается, примэрию, район, кампанию приёмки и участки.</>,
            <><strong>Подполигоны без полигона</strong>: приёмка, не попадающая ни в один импортированный полигон, остаётся неприкреплённым подполигоном и рисуется на карте <em>пунктиром</em>. Кнопка <em>Без полигона (N)</em> на панели страницы Полигоны открывает их список с картой для проверки. Они прикрепляются автоматически, когда их полигон импортирован и архив приёмки импортирован повторно.</>,
            <><strong>В окне Карта полигона</strong>: список подполигонов с площадью каждого и итогом. Тот, кто их импортировал, или Super Admin может удалить подполигон (удаление подтверждается вторым щелчком); так же и в раскрытой строке списка.</>,
            <><strong>При окончательном удалении полигона</strong> удаляются и его подполигоны.</>,
          ]}
        />
        <SectionHeading>Комментарии</SectionHeading>
        <Paragraph>
          К каждому участку могут быть добавлены комментарии авторизованными пользователями.
          Комментарии отображаются в хронологическом порядке и включают имя автора и дату
          добавления. Эта функция облегчает коммуникацию между членами команды по конкретному
          участку.
        </Paragraph>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The <strong>Polygons</strong> page (previously <em>Parcels</em> — renamed
          2026-04-14; the URL stays <Code>/app/parcels</Code>) is the main work surface for
          managing forest parcels. Each parcel contains information about location (GeoJSON
          polygon), area, land category, planting year, and other metadata.
        </Paragraph>
        <SectionHeading>Viewing Parcels</SectionHeading>
        <Paragraph>
          The main table displays all parcels you have access to based on your role. Columns
          include: cadastral number, district, municipality, forestry office, UCF, area, year,
          and available actions.
        </Paragraph>
        <SectionHeading>Table Filtering</SectionHeading>
        <Paragraph>
          Use the filters above the table to narrow down results:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Text search</strong> — filter by cadastral number or name</>,
            <><strong>District</strong> — select the desired district from the dropdown</>,
            <><strong>Forestry office</strong> — filter by forestry office</>,
            <><strong>Year</strong> — select the planting year</>,
          ]}
        />
        <SectionHeading>Available Actions</SectionHeading>
        <Paragraph>
          For each parcel, the actions menu (three-dot button) offers the following options:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Image (SVG)</strong> — view the parcel polygon as a vector image</>,
            <><strong>Map</strong> — open the parcel on the interactive map</>,
            <><strong>Edit</strong> — modify parcel data (available depending on role)</>,
            <><strong>Delete</strong> — delete the parcel (Super Admin and Administrator for own parcels only)</>,
            <><strong>Restore</strong> — recover a previously deleted parcel</>,
          ]}
        />
        <SectionHeading>Manual Parcel Creation</SectionHeading>
        <NumberedList
          items={[
            <>Click the <strong>Add parcel</strong> button at the top of the page.</>,
            <>Fill in the required fields: cadastral number, district, municipality, forestry office, UCF, area, year.</>,
            <>Optionally, add polygon coordinates in GeoJSON format.</>,
            <>Click <strong>Save</strong> to register the parcel.</>,
          ]}
        />
        <SectionHeading>Autocomplete for municipality / entity / forest district (v0.9.3)</SectionHeading>
        <Paragraph>
          As of v0.9.3, the <strong>Municipality</strong>, <strong>Forestry
          entity</strong>, and <strong>Forest district</strong> fields in the parcel
          create/edit dialog use an <em>autocomplete</em> component that suggests
          canonical values from the reference tables imported via the
          <em> Regions</em> page (see the Administration section). As you type, a
          native dropdown (<Code>&lt;datalist&gt;</Code>) shows matching options —
          for entity, you'll see <em>"BL — ÎSÎS Bălți"</em>, and for municipality,
          <em> "Anenii_Noi — Anenii_Noi · 1001"</em> (name — district · code).
        </Paragraph>
        <Paragraph>
          If reference tables haven't been imported yet, or if the value you want
          isn't on the list, you can still type any text — autocomplete won't block
          you from entering values outside the list (Phase A, no FK constraints).
        </Paragraph>
        <SectionHeading>Associated Photos</SectionHeading>
        <Paragraph>
          From the parcel details page, you can view all georeferenced photos associated
          automatically or manually. Photos are displayed as thumbnails with coordinate and
          capture date information.
        </Paragraph>
        <Paragraph>
          The polygon map also shows its photos; the photo list shows the author, year, main
          species (SB), culture year and last operation. Clicking a photo opens its record in the
          Database, and <em>Back</em> returns to the polygon map.
        </Paragraph>
        <SectionHeading>Full UCF</SectionHeading>
        <Paragraph>
          The <strong>UCF</strong> field holds the full unit designation (parcel number plus
          letter, e.g. <Code>3A</Code>), not just the letter. Previously imported polygons were
          converted automatically.
        </Paragraph>
        <SectionHeading>Subpolygons (since 2026-09-26)</SectionHeading>
        <Paragraph>
          A <strong>subpolygon</strong> is a subdivision of a polygon, with its own outline, area
          and full UCF: <Code>1A1</Code> means polygon <Code>1A</Code>, subpolygon <Code>1</Code>.
          Subpolygons are added by import: from a subpolygon shapefile or from technical reception
          archives (see the Import and Technical receptions sections). Technical receptions are
          subpolygons now.
        </Paragraph>
        <BulletList
          items={[
            <><strong>In the Polygons list</strong>: the first column shows an orange arrow and the subpolygon count on rows that have any. A click expands the row and shows its subpolygons: UCF, area, reception campaign and parcels. On a phone the same button sits on the polygon card.</>,
            <><strong>Search and filter</strong>: search also finds the polygon's UCF and a subpolygon's UCF, parcels or archive. In the filter panel, <em>Subpolygons: all / With subpolygons / Without subpolygons</em> narrows the list.</>,
            <><strong>On the map</strong>: the orange <em>Subpolygons</em> layer is on every map, in the layer switcher at the top right, on by default and drawn above the green polygons; from zoom 15 each subpolygon is labelled with its full UCF. A click shows the UCF, parent polygon (or <em>no polygon</em>), area, computed area when it differs, mayoralty, district, reception campaign and parcels.</>,
            <><strong>Subpolygons with no polygon</strong>: a reception lying in no imported polygon stays an unattached subpolygon, drawn <em>dashed</em> on the map. The <em>No polygon (N)</em> button on the Polygons toolbar opens their list, with a map, for review. They attach automatically once their polygon is imported and the reception archive is imported again.</>,
            <><strong>In the polygon Map window</strong>: a list of its subpolygons with each area and the total. The person who imported them, or a Super Admin, can delete a subpolygon (confirmed with a second click); the same applies in an expanded list row.</>,
            <><strong>Permanently deleting a polygon</strong> also removes its subpolygons.</>,
          ]}
        />
        <SectionHeading>Comments</SectionHeading>
        <Paragraph>
          Each parcel can have comments added by authorized users. Comments are displayed
          chronologically and include the author's name and date added. This feature facilitates
          communication between team members about a specific parcel.
        </Paragraph>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Gestionarea parcelelor',
    ru: 'Управление участками',
    en: 'Managing Parcels',
  };

  return { id: 'parcels', title: titles[lang], icon: TreePine, content: content[lang] };
}

// ---------------------------------------------------------------------------

function photosSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Pagina <strong>Baza de date</strong> gestioneaza fotografiile georeferentiate
          colectate in teren. Fiecare fotografie contine coordonate GPS, data si ora capturii
          si este asociata automat sau manual cu o parcela forestiera.
        </Paragraph>
        <SectionHeading>Vizualizarea fotografiilor</SectionHeading>
        <Paragraph>
          Tabelul principal afiseaza fotografiile cu informatii despre: miniatura, coordonate,
          parcela asociata, raion, data capturii si statusul atribuirii. Puteti utiliza
          filtrele de deasupra tabelului pentru a restringe rezultatele.
        </Paragraph>
        <SectionHeading>Statusul fotografiilor (coduri de culoare)</SectionHeading>
        <Paragraph>
          Fiecare fotografie are un indicator de status bazat pe culoare care reflecta
          relatia sa cu poligoanele parcelelor:
        </Paragraph>
        <DocTable
          headers={['Culoare', 'Status', 'Descriere']}
          rows={[
            ['Verde', 'In poligon', 'Fotografia este situata in interiorul unui poligon de parcela. Atribuirea este automata si corecta.'],
            ['Galben', 'Ajustat automat', 'Fotografia se afla in afara oricarui poligon, dar la maximum 200m de unul. Este legata de poligonul cel mai apropiat; coordonatele GPS raman neschimbate.'],
            ['Rosu', 'Neasignat', 'Fotografia nu se afla in apropierea niciunui poligon. Necesita atribuire manuala de catre administrator.'],
            ['Portocaliu', 'Ajustat manual', 'Parcela a fost atribuita manual de un administrator. Coordonatele fotografiei sunt ajustate la centroidul poligonului parcelei atribuite.'],
          ]}
        />
        <InfoBox>
          Algoritmul de atribuire automata functioneaza astfel: la incarcarea unei fotografii,
          sistemul verifica daca coordonatele GPS cad in interiorul unui poligon de parcela
          (<em>point-in-polygon</em>). Daca nu, cauta poligonul cel mai apropiat la o distanta
          de maximum 200m; daca exista, fotografia este atribuita automat acelui poligon (status
          galben), fara a-i muta coordonatele.
        </InfoBox>
        <SectionHeading>Atribuirea manuala a unei parcele</SectionHeading>
        <Paragraph>
          Pentru fotografiile cu status <strong>rosu (Neasignat)</strong>, administratorii pot
          atribui manual o parcela folosind butonul <strong>Asigneaza</strong>:
        </Paragraph>
        <NumberedList
          items={[
            <>In tabelul de fotografii, gasiti fotografia cu status rosu si apasati butonul <strong>Asigneaza</strong>.</>,
            <>Sistemul afiseaza o lista de parcele apropiate, sortate dupa distanta (calculata cu formula Haversine).</>,
            <>Selectati parcela dorita din lista.</>,
            <>Confirmati atribuirea. Coordonatele fotografiei vor fi ajustate la centroidul poligonului parcelei, iar statusul se va schimba in <strong>portocaliu (Ajustat manual)</strong>.</>,
          ]}
        />
        <SectionHeading>Vizualizarea pe harta</SectionHeading>
        <Paragraph>
          Fiecare fotografie poate fi vizualizata pe harta interactiva apasand butonul de
          harta din meniul de actiuni. Harta va centra vizualizarea pe coordonatele
          fotografiei si va afisa poligonul parcelei asociate (daca exista).
        </Paragraph>
        <SectionHeading>Miniaturi si vizualizare detaliata</SectionHeading>
        <Paragraph>
          In tabel, fiecare fotografie este afisata ca miniatura. Apasand pe miniatura se
          deschide vizualizatorul de imagine in ecran complet (<em>lightbox</em>), care permite
          navigarea intre fotografii si afisarea metadatelor complete.
        </Paragraph>
        <SectionHeading>Comentarii la fotografii</SectionHeading>
        <Paragraph>
          Similar parcelelor, fiecare fotografie poate avea comentarii. Utilizati aceasta
          functionalitate pentru a semnala probleme, a confirma locatia sau a comunica cu
          colegii despre o fotografie specifica.
        </Paragraph>
        <SectionHeading>Noutati (2026-04-14)</SectionHeading>
        <BulletList
          items={[
            <><strong>Selector de parcela</strong> in modul de editare — campurile UCF, raion, primarie, ocol silvic, entitate silvica si cod proiect sunt preluate automat din parcela selectata; coordonatele GPS se completeaza cu centroidul poligonului.</>,
            <><strong>Luna de captare</strong> — pe langa anul existent, puteti seta acum si luna (1-12). Aplicatia mobila completeaza automat ambele valori din ceasul dispozitivului.</>,
            <><strong>Autor</strong> — fiecare fotografie are un camp <em>Autor</em> distinct de cel care a importat inregistrarea.</>,
            <><strong>Proiect</strong> — enum cu 5 valori: PNERP (implicit), ORANGE, WORLD_BANK, FORESTRY_FUND, OTHER. Cand alegeti <em>OTHER</em> trebuie sa completati si nota proiectului.</>,
          ]}
        />
        <SectionHeading>Noutati (2026-04-15)</SectionHeading>
        <BulletList
          items={[
            <><strong>Buton "Incarca foto"</strong> — selectati o fotografie JPEG/PNG din calculator; serverul citeste coordonatele GPS si data din metadatele EXIF si creeaza automat inregistrarea.</>,
            <><strong>Buton "Import din Drive"</strong> — lipiti link-ul unui folder public Google Drive si importati in masa toate fotografiile (GPS + data citite direct de la Drive).</>,
            <><strong>Coloana Autor</strong> — in tabel se afiseaza acum codul scurt al utilizatorului (<em>cod</em>, ex. <code>Pnerp_edd_01</code>), cu nume complet la hover.</>,
          ]}
        />
        <SectionHeading>Autocomplete in dialogul de editare (v0.9.3)</SectionHeading>
        <Paragraph>
          La fel ca pe pagina <em>Poligoane</em>, dialogul de editare a unei
          fotografii sugereaza acum valorile canonice pentru
          <strong> Primarie</strong>, <strong>Entitate silvica</strong> si
          <strong> Ocol silvic</strong> (cand acestea sunt vizibile in setarile
          campurilor). Sugestiile vin din tabelele de referinta importate prin
          pagina <em>Regiuni</em>; text liber este in continuare acceptat.
        </Paragraph>
        <SectionHeading>Noutati (2026-08-17 / 2026-09-26)</SectionHeading>
        <BulletList
          items={[
            <><strong>Cautarea</strong> gaseste fotografiile si dupa datele poligonului lor (UCF, primarie, raion, cod proiect), nu doar dupa campurile proprii si numele fisierului.</>,
            <><strong>Filtre dupa datele poligonului</strong> (cod proiect, ID proiect, UCF, entitate silvica); denumirile de raion scrise diferit au fost unificate.</>,
            <><strong>Link direct</strong>: <Code>/app/database?photo=&lt;id&gt;</Code> deschide direct fisa unei fotografii.</>,
            <><strong>Coloana Suprafata (ha)</strong> arata suprafata poligonului de care este legata fotografia.</>,
            <><strong>Coloana Subpoligon</strong> arata UCF-ul complet al subpoligonului in care cade punctul GPS al fotografiei (in interiorul poligonului ei); fisa fotografiei si panoul de pe harta arata si suprafata lui.</>,
            <><strong>Drepturi de editare</strong>: Administratorul editeaza toate fotografiile din entitatile sale, Ajutorul doar pe cele incarcate de el (vezi Roluri si permisiuni).</>,
          ]}
        />
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Страница <strong>База данных</strong> управляет геореференцированными фотографиями,
          собранными в полевых условиях. Каждая фотография содержит GPS-координаты, дату и время
          съёмки и привязана автоматически или вручную к лесному участку.
        </Paragraph>
        <SectionHeading>Просмотр фотографий</SectionHeading>
        <Paragraph>
          Основная таблица отображает фотографии с информацией о: миниатюре, координатах,
          привязанном участке, районе, дате съёмки и статусе привязки. Используйте фильтры
          над таблицей для сужения результатов.
        </Paragraph>
        <SectionHeading>Статус фотографий (цветовые коды)</SectionHeading>
        <Paragraph>
          Каждая фотография имеет цветовой индикатор статуса, отражающий её связь с полигонами
          участков:
        </Paragraph>
        <DocTable
          headers={['Цвет', 'Статус', 'Описание']}
          rows={[
            ['Зелёный', 'In poligon', 'Фотография расположена внутри полигона участка. Привязка автоматическая и корректная.'],
            ['Жёлтый', 'Ajustat automat', 'Фотография вне всех полигонов, но не далее 200 м от одного из них. Она привязана к ближайшему полигону; GPS-координаты не меняются.'],
            ['Красный', 'Neasignat', 'Фотография не находится вблизи ни одного полигона. Требуется ручная привязка администратором.'],
            ['Оранжевый', 'Ajustat manual', 'Участок был привязан вручную администратором. Координаты фотографии скорректированы до центроида полигона привязанного участка.'],
          ]}
        />
        <InfoBox>
          Алгоритм автоматической привязки работает следующим образом: при загрузке фотографии
          система проверяет, попадают ли GPS-координаты внутрь полигона участка
          (<em>point-in-polygon</em>). Если нет, ищет ближайший полигон в радиусе 200 м;
          если он есть, фотография автоматически привязывается к нему (жёлтый статус), без
          изменения её координат.
        </InfoBox>
        <SectionHeading>Ручная привязка участка</SectionHeading>
        <Paragraph>
          Для фотографий со статусом <strong>красный (Neasignat)</strong> администраторы могут
          вручную привязать участок с помощью кнопки <strong>Привязать</strong>:
        </Paragraph>
        <NumberedList
          items={[
            <>В таблице фотографий найдите фотографию с красным статусом и нажмите кнопку <strong>Привязать</strong>.</>,
            <>Система отобразит список ближайших участков, отсортированных по расстоянию (рассчитанному по формуле Хаверсина).</>,
            <>Выберите нужный участок из списка.</>,
            <>Подтвердите привязку. Координаты фотографии будут скорректированы до центроида полигона участка, а статус изменится на <strong>оранжевый (Ajustat manual)</strong>.</>,
          ]}
        />
        <SectionHeading>Просмотр на карте</SectionHeading>
        <Paragraph>
          Каждую фотографию можно просмотреть на интерактивной карте, нажав кнопку карты
          в меню действий. Карта отцентрируется на координатах фотографии и покажет полигон
          привязанного участка (если он есть).
        </Paragraph>
        <SectionHeading>Миниатюры и детальный просмотр</SectionHeading>
        <Paragraph>
          В таблице каждая фотография отображается в виде миниатюры. При нажатии на миниатюру
          открывается полноэкранный просмотрщик (<em>lightbox</em>), позволяющий перемещаться
          между фотографиями и просматривать полные метаданные.
        </Paragraph>
        <SectionHeading>Комментарии к фотографиям</SectionHeading>
        <Paragraph>
          Аналогично участкам, каждая фотография может иметь комментарии. Используйте эту
          функцию, чтобы сообщить о проблемах, подтвердить местоположение или связаться
          с коллегами по конкретной фотографии.
        </Paragraph>
        <SectionHeading>Новое (2026-04-14)</SectionHeading>
        <BulletList
          items={[
            <><strong>Выбор участка</strong> в режиме редактирования — поля UCF, район, примэрия, лесничество, лесная организация и код проекта наследуются из выбранного участка; координаты GPS заполняются центроидом полигона.</>,
            <><strong>Месяц съёмки</strong> — помимо года теперь можно указать месяц (1-12). Мобильное приложение автоматически заполняет оба значения по часам устройства.</>,
            <><strong>Автор</strong> — отдельное поле, не совпадает с пользователем, импортировавшим запись.</>,
            <><strong>Проект</strong> — enum из 5 значений: PNERP (по умолчанию), ORANGE, WORLD_BANK, FORESTRY_FUND, OTHER. При выборе <em>OTHER</em> необходимо заполнить примечание.</>,
          ]}
        />
        <SectionHeading>Новое (2026-04-15)</SectionHeading>
        <BulletList
          items={[
            <><strong>Кнопка "Загрузить фото"</strong> — выберите JPEG/PNG с компьютера; сервер считывает GPS и дату съёмки из EXIF и создаёт запись автоматически.</>,
            <><strong>Кнопка "Импорт из Drive"</strong> — вставьте ссылку на публичную папку Google Drive и импортируйте все фотографии разом (GPS и время читаются напрямую из Drive).</>,
            <><strong>Столбец "Автор"</strong> — теперь отображается короткий код пользователя (<em>cod</em>, напр. <code>Pnerp_edd_01</code>); полное имя показывается при наведении.</>,
          ]}
        />
        <SectionHeading>Автозаполнение в диалоге редактирования (v0.9.3)</SectionHeading>
        <Paragraph>
          Как и на странице <em>Полигоны</em>, диалог редактирования фотографии
          теперь предлагает канонические значения для <strong>Примэрии</strong>,
          <strong> Лесной организации</strong> и <strong>Лесничества</strong>
          (когда эти поля видны в настройках). Подсказки берутся из справочных
          таблиц, импортированных через страницу <em>Регионы</em>; свободный ввод
          по-прежнему допускается.
        </Paragraph>
        <SectionHeading>Новое (2026-08-17 / 2026-09-26)</SectionHeading>
        <BulletList
          items={[
            <><strong>Поиск</strong> находит фотографии и по данным их полигона (UCF, примэрия, район, код проекта), а не только по собственным полям и имени файла.</>,
            <><strong>Фильтры по данным полигона</strong> (код проекта, ID проекта, UCF, лесная организация); разные написания районов объединены.</>,
            <><strong>Прямая ссылка</strong>: <Code>/app/database?photo=&lt;id&gt;</Code> сразу открывает карточку фотографии.</>,
            <><strong>Колонка Площадь (га)</strong> показывает площадь полигона, к которому привязана фотография.</>,
            <><strong>Колонка Подполигон</strong> показывает полный UCF подполигона, в который попадает GPS-точка фотографии (внутри её полигона); карточка фотографии и панель на карте показывают и его площадь.</>,
            <><strong>Права редактирования</strong>: Administrator редактирует все фотографии своих организаций, Ajutor только загруженные им самим (см. Роли и разрешения).</>,
          ]}
        />
      </>
    ),
    en: (
      <>
        <Paragraph>
          The <strong>Database</strong> page manages georeferenced photographs collected in the
          field. Each photo contains GPS coordinates, capture date and time, and is associated
          automatically or manually with a forest parcel.
        </Paragraph>
        <SectionHeading>Viewing Photos</SectionHeading>
        <Paragraph>
          The main table displays photos with information about: thumbnail, coordinates,
          associated parcel, district, capture date, and assignment status. Use the filters
          above the table to narrow results.
        </Paragraph>
        <SectionHeading>Photo Status (Color Codes)</SectionHeading>
        <Paragraph>
          Each photo has a color-based status indicator reflecting its relationship with parcel
          polygons:
        </Paragraph>
        <DocTable
          headers={['Color', 'Status', 'Description']}
          rows={[
            ['Green', 'In polygon', 'The photo is located inside a parcel polygon. Assignment is automatic and correct.'],
            ['Yellow', 'Auto-adjusted', 'The photo is outside every polygon but within 200m of one. It is linked to the nearest polygon; its GPS coordinates are left unchanged.'],
            ['Red', 'Unassigned', 'The photo is not near any polygon. Requires manual assignment by an administrator.'],
            ['Orange', 'Manually adjusted', 'The parcel was manually assigned by an administrator. Photo coordinates are adjusted to the centroid of the assigned parcel polygon.'],
          ]}
        />
        <InfoBox>
          The automatic assignment algorithm works as follows: when a photo is uploaded, the
          system checks whether the GPS coordinates fall inside a parcel polygon
          (<em>point-in-polygon</em>). If not, it searches for the nearest polygon within 200m;
          if there is one, the photo is automatically assigned to it (yellow status) without
          moving its coordinates.
        </InfoBox>
        <SectionHeading>Manual Parcel Assignment</SectionHeading>
        <Paragraph>
          For photos with <strong>red (Unassigned)</strong> status, administrators can manually
          assign a parcel using the <strong>Assign</strong> button:
        </Paragraph>
        <NumberedList
          items={[
            <>In the photos table, find the photo with red status and click the <strong>Assign</strong> button.</>,
            <>The system displays a list of nearby parcels sorted by distance (calculated using the Haversine formula).</>,
            <>Select the desired parcel from the list.</>,
            <>Confirm the assignment. The photo coordinates will be adjusted to the parcel polygon centroid, and the status will change to <strong>orange (Manually adjusted)</strong>.</>,
          ]}
        />
        <SectionHeading>Map View</SectionHeading>
        <Paragraph>
          Each photo can be viewed on the interactive map by clicking the map button in the
          actions menu. The map will center on the photo coordinates and display the associated
          parcel polygon (if one exists).
        </Paragraph>
        <SectionHeading>Thumbnails and Detailed View</SectionHeading>
        <Paragraph>
          In the table, each photo is displayed as a thumbnail. Clicking the thumbnail opens
          the full-screen image viewer (<em>lightbox</em>), which allows navigation between
          photos and displays complete metadata.
        </Paragraph>
        <SectionHeading>Photo Comments</SectionHeading>
        <Paragraph>
          Similar to parcels, each photo can have comments. Use this feature to report issues,
          confirm location, or communicate with colleagues about a specific photo.
        </Paragraph>
        <SectionHeading>What's new (2026-04-14)</SectionHeading>
        <BulletList
          items={[
            <><strong>Parcel picker</strong> in edit mode — UCF, district, municipality, forestry office, forestry entity, and project code are inherited from the selected parcel; GPS coordinates are auto-filled with the polygon centroid.</>,
            <><strong>Capture month</strong> — alongside the existing year, you can now also set the month (1-12). The mobile app auto-fills both from the device clock.</>,
            <><strong>Author</strong> — each photo has an <em>Author</em> field distinct from the user who imported the record.</>,
            <><strong>Project</strong> — a 5-value enum: PNERP (default), ORANGE, WORLD_BANK, FORESTRY_FUND, OTHER. When <em>OTHER</em> is selected, a project note is required.</>,
          ]}
        />
        <SectionHeading>What's new (2026-04-15)</SectionHeading>
        <BulletList
          items={[
            <><strong>"Upload photo" button</strong> — pick a JPEG/PNG from your computer; the server reads GPS and capture date from EXIF and creates the record automatically.</>,
            <><strong>"Import from Drive" button</strong> — paste a public Google Drive folder link and bulk-import every photo (GPS + time read directly from Drive).</>,
            <><strong>Author column</strong> — the table now shows the user's short code (<em>cod</em>, e.g. <code>Pnerp_edd_01</code>); the full name is shown on hover.</>,
          ]}
        />
        <SectionHeading>Autocomplete in the edit dialog (v0.9.3)</SectionHeading>
        <Paragraph>
          As on the <em>Polygons</em> page, the photo edit dialog now suggests
          canonical values for <strong>Municipality</strong>, <strong>Forestry
          entity</strong>, and <strong>Forest district</strong> (when those fields
          are visible in the field settings). Suggestions come from the reference
          tables imported via the <em>Regions</em> page; free-text input is still
          accepted.
        </Paragraph>
        <SectionHeading>What's new (2026-08-17 / 2026-09-26)</SectionHeading>
        <BulletList
          items={[
            <><strong>Search</strong> also finds photos by their polygon's data (UCF, mayoralty, district, project code), not only by their own fields and file name.</>,
            <><strong>Filters by polygon data</strong> (project code, project ID, UCF, forestry entity); differently spelled district names were unified.</>,
            <><strong>Direct link</strong>: <Code>/app/database?photo=&lt;id&gt;</Code> opens a photo's record directly.</>,
            <><strong>Area (ha) column</strong> shows the area of the polygon the photo is linked to.</>,
            <><strong>Subpolygon column</strong> shows the full UCF of the subpolygon the photo's GPS point falls in (within its polygon); the photo record and the map panel also show its area.</>,
            <><strong>Edit rights</strong>: an Administrator edits every photo in their entities, an Ajutor only the ones they uploaded (see Roles & Permissions).</>,
          ]}
        />
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Baza de date (Fotografii)',
    ru: 'База данных (Фотографии)',
    en: 'Database (Photos)',
  };

  return { id: 'photos', title: titles[lang], icon: Camera, content: content[lang] };
}

// ---------------------------------------------------------------------------

function importSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Pagina <strong>Import</strong> permite incarcarea in masa a datelor din fisiere
          Excel. Aceasta este cea mai eficienta metoda de a adauga volume mari de parcele
          sau fotografii in sistem.
        </Paragraph>
        <SectionHeading>Tipuri de import</SectionHeading>
        <BulletList
          items={[
            <><strong>Import parcele</strong> — din fisierul <Code>meta_3.xlsx</Code> care contine datele cadastrale, coordonatele poligoanelor si metadatele parcelelor</>,
            <><strong>Import fotografii</strong> — din fisierul <Code>meta_3_photo.xlsx</Code> care contine informatiile despre fotografiile georeferentiate si legaturile cu parcelele</>,
          ]}
        />
        <SectionHeading>Asistentul de import in 4 pasi</SectionHeading>
        <NumberedList
          items={[
            <><strong>Incarcare fisier</strong> — selectati fisierul Excel de pe calculatorul dumneavoastra. Sistemul accepta formate <Code>.xlsx</Code> si <Code>.xls</Code>.</>,
            <><strong>Previzualizare</strong> — verificati datele citite din fisier. Tabelul de previzualizare arata primele randuri si evidentiaza eventualele erori de format.</>,
            <><strong>Mapare coloane</strong> — confirmati sau ajustati corespondenta intre coloanele din fisier si campurile din sistem. Sistemul incearca sa detecteze automat maparea corecta.</>,
            <><strong>Confirmare si import</strong> — verificati rezumatul si apasati <strong>Confirma</strong> pentru a importa datele. Procesul poate dura cateva secunde pentru fisiere mari.</>,
          ]}
        />
        <SectionHeading>Moduri de import</SectionHeading>
        <DocTable
          headers={['Mod', 'Comportament']}
          rows={[
            ['Omite randuri invalide', 'Randurile cu erori de validare sunt ignorate, restul sunt importate cu succes. Veti primi un raport cu randurile omise.'],
            ['Totul sau nimic', 'Daca exista o singura eroare, intregul import este anulat. Se asigura ca toate datele sunt corecte inainte de salvare.'],
          ]}
        />
        <SectionHeading>Detectarea duplicatelor</SectionHeading>
        <Paragraph>
          Importul este <strong>idempotent</strong> — daca importati acelasi fisier de doua
          ori, randurile deja existente in baza de date vor fi detectate si omise. Detectarea
          se face pe baza unui identificator unic (de exemplu, numarul cadastral pentru parcele
          sau combinatia de coordonate si data pentru fotografii).
        </Paragraph>
        <InfoBox>
          Recomandam sa folositi modul <strong>Omite randuri invalide</strong> pentru importuri
          initiale mari si modul <strong>Totul sau nimic</strong> pentru actualizari unde
          corectitudinea completa a datelor este esentiala.
        </InfoBox>
        <SectionHeading>Import shapefile: poligoane si subpoligoane</SectionHeading>
        <Paragraph>
          Pe langa fisierele Excel, pagina Import accepta shapefile-uri (<Code>.shp</Code>,
          <Code> .dbf</Code>, <Code>.prj</Code>) in proiectia MOLDREF99:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Shapefile (PNERP)</strong>: poligoanele (straturile UCF si Terenuri). Obligatorii sunt doar conturul, raionul si primaria. Daca lipseste <Code>Anul_proie</Code>, se foloseste anul introdus la import sau, daca nu este introdus, anul din coloana <Code>Data</Code>. Daca lipseste <Code>Cod_prima</Code>, codul este cautat in registru dupa numele primariei si raion (si cand UCF-ul este lipit de nume, ex. <Code>Mihaileni7A</Code>). Daca lipseste <Code>UA</Code>, poligonul primeste UCF-ul provizoriu <Code>-</Code>. Previzualizarea arata de unde a fost luat anul si cate coduri au fost gasite dupa nume.</>,
            <><strong>Raportul de probleme</strong> (toate importurile shapefile si de receptii): tabelul <em>Coloanele fisierului</em> arata ce coloana a fost gasita pentru fiecare camp, cat de completa este si ce se intampla daca lipseste. Randurile respinse sunt grupate dupa cauza, fiecare grup explica problema si cum se corecteaza, iar un clic arata randurile afectate cu valorile lor (raion, primarie, UCF, data) ca sa poata fi gasite in QGIS. Butonul <em>Descarca lista (CSV)</em> salveaza toate problemele pentru furnizor.</>,
            <><strong>Pasul Completare</strong> (import poligoane): dupa previzualizare, randurile cu date lipsa sunt afisate intr-un tabel editabil. Raionul, primaria si anul (marcate cu *) sunt obligatorii, iar randurile care raman fara ele nu se importa; UCF-ul lipsa este evidentiat, iar fara el poligonul primeste UCF-ul provizoriu <Code>-</Code>. Bifa <em>Doar randurile cu date lipsa</em> se poate scoate pentru a edita orice rand, iar <em>Completeaza pentru randurile afisate</em> pune aceeasi valoare (ex. anul) pe toate odata. La import, cheia fiecarui poligon se reconstruieste din valorile corectate.</>,
            <><strong>Subpoligoane</strong>: fiecare rand este un subpoligon, cu UCF-ul complet in coloana <Code>UCF_sub</Code> (ex. <Code>1A1</Code>) sau cu numarul in <Code>Nr_sub</Code> combinat cu <Code>UA</Code>. <Code>Suprafata</Code> este optionala (altfel se calculeaza din contur); <Code>Anul_proie</Code> si <Code>Cod_prima</Code> deosebesc poligoanele cu acelasi UCF din primarii diferite. Poligonul parinte trebuie sa fie deja importat, subpoligonul trebuie sa fie in interiorul lui (toleranta 3 m) si sa nu se suprapuna cu alte subpoligoane (marginile comune sunt permise).</>,
            <><strong>Subpoligoane din receptii</strong> (arhive <Code>.zip</Code> de receptie tehnica): fiecare receptie devine subpoligonul poligonului care acopera cea mai mare parte din ea si primeste urmatorul numar in ordinea campaniilor (<Code>3A1</Code>, <Code>3A2</Code>). Receptiile ies adesea putin din poligon, asa ca aici nu se aplica regula celor 3 m. O receptie aflata in afara oricarui poligon ramane subpoligon fara poligon. Detalii in sectiunea Receptii tehnice.</>,
          ]}
        />
        <Paragraph>
          <strong>Reimportul actualizeaza</strong> (din 2026-09-26): un poligon cu aceeasi cheie
          sau un subpoligon cu acelasi poligon parinte si acelasi UCF isi preia conturul si
          suprafata din fisierul nou, in loc sa fie sarit ca <em>exista deja</em>. Celelalte date
          raman neschimbate, iar randurile identice sunt raportate ca neschimbate. Previzualizarea
          si rezultatul arata separat randurile noi, actualizate si neschimbate, plus randurile
          respinse cu motivul lor. Poligoanele actualizate refac atribuirea automata a fotografiilor.
        </Paragraph>
        <SectionHeading>Descarcarea sabloanelor</SectionHeading>
        <Paragraph>
          De pe pagina de import puteti descarca sabloanele Excel goale cu structura corecta
          a coloanelor. Folositi aceste sabloane pentru a va pregati datele inainte de import.
        </Paragraph>
        <WarningBox>
          Importul necesita o <strong>Entitate silvica</strong> atribuita contului
          dumneavoastra. Daca nu aveti una atribuita, contactati administratorul.
        </WarningBox>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Страница <strong>Импорт</strong> позволяет массово загружать данные из файлов
          Excel. Это наиболее эффективный способ добавления большого объёма участков или
          фотографий в систему.
        </Paragraph>
        <SectionHeading>Типы импорта</SectionHeading>
        <BulletList
          items={[
            <><strong>Импорт участков</strong> — из файла <Code>meta_3.xlsx</Code>, содержащего кадастровые данные, координаты полигонов и метаданные участков</>,
            <><strong>Импорт фотографий</strong> — из файла <Code>meta_3_photo.xlsx</Code>, содержащего информацию о геореференцированных фотографиях и связях с участками</>,
          ]}
        />
        <SectionHeading>Мастер импорта в 4 шага</SectionHeading>
        <NumberedList
          items={[
            <><strong>Загрузка файла</strong> — выберите файл Excel на вашем компьютере. Система принимает форматы <Code>.xlsx</Code> и <Code>.xls</Code>.</>,
            <><strong>Предпросмотр</strong> — проверьте данные, прочитанные из файла. Таблица предпросмотра показывает первые строки и выделяет возможные ошибки формата.</>,
            <><strong>Сопоставление столбцов</strong> — подтвердите или скорректируйте соответствие между столбцами файла и полями системы. Система пытается автоматически определить правильное сопоставление.</>,
            <><strong>Подтверждение и импорт</strong> — проверьте сводку и нажмите <strong>Подтвердить</strong> для импорта данных. Процесс может занять несколько секунд для больших файлов.</>,
          ]}
        />
        <SectionHeading>Режимы импорта</SectionHeading>
        <DocTable
          headers={['Режим', 'Поведение']}
          rows={[
            ['Пропускать невалидные строки', 'Строки с ошибками валидации пропускаются, остальные импортируются успешно. Вы получите отчёт о пропущенных строках.'],
            ['Всё или ничего', 'При наличии хотя бы одной ошибки весь импорт отменяется. Гарантирует корректность всех данных перед сохранением.'],
          ]}
        />
        <SectionHeading>Обнаружение дубликатов</SectionHeading>
        <Paragraph>
          Импорт является <strong>идемпотентным</strong> — при повторном импорте того же файла
          строки, уже существующие в базе данных, будут обнаружены и пропущены. Обнаружение
          выполняется на основе уникального идентификатора (например, кадастровый номер для
          участков или комбинация координат и даты для фотографий).
        </Paragraph>
        <InfoBox>
          Рекомендуем использовать режим <strong>Пропускать невалидные строки</strong> для
          начальных крупных импортов и режим <strong>Всё или ничего</strong> для обновлений,
          где важна полная корректность данных.
        </InfoBox>
        <SectionHeading>Импорт shapefile: полигоны и подполигоны</SectionHeading>
        <Paragraph>
          Кроме файлов Excel, страница Импорт принимает shapefile (<Code>.shp</Code>,
          <Code> .dbf</Code>, <Code>.prj</Code>) в проекции MOLDREF99:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Shapefile (PNERP)</strong>: полигоны (слои UCF и Terenuri). Обязательны только контур, район и примэрия. Если нет <Code>Anul_proie</Code>, берётся год, введённый при импорте, а если он не введён, год из колонки <Code>Data</Code>. Если нет <Code>Cod_prima</Code>, код ищется в реестре по названию примэрии и району (и тогда, когда UCF приклеен к названию, напр. <Code>Mihaileni7A</Code>). Если нет <Code>UA</Code>, полигон получает временный UCF <Code>-</Code>. Предпросмотр показывает, откуда взят год и сколько кодов найдено по названию.</>,
            <><strong>Отчёт о проблемах</strong> (все импорты shapefile и приёмок): таблица <em>Колонки файла</em> показывает, какая колонка найдена для каждого поля, насколько она заполнена и что происходит, если её нет. Отклонённые строки сгруппированы по причине, каждая группа объясняет проблему и как её исправить, а по клику видны затронутые строки с их значениями (район, примэрия, UCF, дата), чтобы найти их в QGIS. Кнопка <em>Скачать список (CSV)</em> сохраняет все проблемы для поставщика.</>,
            <><strong>Шаг Заполнение</strong> (импорт полигонов): после предпросмотра строки с недостающими данными показываются в редактируемой таблице. Район, примэрия и год (отмечены *) обязательны, строки без них не импортируются; отсутствующий UCF выделен, без него полигон получает временный UCF <Code>-</Code>. Флажок <em>Только строки с недостающими данными</em> можно снять, чтобы редактировать любую строку, а <em>Заполнить для показанных строк</em> ставит одно значение (напр. год) сразу во все. При импорте ключ каждого полигона пересобирается из исправленных значений.</>,
            <><strong>Подполигоны</strong>: каждая строка это подполигон с полным UCF в колонке <Code>UCF_sub</Code> (напр. <Code>1A1</Code>) или с номером в <Code>Nr_sub</Code> вместе с <Code>UA</Code>. <Code>Suprafata</Code> необязательна (иначе площадь вычисляется по контуру); <Code>Anul_proie</Code> и <Code>Cod_prima</Code> различают полигоны с одинаковым UCF из разных примэрий. Родительский полигон должен быть уже импортирован, подполигон должен лежать внутри него (допуск 3 м) и не пересекаться с другими подполигонами (общие границы допускаются).</>,
            <><strong>Подполигоны из приёмок</strong> (архивы <Code>.zip</Code> технических приёмок): каждая приёмка становится подполигоном того полигона, который покрывает большую её часть, и получает следующий номер в порядке кампаний (<Code>3A1</Code>, <Code>3A2</Code>). Приёмки часто немного выходят за полигон, поэтому правило 3 м здесь не применяется. Приёмка вне любого полигона остаётся подполигоном без полигона. Подробнее в разделе Технические приёмки.</>,
          ]}
        />
        <Paragraph>
          <strong>Повторный импорт обновляет</strong> (с 2026-09-26): полигон с тем же ключом или
          подполигон с тем же родителем и UCF получает контур и площадь из нового файла, вместо
          пропуска с сообщением <em>exista deja</em>. Остальные данные не меняются, а одинаковые
          строки отмечаются как без изменений. Предпросмотр и результат показывают отдельно новые,
          обновлённые и неизменённые строки, а также отклонённые строки с причиной. Обновлённые
          полигоны заново выполняют автоматическую привязку фотографий.
        </Paragraph>
        <SectionHeading>Скачивание шаблонов</SectionHeading>
        <Paragraph>
          На странице импорта можно скачать пустые шаблоны Excel с правильной структурой
          столбцов. Используйте эти шаблоны для подготовки данных перед импортом.
        </Paragraph>
        <WarningBox>
          Для импорта необходима <strong>лесная организация</strong>, назначенная вашей
          учётной записи. Если она не назначена, свяжитесь с администратором.
        </WarningBox>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The <strong>Import</strong> page allows bulk uploading of data from Excel files. This
          is the most efficient way to add large volumes of parcels or photos to the system.
        </Paragraph>
        <SectionHeading>Import Types</SectionHeading>
        <BulletList
          items={[
            <><strong>Parcel import</strong> — from the <Code>meta_3.xlsx</Code> file containing cadastral data, polygon coordinates, and parcel metadata</>,
            <><strong>Photo import</strong> — from the <Code>meta_3_photo.xlsx</Code> file containing information about georeferenced photos and parcel links</>,
          ]}
        />
        <SectionHeading>4-Step Import Wizard</SectionHeading>
        <NumberedList
          items={[
            <><strong>Upload file</strong> — select the Excel file from your computer. The system accepts <Code>.xlsx</Code> and <Code>.xls</Code> formats.</>,
            <><strong>Preview</strong> — review the data read from the file. The preview table shows the first rows and highlights any format errors.</>,
            <><strong>Column mapping</strong> — confirm or adjust the correspondence between file columns and system fields. The system attempts to auto-detect the correct mapping.</>,
            <><strong>Confirm and import</strong> — review the summary and click <strong>Confirm</strong> to import the data. The process may take a few seconds for large files.</>,
          ]}
        />
        <SectionHeading>Import Modes</SectionHeading>
        <DocTable
          headers={['Mode', 'Behavior']}
          rows={[
            ['Skip invalid rows', 'Rows with validation errors are skipped, the rest are imported successfully. You will receive a report of skipped rows.'],
            ['All or nothing', 'If there is a single error, the entire import is canceled. Ensures all data is correct before saving.'],
          ]}
        />
        <SectionHeading>Duplicate Detection</SectionHeading>
        <Paragraph>
          The import is <strong>idempotent</strong> — if you import the same file twice, rows
          already existing in the database will be detected and skipped. Detection is based on a
          unique identifier (for example, the cadastral number for parcels or the combination of
          coordinates and date for photos).
        </Paragraph>
        <InfoBox>
          We recommend using <strong>Skip invalid rows</strong> mode for large initial imports
          and <strong>All or nothing</strong> mode for updates where complete data correctness is
          essential.
        </InfoBox>
        <SectionHeading>Shapefile import: polygons and subpolygons</SectionHeading>
        <Paragraph>
          Besides Excel files, the Import page accepts shapefiles (<Code>.shp</Code>,
          <Code> .dbf</Code>, <Code>.prj</Code>) in the MOLDREF99 projection:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Shapefile (PNERP)</strong>: polygons (the UCF and Terenuri layers). Only the outline, district and mayoralty are required. Without <Code>Anul_proie</Code>, the year typed at import is used or, if none was typed, the year of the <Code>Data</Code> column. Without <Code>Cod_prima</Code>, the code is looked up in the register by mayoralty name and district (also when the UCF is glued onto the name, e.g. <Code>Mihaileni7A</Code>). Without <Code>UA</Code>, the polygon gets the placeholder UCF <Code>-</Code>. The preview shows where the year came from and how many codes were found by name.</>,
            <><strong>Problem report</strong> (every shapefile and reception import): the <em>File columns</em> table shows which column was matched for each field, how full it is and what happens when it is missing. Rejected rows are grouped by cause; each group explains the problem and the fix, and a click lists the affected rows with their values (district, mayoralty, UCF, date) so they can be found in QGIS. <em>Download list (CSV)</em> saves every problem for the provider.</>,
            <><strong>Completion step</strong> (polygon import): after the preview, rows with missing data are shown in an editable table. District, mayoralty and year (marked *) are required, and rows still missing them are not imported; a missing UCF is highlighted, and without one the polygon gets the placeholder UCF <Code>-</Code>. Untick <em>Only rows with missing data</em> to edit any row, and <em>Fill for the rows shown</em> sets one value (e.g. the year) on all of them at once. On import each polygon's key is rebuilt from the corrected values.</>,
            <><strong>Subpolygons</strong>: each row is a subpolygon, with its full UCF in the <Code>UCF_sub</Code> column (e.g. <Code>1A1</Code>) or its number in <Code>Nr_sub</Code> combined with <Code>UA</Code>. <Code>Suprafata</Code> is optional (otherwise computed from the outline); <Code>Anul_proie</Code> and <Code>Cod_prima</Code> tell apart same-UCF polygons of different mayoralties. The parent polygon must already be imported, and the subpolygon must lie inside it (3 m tolerance) and not overlap other subpolygons (shared edges are allowed).</>,
            <><strong>Subpolygons from receptions</strong> (technical reception <Code>.zip</Code> archives): each reception becomes a subpolygon of the polygon covering most of it and gets the next number in campaign order (<Code>3A1</Code>, <Code>3A2</Code>). Receptions often overhang their polygon a little, so the 3 m rule does not apply here. A reception outside every polygon stays a subpolygon with no polygon. Details in the Technical receptions section.</>,
          ]}
        />
        <Paragraph>
          <strong>Re-import refreshes</strong> (since 2026-09-26): a polygon with the same key, or
          a subpolygon with the same parent and UCF, takes its outline and area from the new file
          instead of being skipped as <em>exista deja</em>. Other fields stay as they are, and
          identical rows are reported as unchanged. The preview and the result show new, updated
          and unchanged rows separately, plus rejected rows with their reason. Updated polygons
          re-run the automatic photo assignment.
        </Paragraph>
        <SectionHeading>Downloading Templates</SectionHeading>
        <Paragraph>
          From the import page, you can download blank Excel templates with the correct column
          structure. Use these templates to prepare your data before importing.
        </Paragraph>
        <WarningBox>
          Import requires a <strong>forestry entity</strong> assigned to your account. If you
          don't have one assigned, contact your administrator.
        </WarningBox>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Import date',
    ru: 'Импорт данных',
    en: 'Import Data',
  };

  return { id: 'import', title: titles[lang], icon: Upload, content: content[lang] };
}

// ---------------------------------------------------------------------------

function filterMapSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Pagina <strong>Filtrare si harta</strong> combina un sistem avansat de filtrare
          cu 15 controale organizate in 5 sectiuni pliabile, impreuna cu o harta interactiva
          pentru vizualizarea geografica a datelor forestiere. Toate filtrele sunt sincronizate
          cu URL-ul paginii, permitand partajarea vizualizarilor cu colegii.
        </Paragraph>
        <SectionHeading>Panoul de filtrare (5 sectiuni, 15 controale)</SectionHeading>
        <Paragraph>
          Panoul de filtrare este organizat in sectiuni pliabile de tip acordeon. Fiecare
          sectiune poate fi deschisa sau inchisa independent. Un contor de filtre active este
          afisat in antetul panoului.
        </Paragraph>
        <DocTable
          headers={['Sectiune', 'Filtre', 'Tip control']}
          rows={[
            ['Cautare', 'Cautare text (raion, primarie, cod proiect, nume fisier)', 'Camp text'],
            ['Localizare', 'Entitate silvica, Raion, Primarie, Ocol silvic', 'Liste derulante (populate din API facets)'],
            ['Date tehnice', 'Cod proiect, UCF, Suprafata minima, Suprafata maxima', 'Camp text + interval numeric'],
            ['Perioada', 'Anul (selectie), Anul de la, Anul pana la, Semestru (1 sau 2, doar fotografii)', 'Lista derulanta + interval ani'],
            ['Fotografii', 'Status poligon (4 statusuri), Are parcela (toate/cu/fara)', 'Lista derulanta + comutator'],
          ]}
        />
        <Paragraph>
          Butonul <strong>Reseteaza</strong> sterge toate filtrele aplicate. Pe dispozitive
          mobile, panoul de filtre se deschide ca un panou lateral (<em>sheet overlay</em>).
        </Paragraph>
        <SectionHeading>Harta interactiva</SectionHeading>
        <Paragraph>
          Harta afiseaza doua straturi de date suprapuse pe titlurile OpenStreetMap:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Poligoane parcele (verde)</strong> — contururile geometrice ale parcelelor forestiere, afisate ca poligoane verzi semi-transparente</>,
            <><strong>Markere fotografii (albastru)</strong> — pozitia geografica a fiecarei fotografii, afisata ca un marker albastru pe harta</>,
          ]}
        />
        <SectionHeading>Controlul straturilor</SectionHeading>
        <Paragraph>
          Utilizati butoanele de comutare a straturilor din coltul din dreapta sus al hartii
          pentru a afisa sau ascunde separat poligoanele parcelelor si markerele fotografiilor.
          Aceasta permite concentrarea pe un singur tip de date la un moment dat.
        </Paragraph>
        <SectionHeading>Link-uri partajabile</SectionHeading>
        <InfoBox>
          Filtrele sunt sincronizate cu URL-ul paginii. Aceasta inseamna ca puteti copia
          adresa din bara de adrese a browserului si o puteti trimite unui coleg — acesta va
          vedea exact aceleasi filtre aplicate. Foarte util pentru colaborare si raportare.
        </InfoBox>
        <SectionHeading>Interactiunea cu elementele hartii</SectionHeading>
        <Paragraph>
          Apasati pe un poligon de parcela sau pe un marker de fotografie pentru a vedea un
          popup cu informatii detaliate. Din popup puteti naviga direct la pagina de detalii
          a parcelei sau fotografiei respective.
        </Paragraph>
        <SectionHeading>Noutati pe harta (2026-04-14)</SectionHeading>
        <BulletList
          items={[
            <><strong>Comutator strat de baza</strong> — alegeti intre OpenStreetMap (implicit), Esri World Imagery (satelit), OpenTopoMap (topografic) si CartoDB Light (minimal). Alegerea este memorata in browser.</>,
            <><strong>Etichete UCF pe poligoane</strong> — fiecare poligon afiseaza un tooltip permanent cu valoarea UCF in centrul sau; comutator pe harta pentru afisare/ascundere.</>,
            <><strong>Culori dupa an</strong> — poligoanele sunt colorate dupa anul de plantare, cu o legenda care explica asocierea culoare →an.</>,
            <><strong>Cautare in poligoane</strong> — textul din campul de cautare filtreaza acum si stratul de poligoane si face zoom pe rezultatele potrivite.</>,
            <><strong>Panou lateral pentru fotografii</strong> — apasand pe un marker de fotografie se deschide un panou din dreapta cu imaginea la dimensiune mare, autorul, proiectul, anul/luna si link-ul de descarcare.</>,
          ]}
        />
        <SectionHeading>Completari harta (2026-04-15)</SectionHeading>
        <BulletList
          items={[
            <><strong>Etichete UCF apasabile</strong> — click pe pastila "1A" selecteaza direct poligonul si deschide lista fotografiilor din el.</>,
            <><strong>Etichete fara suprapunere</strong> — etichetele suprapuse sunt mutate vertical pentru a ramane toate vizibile (nu mai sunt ascunse).</>,
            <><strong>Panou cu fotografiile poligonului</strong> — cand selectati un poligon, in dreapta apare lista miniaturilor fotografiilor din acel poligon, ordonate dupa data adaugarii.</>,
            <><strong>Panouri laterale exclusive</strong> — deschiderea unui panou inchide automat celalalt, astfel incat harta sa ramana curata.</>,
            <><strong>Miniaturi Drive fixate</strong> — previzualizarile fotografiilor din Google Drive folosesc endpoint-ul de thumbnail (nu mai dau eroare 403).</>,
          ]}
        />
        <SectionHeading>Filtre multi-select (v0.9.3)</SectionHeading>
        <Paragraph>
          De la versiunea 0.9.3, toate listele derulante din panoul de filtrare accepta
          mai multe valori simultan. Aceasta inlocuieste comportamentul anterior in care
          fiecare filtru permitea o singura valoare. Filtrele sunt acum disponibile si pe
          paginile <strong>Poligoane</strong> (<Code>/app/parcels</Code>) si
          <strong> Baza de date</strong> (<Code>/app/database</Code>) cu acelasi
          comportament.
        </Paragraph>
        <NumberedList
          items={[
            <>Apasati pe filtru pentru a deschide lista — bifati cate optiuni doriti. Filtrarea se aplica la fiecare schimbare; nu este nevoie sa apasati un buton "Aplica".</>,
            <>Eticheta filtrului afiseaza <em>numele valorii</em> cand ati selectat una singura, sau <em>"N selectate"</em> cand ati selectat mai multe.</>,
            <>Apasati X-ul mic langa eticheta pentru a goli rapid intregul filtru.</>,
            <>URL-ul paginii este actualizat cu o lista CSV (<Code>?raioane=Anenii+Noi,Cahul</Code>) — partajati URL-ul si veti partaja exact aceeasi selectie.</>,
          ]}
        />
        <SectionHeading>Optiunea "— Neasignat" (v0.9.3)</SectionHeading>
        <Paragraph>
          Cand exista randuri in baza de date care nu au valoare pe coloana respectiva
          (de ex. fotografii fara an completat, sau parcele fara ocol silvic), un nou
          item special <em>"— Neasignat"</em> apare in capul listei. Bifati-l pentru a
          include in rezultate randurile fara valoare. Optiunea apare automat doar cand
          este utila — nu va distrage cand toate randurile au valori.
        </Paragraph>
        <SectionHeading>Eticheta plurala "Ocoale Silvice"</SectionHeading>
        <Paragraph>
          Eticheta filtrului a fost actualizata de la <em>"Ocol silvic"</em> la
          <em> "Ocoale Silvice"</em> pentru a reflecta ca puteti selecta mai multe simultan.
        </Paragraph>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Страница <strong>Фильтрация и карта</strong> сочетает расширенную систему фильтрации
          с 15 элементами управления, организованными в 5 сворачиваемых секций, с интерактивной
          картой для географической визуализации лесных данных. Все фильтры синхронизированы
          с URL страницы, что позволяет делиться представлениями с коллегами.
        </Paragraph>
        <SectionHeading>Панель фильтрации (5 секций, 15 элементов управления)</SectionHeading>
        <Paragraph>
          Панель фильтрации организована в сворачиваемые секции-аккордеоны. Каждая секция
          может быть открыта или закрыта независимо. Счётчик активных фильтров отображается
          в заголовке панели.
        </Paragraph>
        <DocTable
          headers={['Секция', 'Фильтры', 'Тип элемента управления']}
          rows={[
            ['Поиск', 'Текстовый поиск (район, примэрия, код проекта, имя файла)', 'Текстовое поле'],
            ['Локализация', 'Лесная организация, Район, Примэрия, Лесничество', 'Выпадающие списки (заполняются из API facets)'],
            ['Технические данные', 'Код проекта, UCF, Минимальная площадь, Максимальная площадь', 'Текстовое поле + числовой интервал'],
            ['Период', 'Год (выбор), Год от, Год до, Семестр (1 или 2, только фотографии)', 'Выпадающий список + интервал лет'],
            ['Фотографии', 'Статус полигона (4 статуса), Есть участок (все/с/без)', 'Выпадающий список + переключатель'],
          ]}
        />
        <Paragraph>
          Кнопка <strong>Сбросить</strong> очищает все применённые фильтры. На мобильных
          устройствах панель фильтров открывается как боковая панель (<em>sheet overlay</em>).
        </Paragraph>
        <SectionHeading>Интерактивная карта</SectionHeading>
        <Paragraph>
          Карта отображает два слоя данных поверх тайлов OpenStreetMap:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Полигоны участков (зелёные)</strong> — геометрические контуры лесных участков, отображаемые как полупрозрачные зелёные полигоны</>,
            <><strong>Маркеры фотографий (синие)</strong> — географическое положение каждой фотографии, отображаемое как синий маркер на карте</>,
          ]}
        />
        <SectionHeading>Управление слоями</SectionHeading>
        <Paragraph>
          Используйте кнопки переключения слоёв в правом верхнем углу карты для отображения
          или скрытия полигонов участков и маркеров фотографий по отдельности. Это позволяет
          сосредоточиться на одном типе данных в конкретный момент.
        </Paragraph>
        <SectionHeading>Общие ссылки</SectionHeading>
        <InfoBox>
          Фильтры синхронизированы с URL страницы. Это значит, что вы можете скопировать
          адрес из адресной строки браузера и отправить коллеге — он увидит точно такие же
          применённые фильтры. Очень полезно для совместной работы и отчётности.
        </InfoBox>
        <SectionHeading>Взаимодействие с элементами карты</SectionHeading>
        <Paragraph>
          Нажмите на полигон участка или маркер фотографии, чтобы увидеть всплывающее окно
          с подробной информацией. Из всплывающего окна можно перейти непосредственно на
          страницу деталей соответствующего участка или фотографии.
        </Paragraph>
        <SectionHeading>Новое на карте (2026-04-14)</SectionHeading>
        <BulletList
          items={[
            <><strong>Переключатель базового слоя</strong> — выбор между OpenStreetMap (по умолчанию), Esri World Imagery (спутник), OpenTopoMap (топография) и CartoDB Light (минимализм). Выбор сохраняется в браузере.</>,
            <><strong>Подписи UCF на полигонах</strong> — каждый полигон отображает постоянный тултип со значением UCF в центре; переключатель на карте позволяет скрыть/показать.</>,
            <><strong>Цвет по году</strong> — полигоны окрашиваются в зависимости от года посадки; легенда показывает соответствие цвет →год.</>,
            <><strong>Поиск по полигонам</strong> — текст из поля поиска фильтрует слой полигонов и автоматически масштабирует карту к совпадениям.</>,
            <><strong>Боковая панель фотографии</strong> — клик по маркеру открывает правую панель с полноразмерным изображением, автором, проектом, годом/месяцем и ссылкой для скачивания.</>,
          ]}
        />
        <SectionHeading>Дополнения карты (2026-04-15)</SectionHeading>
        <BulletList
          items={[
            <><strong>Кликабельные метки UCF</strong> — клик по «таблетке» вроде «1A» выбирает полигон и открывает список его фотографий.</>,
            <><strong>Подписи без наложения</strong> — перекрывающиеся метки смещаются по вертикали, оставаясь видимыми (больше не скрываются).</>,
            <><strong>Панель фотографий полигона</strong> — при выборе полигона справа открывается сетка миниатюр его фотографий, отсортированных по дате добавления.</>,
            <><strong>Взаимоисключающие боковые панели</strong> — открытие одной автоматически закрывает другую, чтобы карта оставалась читаемой.</>,
            <><strong>Превью Drive исправлено</strong> — предпросмотр фотографий из Google Drive использует thumbnail-эндпоинт (больше нет ошибки 403).</>,
          ]}
        />
        <SectionHeading>Multi-select фильтры (v0.9.3)</SectionHeading>
        <Paragraph>
          Начиная с версии 0.9.3 все выпадающие списки в панели фильтров принимают
          несколько значений одновременно. Это заменяет прежнее поведение, когда каждый
          фильтр допускал только одно значение. Те же фильтры теперь доступны и на
          страницах <strong>Полигоны</strong> (<Code>/app/parcels</Code>) и
          <strong> База данных</strong> (<Code>/app/database</Code>) с идентичным
          поведением.
        </Paragraph>
        <NumberedList
          items={[
            <>Нажмите на фильтр, чтобы открыть список — отметьте сколько опций нужно. Фильтрация применяется при каждом изменении; кнопка «Применить» не нужна.</>,
            <>Метка фильтра показывает <em>имя значения</em>, если выбрано одно, или <em>«N выбрано»</em>, если выбрано несколько.</>,
            <>Нажмите маленький X рядом с меткой, чтобы быстро очистить весь фильтр.</>,
            <>URL страницы обновляется CSV-списком (<Code>?raioane=Anenii+Noi,Cahul</Code>) — поделитесь URL и поделитесь точно такой же выборкой.</>,
          ]}
        />
        <SectionHeading>Опция «— Не назначено» (v0.9.3)</SectionHeading>
        <Paragraph>
          Когда в базе данных есть записи без значения в соответствующем столбце
          (например, фотографии без указанного года или участки без лесничества), в
          начале списка появляется специальный пункт <em>«— Не назначено»</em>.
          Отметьте его, чтобы включить в результаты записи без значения. Опция
          появляется автоматически только когда она полезна — не отвлекает, когда у
          всех записей есть значения.
        </Paragraph>
        <SectionHeading>Множественная подпись «Лесничества»</SectionHeading>
        <Paragraph>
          Метка фильтра обновлена с <em>«Лесничество»</em> на <em>«Лесничества»</em>,
          чтобы отразить, что можно выбрать несколько одновременно.
        </Paragraph>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The <strong>Filters & Map</strong> page combines an advanced filtering system with
          15 controls organized into 5 collapsible sections, along with an interactive map for
          geographic visualization of forest data. All filters are synchronized with the page
          URL, allowing you to share views with colleagues.
        </Paragraph>
        <SectionHeading>Filter Panel (5 Sections, 15 Controls)</SectionHeading>
        <Paragraph>
          The filter panel is organized into collapsible accordion sections. Each section can be
          opened or closed independently. An active filter counter is displayed in the panel
          header.
        </Paragraph>
        <DocTable
          headers={['Section', 'Filters', 'Control Type']}
          rows={[
            ['Search', 'Text search (district, municipality, project code, file name)', 'Text field'],
            ['Location', 'Forestry entity, District, Municipality, Forestry office', 'Dropdowns (populated from API facets)'],
            ['Technical data', 'Project code, UCF, Min area, Max area', 'Text field + numeric range'],
            ['Period', 'Year (selection), Year from, Year to, Semester (1 or 2, photos only)', 'Dropdown + year range'],
            ['Photos', 'Polygon status (4 statuses), Has parcel (all/with/without)', 'Dropdown + toggle'],
          ]}
        />
        <Paragraph>
          The <strong>Reset</strong> button clears all applied filters. On mobile devices, the
          filter panel opens as a side panel (<em>sheet overlay</em>).
        </Paragraph>
        <SectionHeading>Interactive Map</SectionHeading>
        <Paragraph>
          The map displays two data layers overlaid on OpenStreetMap tiles:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Parcel polygons (green)</strong> — geometric outlines of forest parcels displayed as semi-transparent green polygons</>,
            <><strong>Photo markers (blue)</strong> — geographic position of each photo displayed as a blue marker on the map</>,
          ]}
        />
        <SectionHeading>Layer Control</SectionHeading>
        <Paragraph>
          Use the layer toggle buttons in the top-right corner of the map to show or hide
          parcel polygons and photo markers separately. This allows focusing on a single data
          type at a time.
        </Paragraph>
        <SectionHeading>Shareable Links</SectionHeading>
        <InfoBox>
          Filters are synchronized with the page URL. This means you can copy the address from
          the browser's address bar and send it to a colleague — they will see exactly the same
          filters applied. Very useful for collaboration and reporting.
        </InfoBox>
        <SectionHeading>Interacting with Map Elements</SectionHeading>
        <Paragraph>
          Click on a parcel polygon or photo marker to see a popup with detailed information.
          From the popup, you can navigate directly to the details page of the respective parcel
          or photo.
        </Paragraph>
        <SectionHeading>Map updates (2026-04-14)</SectionHeading>
        <BulletList
          items={[
            <><strong>Base layer switcher</strong> — choose between OpenStreetMap (default), Esri World Imagery (satellite), OpenTopoMap (topographic), and CartoDB Light (minimal). Selection is persisted in the browser.</>,
            <><strong>UCF labels on polygons</strong> — each polygon shows a permanent tooltip with its UCF value at its centroid; a map toggle hides or shows the labels.</>,
            <><strong>Color by year</strong> — polygons are colored by planting year, with a legend explaining the color →year mapping.</>,
            <><strong>Polygon-scoped search</strong> — the search field also filters the polygon layer and auto-zooms to matching results.</>,
            <><strong>Photo side panel</strong> — clicking a photo marker opens a right-side panel with the full-size image, author, project, year/month, and download link.</>,
          ]}
        />
        <SectionHeading>Map follow-ups (2026-04-15)</SectionHeading>
        <BulletList
          items={[
            <><strong>Clickable UCF labels</strong> — clicking a "1A" pill selects the polygon directly and opens the list of its photos.</>,
            <><strong>No-overlap labels</strong> — overlapping labels are nudged vertically so all stay visible (no longer hidden).</>,
            <><strong>Polygon photos panel</strong> — selecting a polygon opens a right-side grid of its photo thumbnails, ordered by creation date.</>,
            <><strong>Mutually exclusive side panels</strong> — opening one panel closes the other automatically to keep the map readable.</>,
            <><strong>Drive thumbnails fixed</strong> — photo previews now use Google Drive's thumbnail endpoint (no more 403 errors).</>,
          ]}
        />
        <SectionHeading>Multi-select filters (v0.9.3)</SectionHeading>
        <Paragraph>
          As of v0.9.3, every dropdown in the filter panel accepts multiple values at
          once. This replaces the prior behaviour where each filter only allowed a
          single value. The same filters are also available on the
          <strong> Polygons</strong> page (<Code>/app/parcels</Code>) and the
          <strong> Database</strong> page (<Code>/app/database</Code>) with identical
          behaviour.
        </Paragraph>
        <NumberedList
          items={[
            <>Click a filter to open the list — check as many options as you need. Filtering applies on every change; no "Apply" button is required.</>,
            <>The filter label shows the <em>value name</em> when one is selected, or <em>"N selected"</em> when several are picked.</>,
            <>Click the small X next to the label to clear the entire filter quickly.</>,
            <>The page URL is updated with a CSV list (<Code>?raioane=Anenii+Noi,Cahul</Code>) — share the URL and you share the exact same selection.</>,
          ]}
        />
        <SectionHeading>"— Unassigned" option (v0.9.3)</SectionHeading>
        <Paragraph>
          When the database contains rows with no value in a particular column (e.g.
          photos with no year, or parcels with no forest district), a special
          <em> "— Unassigned"</em> item appears at the top of the list. Tick it to
          include rows without a value in your results. The option only appears when
          useful — it stays out of the way when every row has a value.
        </Paragraph>
        <SectionHeading>Plural "Forest districts" label</SectionHeading>
        <Paragraph>
          The filter label was updated from <em>"Forest district"</em> to
          <em> "Forest districts"</em> to reflect that several can be selected
          simultaneously.
        </Paragraph>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Filtrare si harta',
    ru: 'Фильтрация и карта',
    en: 'Filters & Map',
  };

  return { id: 'filter-map', title: titles[lang], icon: Map, content: content[lang] };
}

// ---------------------------------------------------------------------------

function mobileSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Aplicatia mobila PNERP este instrumentul principal pentru colectarea fotografiilor
          georeferentiate in teren. Disponibila pentru dispozitive Android si iOS, permite
          capturarea si sincronizarea fotografiilor chiar si in zone fara acoperire de retea.
        </Paragraph>
        <SectionHeading>Instalarea aplicatiei</SectionHeading>
        <BulletList
          items={[
            <><strong>Expo Go (dezvoltare)</strong> — instalati aplicatia <em>Expo Go</em> din App Store sau Google Play, apoi scanati codul QR furnizat de echipa tehnica</>,
            <><strong>APK (Android)</strong> — descarcati fisierul APK direct de la administratorul dumneavoastra si instalati-l pe dispozitiv (activati <em>Surse necunoscute</em> in setari)</>,
          ]}
        />
        <SectionHeading>Autentificarea pe mobil</SectionHeading>
        <Paragraph>
          Utilizati aceleasi credentiale (nume de utilizator si parola) ca pe platforma web.
          Dupa autentificare, sesiunea ramane activa pe dispozitiv pana la deconectare manuala.
        </Paragraph>
        <SectionHeading>Capturarea fotografiilor</SectionHeading>
        <NumberedList
          items={[
            <>Deschideti ecranul <strong>Camera</strong> din meniul aplicatiei.</>,
            <>Tineti dispozitivul stabil si asigurati-va ca GPS-ul este activat.</>,
            <>Apasati butonul de captura. Fotografia va fi salvata automat cu coordonatele GPS si data/ora.</>,
            <>Optional, adaugati metadate suplimentare (raion, primarie, ocol silvic) — daca aveti metadate implicite setate in profil, acestea se completeaza automat.</>,
          ]}
        />
        <SectionHeading>Modul offline</SectionHeading>
        <Paragraph>
          Una dintre cele mai importante functionalitati ale aplicatiei mobile este suportul
          complet pentru lucrul fara conexiune la internet:
        </Paragraph>
        <BulletList
          items={[
            <>Fotografiile capturate sunt <strong>salvate local</strong> pe dispozitiv cand nu exista conexiune, intr-o coada de asteptare</>,
            <>Cand conexiunea este restabilita, fotografiile sunt <strong>sincronizate automat</strong> cu serverul la fiecare 30 de secunde</>,
            <>Un <strong>indicator vizual</strong> (badge) arata numarul de fotografii nesincronizate in coada</>,
            <>Fotografiile sunt pastrate local pana la confirmarea sincronizarii cu succes</>,
            <>Buton de <strong>sincronizare manuala</strong> pentru a forta trimiterea fotografiilor din coada</>,
          ]}
        />
        <SectionHeading>Setari de sincronizare</SectionHeading>
        <DocTable
          headers={['Setare', 'Descriere']}
          rows={[
            ['Doar WiFi', 'Sincronizarea are loc doar cand dispozitivul este conectat la o retea WiFi. Util pentru economisirea datelor mobile.'],
            ['Stergere automata dupa incarcare', 'Fotografiile locale sunt sterse automat dupa sincronizarea cu succes cu serverul. Elibereaza spatiul de stocare.'],
          ]}
        />
        <SectionHeading>Setari profil pe mobil</SectionHeading>
        <Paragraph>
          Din ecranul <strong>Profil</strong> al aplicatiei mobile puteti:
        </Paragraph>
        <BulletList
          items={[
            'Vizualiza si modifica metadatele implicite',
            'Schimba limba interfetei',
            'Vizualiza entitatea silvica atribuita',
            'Verifica starea sincronizarii',
            'Deconecta contul',
          ]}
        />
        <SectionHeading>Suprapunerea metadatelor pe imagine</SectionHeading>
        <InfoBox>
          Fotografiile capturate cu aplicatia mobila contin o suprapunere vizuala
          (<em>overlay</em>) cu metadatele principale: coordonate GPS, data si ora, raion,
          primarie. Aceasta suprapunere este integrata in imagine si este vizibila atat in
          aplicatie, cat si pe platforma web.
        </InfoBox>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Мобильное приложение PNERP — основной инструмент для сбора геореференцированных
          фотографий в полевых условиях. Доступно для устройств Android и iOS, позволяет
          делать снимки и синхронизировать их даже в зонах без сетевого покрытия.
        </Paragraph>
        <SectionHeading>Установка приложения</SectionHeading>
        <BulletList
          items={[
            <><strong>Expo Go (разработка)</strong> — установите приложение <em>Expo Go</em> из App Store или Google Play, затем отсканируйте QR-код, предоставленный технической командой</>,
            <><strong>APK (Android)</strong> — скачайте APK-файл напрямую у вашего администратора и установите его на устройство (активируйте <em>Неизвестные источники</em> в настройках)</>,
          ]}
        />
        <SectionHeading>Авторизация на мобильном</SectionHeading>
        <Paragraph>
          Используйте те же учётные данные (имя пользователя и пароль), что и на веб-платформе.
          После авторизации сессия остаётся активной на устройстве до ручного выхода.
        </Paragraph>
        <SectionHeading>Съёмка фотографий</SectionHeading>
        <NumberedList
          items={[
            <>Откройте экран <strong>Камера</strong> в меню приложения.</>,
            <>Держите устройство стабильно и убедитесь, что GPS включён.</>,
            <>Нажмите кнопку съёмки. Фотография будет автоматически сохранена с GPS-координатами и датой/временем.</>,
            <>При необходимости добавьте дополнительные метаданные (район, примэрия, лесничество) — если у вас заданы метаданные по умолчанию в профиле, они заполняются автоматически.</>,
          ]}
        />
        <SectionHeading>Офлайн-режим</SectionHeading>
        <Paragraph>
          Одна из важнейших функций мобильного приложения — полная поддержка работы без
          подключения к интернету:
        </Paragraph>
        <BulletList
          items={[
            <>Сделанные фотографии <strong>сохраняются локально</strong> на устройстве при отсутствии подключения в очереди ожидания</>,
            <>При восстановлении подключения фотографии <strong>автоматически синхронизируются</strong> с сервером каждые 30 секунд</>,
            <><strong>Визуальный индикатор</strong> (значок) показывает количество несинхронизированных фотографий в очереди</>,
            <>Фотографии хранятся локально до подтверждения успешной синхронизации</>,
            <>Кнопка <strong>ручной синхронизации</strong> для принудительной отправки фотографий из очереди</>,
          ]}
        />
        <SectionHeading>Настройки синхронизации</SectionHeading>
        <DocTable
          headers={['Настройка', 'Описание']}
          rows={[
            ['Только WiFi', 'Синхронизация происходит только при подключении устройства к сети WiFi. Полезно для экономии мобильного трафика.'],
            ['Автоудаление после загрузки', 'Локальные фотографии автоматически удаляются после успешной синхронизации с сервером. Освобождает пространство хранения.'],
          ]}
        />
        <SectionHeading>Настройки профиля на мобильном</SectionHeading>
        <Paragraph>
          На экране <strong>Профиль</strong> мобильного приложения вы можете:
        </Paragraph>
        <BulletList
          items={[
            'Просматривать и изменять метаданные по умолчанию',
            'Менять язык интерфейса',
            'Просматривать назначенную лесную организацию',
            'Проверять состояние синхронизации',
            'Выйти из учётной записи',
          ]}
        />
        <SectionHeading>Наложение метаданных на изображение</SectionHeading>
        <InfoBox>
          Фотографии, сделанные мобильным приложением, содержат визуальное наложение
          (<em>overlay</em>) с основными метаданными: GPS-координаты, дата и время, район,
          примэрия. Это наложение встроено в изображение и видно как в приложении, так и на
          веб-платформе.
        </InfoBox>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The PNERP mobile app is the primary tool for collecting georeferenced photographs in
          the field. Available for Android and iOS devices, it allows capturing and syncing
          photos even in areas without network coverage.
        </Paragraph>
        <SectionHeading>Installing the App</SectionHeading>
        <BulletList
          items={[
            <><strong>Expo Go (development)</strong> — install the <em>Expo Go</em> app from the App Store or Google Play, then scan the QR code provided by the technical team</>,
            <><strong>APK (Android)</strong> — download the APK file directly from your administrator and install it on your device (enable <em>Unknown sources</em> in settings)</>,
          ]}
        />
        <SectionHeading>Mobile Authentication</SectionHeading>
        <Paragraph>
          Use the same credentials (username and password) as on the web platform. After
          authentication, the session remains active on the device until manual logout.
        </Paragraph>
        <SectionHeading>Capturing Photos</SectionHeading>
        <NumberedList
          items={[
            <>Open the <strong>Camera</strong> screen from the app menu.</>,
            <>Hold the device steady and make sure GPS is enabled.</>,
            <>Press the capture button. The photo will be automatically saved with GPS coordinates and date/time.</>,
            <>Optionally, add additional metadata (district, municipality, forestry office) — if you have default metadata set in your profile, these are filled in automatically.</>,
          ]}
        />
        <SectionHeading>Offline Mode</SectionHeading>
        <Paragraph>
          One of the most important features of the mobile app is full support for working
          without an internet connection:
        </Paragraph>
        <BulletList
          items={[
            <>Captured photos are <strong>saved locally</strong> on the device when there is no connection, in a waiting queue</>,
            <>When the connection is restored, photos are <strong>automatically synced</strong> with the server every 30 seconds</>,
            <>A <strong>visual indicator</strong> (badge) shows the number of unsynced photos in the queue</>,
            <>Photos are kept locally until successful sync confirmation</>,
            <><strong>Manual sync</strong> button to force sending photos from the queue</>,
          ]}
        />
        <SectionHeading>Sync Settings</SectionHeading>
        <DocTable
          headers={['Setting', 'Description']}
          rows={[
            ['WiFi only', 'Sync occurs only when the device is connected to a WiFi network. Useful for saving mobile data.'],
            ['Auto-delete after upload', 'Local photos are automatically deleted after successful sync with the server. Frees up storage space.'],
          ]}
        />
        <SectionHeading>Mobile Profile Settings</SectionHeading>
        <Paragraph>
          From the <strong>Profile</strong> screen of the mobile app you can:
        </Paragraph>
        <BulletList
          items={[
            'View and modify default metadata',
            'Change the interface language',
            'View the assigned forestry entity',
            'Check sync status',
            'Log out of the account',
          ]}
        />
        <SectionHeading>Metadata Overlay on Image</SectionHeading>
        <InfoBox>
          Photos captured with the mobile app include a visual overlay with key metadata: GPS
          coordinates, date and time, district, municipality. This overlay is embedded in the
          image and is visible both in the app and on the web platform.
        </InfoBox>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Aplicatia mobila',
    ru: 'Мобильное приложение',
    en: 'Mobile App',
  };

  return { id: 'mobile', title: titles[lang], icon: Smartphone, content: content[lang] };
}

// ---------------------------------------------------------------------------

function adminSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Sectiunea de <strong>Administrare</strong> este disponibila pentru utilizatorii cu
          rol de <em>Super Admin</em> sau <em>Administrator</em>. Permite gestionarea
          utilizatorilor, entitatilor silvice si setarilor sistemului.
        </Paragraph>
        <SectionHeading>Gestionarea utilizatorilor</SectionHeading>
        <Paragraph>
          Pagina <strong>Utilizatori</strong> afiseaza toti utilizatorii la care aveti acces:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Super Admin</strong> — vede si gestioneaza toti utilizatorii din sistem</>,
            <><strong>Administrator</strong> — vede si gestioneaza doar utilizatorii de tip <em>Ajutor Administrator</em> pe care i-a creat</>,
          ]}
        />
        <SectionHeading>Crearea unui utilizator nou</SectionHeading>
        <NumberedList
          items={[
            <>Apasati butonul <strong>Creaza utilizator</strong>.</>,
            <>Completati numele de utilizator, numele complet si parola initiala.</>,
            <>Selectati rolul (Super Admin poate atribui orice rol; Administrator poate crea doar Ajutor Administrator).</>,
            <>Atribuiti o <strong>Entitate silvica</strong> — aceasta determina ce date poate crea si vedea utilizatorul.</>,
            <>Apasati <strong>Salveaza</strong>.</>,
          ]}
        />
        <SectionHeading>Dezactivarea unui cont</SectionHeading>
        <Paragraph>
          In loc sa stergeti un cont de utilizator (ceea ce ar pierde istoricul datelor),
          puteti <strong>dezactiva</strong> contul. Utilizatorul dezactivat nu se mai poate
          autentifica, dar datele sale raman in sistem.
        </Paragraph>
        <SectionHeading>Schimbarea rolului</SectionHeading>
        <Paragraph>
          Super Admin poate modifica rolul oricarui utilizator. Schimbarea rolului afecteaza
          imediat nivelul de acces al utilizatorului la urmatoarea autentificare.
        </Paragraph>
        <SectionHeading>Atribuirea Entitatilor silvice (multi-valoare, v0.9.3)</SectionHeading>
        <Paragraph>
          Incepand cu versiunea 0.9.3, fiecare utilizator poate fi atribuit la
          <strong> mai multe entitati silvice</strong> (nu doar una). Lista este ordonata —
          primul element actioneaza ca <em>entitate implicita</em> pentru autofill (cand
          utilizatorul creeaza o parcela sau o fotografie, valoarea implicita pentru
          campul "entitate silvica" este primul element din lista lui).
        </Paragraph>
        <Paragraph>
          Interfata de gestionare prezinta entitatile ca <strong>chip-uri (pile)</strong>:
        </Paragraph>
        <NumberedList
          items={[
            <>Apasati <strong>+ Adauga</strong> langa lista pentru a deschide un panou de selectie. Super Admin poate selecta orice valoare; Administrator poate atribui doar entitati prezente in propria lista.</>,
            <>Apasati pe <strong>X-ul</strong> de pe un chip pentru a-l elimina rapid din lista.</>,
            <>Folositi <strong>sagetile</strong> sus/jos sau drag-and-drop pentru a reordona lista. Primul element devine implicit pentru autofill.</>,
            <>Modificarile sunt salvate optimist — daca incercati sa atribuiti o entitate care nu este permisa (administratorii nu pot da unui ajutor entitati pe care nu le au ei insisi), interfata revine la starea anterioara si afiseaza eroarea <Code>ENTITY_NOT_IN_SCOPE</Code>.</>,
          ]}
        />
        <Paragraph>
          Listele derulante de metadate (raion, primarie, ocol silvic) sunt acum unite
          peste toate entitatile atribuite — un utilizator cu doua entitati va vedea
          valorile facets ale ambelor combinate, plus un detaliu per-entitate
          (<Code>byEntity</Code>) returnat de endpoint-ul <Code>/api/me/cascading-options</Code>.
        </Paragraph>
        <WarningBox>
          Atribuirea cel putin <strong>unei entitati silvice</strong> este obligatorie
          pentru ca un utilizator sa poata crea parcele, importa date sau sincroniza
          fotografii de pe mobil. <strong>Super Admin</strong> si
          <strong> Controlor</strong> sunt exceptati. Pe pagina de profil, primul element
          al listei este marcat cu un badge <em>"Implicită"</em>.
        </WarningBox>
        <SectionHeading>Setari campuri fotografii (Super Admin)</SectionHeading>
        <Paragraph>
          Din pagina <strong>Setari</strong> (disponibila doar pentru Super Admin), puteti
          controla vizibilitatea si obligativitatea campurilor de metadate pentru fotografii:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Vizibil</strong> — daca campul este afisat in interfata</>,
            <><strong>Obligatoriu</strong> — daca campul trebuie completat la incarcarea fotografiei</>,
          ]}
        />
        <SectionHeading>Jurnalul de audit</SectionHeading>
        <Paragraph>
          Pagina <strong>Jurnal audit</strong> (disponibila doar pentru Super Admin) inregistreaza
          toate actiunile importante din sistem: autentificari, creare/editare/stergere de
          parcele si fotografii, modificari de utilizatori si setari. Fiecare intrare contine
          data, utilizatorul, actiunea si detaliile modificarii.
        </Paragraph>
        <SectionHeading>Resetarea parolei</SectionHeading>
        <Paragraph>
          Administratorii pot reseta parola unui utilizator din pagina de gestionare a
          utilizatorilor. Noua parola trebuie comunicata utilizatorului printr-un canal sigur.
          Se recomanda ca utilizatorul sa isi schimbe parola la prima autentificare.
        </Paragraph>
        <SectionHeading>Pagina Regiuni — date de referinta (v0.9.3, doar Super Admin)</SectionHeading>
        <Paragraph>
          Pagina <strong>Regiuni</strong> (<Code>/app/admin/regions</Code>) este o
          adaugare noua in versiunea 0.9.3 si permite importul tabelelor canonice de
          referinta din fisierul <Code>info/regions.xlsx</Code>:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Primarii</strong> — aproximativ 908 de primarii din Republica Moldova, cu cod numeric (<Code>Cod_Primarii</Code>), nume si raion.</>,
            <><strong>Entitati silvice</strong> — 26 de entitati (intreprinderi de stat) cu cod scurt (BL, CL, CU, ...) si nume canonic complet (<em>"ÎSÎS Bălți"</em>).</>,
            <><strong>Ocoale silvice</strong> — 87 de ocoale, fiecare apartinand unei entitati silvice (FK).</>,
          ]}
        />
        <SectionHeading>Wizard de import in 4 pasi</SectionHeading>
        <NumberedList
          items={[
            <><strong>Incarcare</strong> — selectati fisierul <Code>regions.xlsx</Code>. Doar fisierele <Code>.xlsx</Code> sunt acceptate.</>,
            <><strong>Previzualizare</strong> — sistemul analizeaza fisierul si afiseaza pe blocuri (Primarii / Entitati / Ocoale): numar de randuri valide, invalide, duplicate detectate (in fisier si in baza de date), si erori de parsare. Daca anumite blocuri colaterale (tipul de lucrari, specii de arbori, scheme de plantare) sunt detectate in fisier, va aparea o avertizare — aceste blocuri nu sunt importate in v0.9.3.</>,
            <><strong>Confirmare</strong> — alegeti modul de import: <em>Skip invalid</em> (implicit, sare peste randurile invalide) sau <em>All or nothing</em> (anuleaza tot la prima eroare). Optional, bifati <em>"Apply name changes"</em> pentru a actualiza numele sau raionul randurilor existente daca s-au schimbat in fisier (acest control este auto-disabled cand nu sunt detectate astfel de modificari).</>,
            <><strong>Finalizat</strong> — afisaza un raport pentru fiecare bloc cu numarul de randuri inserate, actualizate si sarite, plus durata totala. Operatia se face atomic intr-o tranzactie.</>,
          ]}
        />
        <SectionHeading>Idempotenta importurilor de regiuni</SectionHeading>
        <Paragraph>
          Importul foloseste codul (<Code>cod</Code>) ca cheie naturala — re-rularea
          aceluiasi fisier pe o baza de date deja populata produce un raport cu zero
          inserari si zero actualizari (totul este "skipped"). Aceasta inseamna ca
          puteti rula importul de mai multe ori in siguranta, de exemplu dupa o
          actualizare partiala a fisierului sursa.
        </Paragraph>
        <SectionHeading>Stare curenta (panou superior)</SectionHeading>
        <Paragraph>
          Sub antet, pagina afiseaza un panou cu starea curenta a celor trei tabele
          (cate primarii / entitati / ocoale exista in baza de date, plus un esantion
          de 5 randuri din fiecare). Acest panou se reincarca automat dupa fiecare
          import si va permite sa verificati rezultatul fara sa parasiti pagina.
        </Paragraph>
        <SectionHeading>Normalizator de duplicate la deploy</SectionHeading>
        <Paragraph>
          La fiecare deploy al API-ului, un script de normalizare ruleaza automat
          (configurat in <Code>fly.toml</Code>) si colapseaza duplicatele text liber
          pentru entitati silvice — de exemplu, randurile cu valoarea <em>"IS Chisinau"</em>
          (fara diacritice) sunt actualizate la <em>"ÎS Chișinău"</em> (varianta
          canonica). Scriptul este idempotent (a doua rulare nu mai face nimic) si
          modifica simultan parcele, fotografii si listele de entitati ale utilizatorilor.
        </Paragraph>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Раздел <strong>Администрирование</strong> доступен пользователям с ролью
          <em> Super Admin</em> или <em>Administrator</em>. Позволяет управлять
          пользователями, лесными организациями и системными настройками.
        </Paragraph>
        <SectionHeading>Управление пользователями</SectionHeading>
        <Paragraph>
          Страница <strong>Пользователи</strong> отображает всех пользователей, к которым
          у вас есть доступ:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Super Admin</strong> — видит и управляет всеми пользователями системы</>,
            <><strong>Administrator</strong> — видит и управляет только пользователями типа <em>Ajutor Administrator</em>, которых он создал</>,
          ]}
        />
        <SectionHeading>Создание нового пользователя</SectionHeading>
        <NumberedList
          items={[
            <>Нажмите кнопку <strong>Создать пользователя</strong>.</>,
            <>Заполните имя пользователя, полное имя и начальный пароль.</>,
            <>Выберите роль (Super Admin может назначить любую роль; Administrator может создавать только Ajutor Administrator).</>,
            <>Назначьте <strong>лесную организацию</strong> — она определяет, какие данные пользователь может создавать и просматривать.</>,
            <>Нажмите <strong>Сохранить</strong>.</>,
          ]}
        />
        <SectionHeading>Деактивация учётной записи</SectionHeading>
        <Paragraph>
          Вместо удаления учётной записи пользователя (что привело бы к потере истории данных)
          вы можете <strong>деактивировать</strong> учётную запись. Деактивированный пользователь
          больше не сможет авторизоваться, но его данные останутся в системе.
        </Paragraph>
        <SectionHeading>Изменение роли</SectionHeading>
        <Paragraph>
          Super Admin может изменить роль любого пользователя. Изменение роли немедленно
          влияет на уровень доступа пользователя при следующей авторизации.
        </Paragraph>
        <SectionHeading>Назначение лесных организаций (множественные значения, v0.9.3)</SectionHeading>
        <Paragraph>
          Начиная с версии 0.9.3 каждый пользователь может быть назначен на
          <strong> несколько лесных организаций</strong> (а не только на одну). Список
          упорядочен — первый элемент работает как <em>организация по умолчанию</em>
          для автозаполнения (когда пользователь создаёт участок или фотографию,
          значение по умолчанию для поля «лесная организация» — это первый элемент его
          списка).
        </Paragraph>
        <Paragraph>
          Интерфейс управления отображает организации в виде <strong>чипов</strong>:
        </Paragraph>
        <NumberedList
          items={[
            <>Нажмите <strong>+ Добавить</strong> рядом со списком, чтобы открыть панель выбора. Super Admin может выбрать любое значение; Administrator может назначить только организации, присутствующие в его собственном списке.</>,
            <>Нажмите <strong>X</strong> на чипе, чтобы быстро удалить его из списка.</>,
            <>Используйте стрелки вверх/вниз или drag-and-drop для переупорядочивания. Первый элемент становится дефолтным для автозаполнения.</>,
            <>Изменения сохраняются оптимистично — если попытаться назначить запрещённую организацию (администраторы не могут давать ассистенту организации, которых у них самих нет), интерфейс возвращается к прежнему состоянию и показывает ошибку <Code>ENTITY_NOT_IN_SCOPE</Code>.</>,
          ]}
        />
        <Paragraph>
          Выпадающие списки метаданных (район, примэрия, лесничество) теперь объединены
          по всем назначенным организациям — пользователь с двумя организациями увидит
          объединённые значения facets обеих, плюс расшифровку по каждой организации
          (<Code>byEntity</Code>) от эндпоинта <Code>/api/me/cascading-options</Code>.
        </Paragraph>
        <WarningBox>
          Назначение хотя бы <strong>одной лесной организации</strong> обязательно для
          того, чтобы пользователь мог создавать участки, импортировать данные или
          синхронизировать фотографии с мобильного. <strong>Super Admin</strong> и
          <strong> Controlor</strong> освобождены от этого требования. На странице
          профиля первый элемент списка отмечен значком <em>«По умолчанию»</em>.
        </WarningBox>
        <SectionHeading>Настройки полей фотографий (Super Admin)</SectionHeading>
        <Paragraph>
          На странице <strong>Настройки</strong> (доступна только для Super Admin) можно
          управлять видимостью и обязательностью полей метаданных для фотографий:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Видимое</strong> — отображается ли поле в интерфейсе</>,
            <><strong>Обязательное</strong> — должно ли поле быть заполнено при загрузке фотографии</>,
          ]}
        />
        <SectionHeading>Журнал аудита</SectionHeading>
        <Paragraph>
          Страница <strong>Журнал аудита</strong> (доступна только для Super Admin) фиксирует
          все важные действия в системе: авторизации, создание/редактирование/удаление участков
          и фотографий, изменения пользователей и настроек. Каждая запись содержит дату,
          пользователя, действие и детали изменения.
        </Paragraph>
        <SectionHeading>Сброс пароля</SectionHeading>
        <Paragraph>
          Администраторы могут сбросить пароль пользователя со страницы управления
          пользователями. Новый пароль должен быть передан пользователю по защищённому каналу.
          Рекомендуется, чтобы пользователь сменил пароль при первой авторизации.
        </Paragraph>
        <SectionHeading>Страница «Регионы» — справочные данные (v0.9.3, только Super Admin)</SectionHeading>
        <Paragraph>
          Страница <strong>Регионы</strong> (<Code>/app/admin/regions</Code>) — это
          новое дополнение в версии 0.9.3, позволяющее импортировать канонические
          справочные таблицы из файла <Code>info/regions.xlsx</Code>:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Примэрии</strong> — около 908 примэрий Республики Молдова с числовым кодом (<Code>Cod_Primarii</Code>), названием и районом.</>,
            <><strong>Лесные организации</strong> — 26 организаций (государственных предприятий) с коротким кодом (BL, CL, CU, ...) и каноническим полным названием (<em>«ÎSÎS Bălți»</em>).</>,
            <><strong>Лесничества</strong> — 87 лесничеств, каждое из которых принадлежит лесной организации (FK).</>,
          ]}
        />
        <SectionHeading>Мастер импорта в 4 шага</SectionHeading>
        <NumberedList
          items={[
            <><strong>Загрузка</strong> — выберите файл <Code>regions.xlsx</Code>. Принимаются только файлы <Code>.xlsx</Code>.</>,
            <><strong>Предпросмотр</strong> — система анализирует файл и показывает по блокам (Примэрии / Организации / Лесничества): количество валидных строк, невалидных, обнаруженных дубликатов (в файле и в БД) и ошибок парсинга. Если в файле обнаружены побочные блоки (типы работ, виды деревьев, схемы посадки), появится предупреждение — эти блоки в v0.9.3 не импортируются.</>,
            <><strong>Подтверждение</strong> — выберите режим импорта: <em>Skip invalid</em> (по умолчанию, пропускает невалидные строки) или <em>All or nothing</em> (отменяет всё при первой ошибке). Опционально отметьте <em>«Apply name changes»</em> для обновления имени или района существующих строк, если они изменились в файле (этот элемент управления автоматически отключается, если такие изменения не обнаружены).</>,
            <><strong>Готово</strong> — отображает отчёт по каждому блоку с количеством вставленных, обновлённых и пропущенных строк, плюс общую длительность. Операция атомарна (одна транзакция).</>,
          ]}
        />
        <SectionHeading>Идемпотентность импорта регионов</SectionHeading>
        <Paragraph>
          Импорт использует код (<Code>cod</Code>) как естественный ключ — повторный
          запуск того же файла на уже заполненной базе данных даёт отчёт с нулём
          вставок и нулём обновлений (всё «skipped»). Это значит, что импорт можно
          безопасно запускать многократно, например после частичного обновления
          исходного файла.
        </Paragraph>
        <SectionHeading>Текущее состояние (верхняя панель)</SectionHeading>
        <Paragraph>
          Под заголовком страница отображает панель текущего состояния трёх таблиц
          (количество примэрий / организаций / лесничеств в БД, плюс выборка из 5
          строк каждой). Эта панель автоматически обновляется после каждого импорта
          и позволяет проверить результат, не покидая страницу.
        </Paragraph>
        <SectionHeading>Нормализатор дубликатов при деплое</SectionHeading>
        <Paragraph>
          При каждом деплое API автоматически запускается скрипт нормализации
          (настроенный в <Code>fly.toml</Code>), который сворачивает дубликаты
          свободного текста для лесных организаций — например, строки со значением
          <em> «IS Chisinau»</em> (без диакритики) обновляются до <em>«ÎS Chișinău»</em>
          (канонический вариант). Скрипт идемпотентен (повторный запуск не делает
          ничего) и одновременно затрагивает участки, фотографии и списки организаций
          пользователей.
        </Paragraph>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The <strong>Administration</strong> section is available to users with a
          <em> Super Admin</em> or <em>Administrator</em> role. It allows managing users,
          forestry entities, and system settings.
        </Paragraph>
        <SectionHeading>User Management</SectionHeading>
        <Paragraph>
          The <strong>Users</strong> page displays all users you have access to:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Super Admin</strong> — sees and manages all users in the system</>,
            <><strong>Administrator</strong> — sees and manages only <em>Ajutor Administrator</em> users they created</>,
          ]}
        />
        <SectionHeading>Creating a New User</SectionHeading>
        <NumberedList
          items={[
            <>Click the <strong>Create user</strong> button.</>,
            <>Fill in the username, full name, and initial password.</>,
            <>Select the role (Super Admin can assign any role; Administrator can only create Ajutor Administrator).</>,
            <>Assign a <strong>forestry entity</strong> — this determines what data the user can create and view.</>,
            <>Click <strong>Save</strong>.</>,
          ]}
        />
        <SectionHeading>Deactivating an Account</SectionHeading>
        <Paragraph>
          Instead of deleting a user account (which would lose the data history), you can
          <strong> deactivate</strong> the account. A deactivated user can no longer log in, but
          their data remains in the system.
        </Paragraph>
        <SectionHeading>Changing Roles</SectionHeading>
        <Paragraph>
          Super Admin can modify any user's role. The role change immediately affects the user's
          access level at the next login.
        </Paragraph>
        <SectionHeading>Assigning Forestry Entities (multi-value, v0.9.3)</SectionHeading>
        <Paragraph>
          As of v0.9.3, each user can be assigned to <strong>multiple forestry
          entities</strong> (not just one). The list is ordered — the first element acts
          as the <em>default entity</em> for autofill (when the user creates a parcel or
          a photo, the default value for the "forestry entity" field is the first
          element of their list).
        </Paragraph>
        <Paragraph>
          The management UI displays entities as <strong>chips (pills)</strong>:
        </Paragraph>
        <NumberedList
          items={[
            <>Click <strong>+ Add</strong> next to the list to open a selector panel. Super Admin can pick any value; Administrator can only assign entities present in their own list.</>,
            <>Click the <strong>X</strong> on a chip to quickly remove it from the list.</>,
            <>Use the up/down <strong>arrows</strong> or drag-and-drop to reorder. The first element becomes the autofill default.</>,
            <>Changes are saved optimistically — if you try to assign a disallowed entity (administrators cannot give an assistant entities they don't have themselves), the UI rolls back and shows an <Code>ENTITY_NOT_IN_SCOPE</Code> error.</>,
          ]}
        />
        <Paragraph>
          Metadata dropdowns (district, municipality, forestry office) are now unioned
          across all assigned entities — a user with two entities sees the combined
          facets values, plus a per-entity breakdown (<Code>byEntity</Code>) returned
          by the <Code>/api/me/cascading-options</Code> endpoint.
        </Paragraph>
        <WarningBox>
          Assigning at least <strong>one forestry entity</strong> is mandatory for a
          user to create parcels, import data, or sync photos from mobile.
          <strong> Super Admin</strong> and <strong>Controlor</strong> are exempt. On
          the profile page, the first list element is marked with a
          <em> "Default"</em> badge.
        </WarningBox>
        <SectionHeading>Photo Field Settings (Super Admin)</SectionHeading>
        <Paragraph>
          From the <strong>Settings</strong> page (available only for Super Admin), you can
          control the visibility and requirement of metadata fields for photos:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Visible</strong> — whether the field is displayed in the interface</>,
            <><strong>Required</strong> — whether the field must be filled when uploading a photo</>,
          ]}
        />
        <SectionHeading>Audit Log</SectionHeading>
        <Paragraph>
          The <strong>Audit log</strong> page (available only for Super Admin) records all
          important actions in the system: logins, creation/editing/deletion of parcels and
          photos, user and settings changes. Each entry contains the date, user, action, and
          change details.
        </Paragraph>
        <SectionHeading>Password Reset</SectionHeading>
        <Paragraph>
          Administrators can reset a user's password from the user management page. The new
          password must be communicated to the user through a secure channel. It is recommended
          that the user change their password at first login.
        </Paragraph>
        <SectionHeading>Regions Page — reference data (v0.9.3, Super Admin only)</SectionHeading>
        <Paragraph>
          The <strong>Regions</strong> page (<Code>/app/admin/regions</Code>) is a new
          addition in v0.9.3 that imports the canonical reference tables from the
          <Code>info/regions.xlsx</Code> file:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Mayoralties</strong> — about 908 Moldovan mayoralties with numeric code (<Code>Cod_Primarii</Code>), name, and district.</>,
            <><strong>Forestry entities</strong> — 26 state enterprises with a short code (BL, CL, CU, ...) and the canonical full name (<em>"ÎSÎS Bălți"</em>).</>,
            <><strong>Forest districts</strong> — 87 districts, each belonging to a forestry entity (FK).</>,
          ]}
        />
        <SectionHeading>4-step import wizard</SectionHeading>
        <NumberedList
          items={[
            <><strong>Upload</strong> — select the <Code>regions.xlsx</Code> file. Only <Code>.xlsx</Code> files are accepted.</>,
            <><strong>Preview</strong> — the system parses the file and reports per-block (Mayoralties / Entities / Districts): valid row count, invalid count, duplicates detected (in-file and in-DB), and parse errors. If sidecar blocks (work types, tree species, planting schemes) are detected, a warning appears — those blocks are not imported in v0.9.3.</>,
            <><strong>Confirm</strong> — pick an import mode: <em>Skip invalid</em> (default — skips invalid rows) or <em>All or nothing</em> (aborts on the first error). Optionally tick <em>"Apply name changes"</em> to update the name or district of existing rows when they changed in the file (this control is auto-disabled when no such changes are detected).</>,
            <><strong>Done</strong> — shows a per-block report with the number of inserted, updated, and skipped rows, plus total duration. The operation is atomic (one transaction).</>,
          ]}
        />
        <SectionHeading>Idempotent regions imports</SectionHeading>
        <Paragraph>
          The import uses the code (<Code>cod</Code>) as a natural key — re-running the
          same file against an already-populated DB produces a report with zero inserts
          and zero updates (everything is "skipped"). This means imports can be run
          repeatedly with no risk, e.g. after a partial update of the source file.
        </Paragraph>
        <SectionHeading>Current state (top panel)</SectionHeading>
        <Paragraph>
          Below the header, the page displays a current-state panel for the three
          tables (counts of mayoralties / entities / districts in the DB, plus a
          5-row sample of each). This panel auto-refreshes after every import so you
          can verify the result without leaving the page.
        </Paragraph>
        <SectionHeading>Deploy-time duplicate normalizer</SectionHeading>
        <Paragraph>
          On every API deploy, a normalization script runs automatically (configured
          in <Code>fly.toml</Code>) and collapses free-text duplicates for forestry
          entities — for example, rows with value <em>"IS Chisinau"</em> (no
          diacritics) are updated to <em>"ÎS Chișinău"</em> (the canonical form).
          The script is idempotent (a second run does nothing) and updates parcels,
          photos, and user entity lists at once.
        </Paragraph>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Administrare',
    ru: 'Администрирование',
    en: 'Administration',
  };

  return { id: 'admin', title: titles[lang], icon: Settings, content: content[lang] };
}

// ---------------------------------------------------------------------------

function profileSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Pagina de <strong>Profil</strong> permite gestionarea informatiilor personale,
          a metadatelor implicite si a setarilor contului. Accesati aceasta pagina apasand
          pe numele dumneavoastra din bara laterala.
        </Paragraph>
        <SectionHeading>Date personale</SectionHeading>
        <BulletList
          items={[
            <><strong>Nume complet</strong> — numele afisat in profil (vizibil doar aici)</>,
            <><strong>Cod autor</strong> — codul scurt atribuit de administrator (de ex. <code>Pnerp_edd_01</code>). Este afisat pe fotografii, in tabelul <em>Baza de date</em> si pe watermark-ul din aplicatia mobila. Nu este editabil de utilizator.</>,
            <><strong>Email</strong> — adresa de email pentru notificari (optional)</>,
            <><strong>Limba preferata</strong> — selectati intre Romana, Rusa si Engleza. Schimbarea limbii afecteaza imediat interfata.</>,
          ]}
        />
        <SectionHeading>Metadate implicite</SectionHeading>
        <Paragraph>
          Setati valori implicite pentru campurile de metadate care se completeaza automat
          la incarcarea fotografiilor:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Raion implicit</strong> — raionul selectat automat</>,
            <><strong>Primarie implicita</strong> — primaria selectata automat</>,
            <><strong>UCF implicit</strong> — unitatea de control forestier selectata automat</>,
            <><strong>Ocol silvic implicit</strong> — ocolul silvic selectat automat</>,
          ]}
        />
        <InfoBox>
          Configurarea metadatelor implicite economiseste timp semnificativ la incarcarea
          fotografiilor, in special pe aplicatia mobila unde introducerea manuala este mai
          dificila.
        </InfoBox>
        <SectionHeading>Entitati silvice atribuite (multi-valoare, v0.9.3)</SectionHeading>
        <Paragraph>
          Sectiunea <strong>Entitati silvice</strong> afiseaza lista de entitati
          atribuite contului dumneavoastra ca un rand de chip-uri (pile). Incepand
          cu versiunea 0.9.3, lista poate contine <strong>mai multe entitati</strong>
          (nu doar una). Primul element din lista este marcat cu un badge
          <em> "Implicită"</em> — aceasta este entitatea folosita automat ca valoare
          implicita la crearea de parcele si fotografii.
        </Paragraph>
        <Paragraph>
          Lista este <strong>read-only pentru utilizator</strong> — configurarea se
          face de catre un administrator (Super Admin sau Administrator) din pagina
          de gestionare a utilizatorilor. Daca aveti nevoie sa primiti acces la o
          alta entitate, sau sa schimbati entitatea implicita, contactati
          administratorul.
        </Paragraph>
        <Paragraph>
          Daca lista este goala (nu aveti nicio entitate atribuita), vor aparea
          mesaje de avertizare in interfata si nu veti putea crea parcele sau
          incarca fotografii pana cand un administrator va atribuie cel putin o
          entitate. Super Admin si Controlor sunt exceptati de la aceasta cerinta.
        </Paragraph>
        <SectionHeading>Schimbarea parolei</SectionHeading>
        <NumberedList
          items={[
            <>Completati <strong>parola curenta</strong> in campul corespunzator.</>,
            <>Introduceti <strong>noua parola</strong> (minim 8 caractere, cel putin o litera mare, o litera mica, o cifra si un caracter special).</>,
            <>Confirmati noua parola in campul de confirmare.</>,
            <>Apasati <strong>Schimba parola</strong>.</>,
          ]}
        />
        <SectionHeading>Setari notificari</SectionHeading>
        <Paragraph>
          Activati sau dezactivati notificarile pe email folosind comutatorul din sectiunea
          Notificari. Cand este activat, veti primi notificari pe email despre activitati
          importante legate de datele dumneavoastra.
        </Paragraph>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Страница <strong>Профиль</strong> позволяет управлять личной информацией,
          метаданными по умолчанию и настройками учётной записи. Откройте эту страницу,
          нажав на своё имя в боковой панели.
        </Paragraph>
        <SectionHeading>Личные данные</SectionHeading>
        <BulletList
          items={[
            <><strong>Полное имя</strong> — имя, отображаемое в профиле (видно только здесь)</>,
            <><strong>Код автора</strong> — короткий код, назначенный администратором (напр. <code>Pnerp_edd_01</code>). Отображается на фотографиях, в таблице <em>База данных</em> и на водяном знаке мобильного приложения. Не редактируется пользователем.</>,
            <><strong>Email</strong> — адрес электронной почты для уведомлений (необязательно)</>,
            <><strong>Предпочитаемый язык</strong> — выберите между Румынским, Русским и Английским. Смена языка немедленно влияет на интерфейс.</>,
          ]}
        />
        <SectionHeading>Метаданные по умолчанию</SectionHeading>
        <Paragraph>
          Задайте значения по умолчанию для полей метаданных, которые автоматически
          заполняются при загрузке фотографий:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Район по умолчанию</strong> — автоматически выбираемый район</>,
            <><strong>Примэрия по умолчанию</strong> — автоматически выбираемая примэрия</>,
            <><strong>UCF по умолчанию</strong> — автоматически выбираемая единица лесного контроля</>,
            <><strong>Лесничество по умолчанию</strong> — автоматически выбираемое лесничество</>,
          ]}
        />
        <InfoBox>
          Настройка метаданных по умолчанию значительно экономит время при загрузке
          фотографий, особенно в мобильном приложении, где ручной ввод затруднён.
        </InfoBox>
        <SectionHeading>Назначенные лесные организации (множественные значения, v0.9.3)</SectionHeading>
        <Paragraph>
          Раздел <strong>Лесные организации</strong> отображает список организаций,
          назначенных вашей учётной записи, в виде ряда чипов. Начиная с версии 0.9.3
          список может содержать <strong>несколько организаций</strong> (а не только
          одну). Первый элемент списка отмечен значком <em>«По умолчанию»</em> —
          это организация, автоматически используемая как значение по умолчанию при
          создании участков и фотографий.
        </Paragraph>
        <Paragraph>
          Список <strong>только для чтения</strong> со стороны пользователя —
          настройка выполняется администратором (Super Admin или Administrator) на
          странице управления пользователями. Если вам нужен доступ к другой
          организации или хотите сменить организацию по умолчанию, свяжитесь с
          администратором.
        </Paragraph>
        <Paragraph>
          Если список пуст (организации не назначены), в интерфейсе появятся
          предупреждения, и вы не сможете создавать участки или загружать
          фотографии, пока администратор не назначит хотя бы одну организацию.
          Super Admin и Controlor освобождены от этого требования.
        </Paragraph>
        <SectionHeading>Смена пароля</SectionHeading>
        <NumberedList
          items={[
            <>Введите <strong>текущий пароль</strong> в соответствующее поле.</>,
            <>Введите <strong>новый пароль</strong> (минимум 8 символов, хотя бы одна заглавная и строчная буква, цифра и специальный символ).</>,
            <>Подтвердите новый пароль в поле подтверждения.</>,
            <>Нажмите <strong>Сменить пароль</strong>.</>,
          ]}
        />
        <SectionHeading>Настройки уведомлений</SectionHeading>
        <Paragraph>
          Включите или отключите уведомления по электронной почте с помощью переключателя
          в разделе Уведомления. При включении вы будете получать уведомления по электронной
          почте о важных действиях, связанных с вашими данными.
        </Paragraph>
      </>
    ),
    en: (
      <>
        <Paragraph>
          The <strong>Profile</strong> page allows managing personal information, default
          metadata, and account settings. Access this page by clicking your name in the sidebar.
        </Paragraph>
        <SectionHeading>Personal Data</SectionHeading>
        <BulletList
          items={[
            <><strong>Full name</strong> — name shown only on this profile page</>,
            <><strong>Author code</strong> — short code assigned by the admin (e.g. <code>Pnerp_edd_01</code>). Shown on photos, in the <em>Database</em> table, and on the mobile watermark. Read-only for users.</>,
            <><strong>Email</strong> — email address for notifications (optional)</>,
            <><strong>Preferred language</strong> — choose between Romanian, Russian, and English. Changing the language immediately affects the interface.</>,
          ]}
        />
        <SectionHeading>Default Metadata</SectionHeading>
        <Paragraph>
          Set default values for metadata fields that are automatically filled when uploading
          photos:
        </Paragraph>
        <BulletList
          items={[
            <><strong>Default district</strong> — automatically selected district</>,
            <><strong>Default municipality</strong> — automatically selected municipality</>,
            <><strong>Default UCF</strong> — automatically selected forestry control unit</>,
            <><strong>Default forestry office</strong> — automatically selected forestry office</>,
          ]}
        />
        <InfoBox>
          Configuring default metadata saves significant time when uploading photos, especially
          on the mobile app where manual input is more difficult.
        </InfoBox>
        <SectionHeading>Assigned Forestry Entities (multi-value, v0.9.3)</SectionHeading>
        <Paragraph>
          The <strong>Forestry entities</strong> section displays the list of entities
          assigned to your account as a row of chips (pills). As of v0.9.3, the list
          can contain <strong>multiple entities</strong> (not just one). The first
          element is marked with a <em>"Default"</em> badge — this is the entity used
          automatically as the default value when you create parcels and photos.
        </Paragraph>
        <Paragraph>
          The list is <strong>read-only for users</strong> — configuration is done by
          an administrator (Super Admin or Administrator) from the user management
          page. If you need access to another entity, or to switch your default
          entity, contact your administrator.
        </Paragraph>
        <Paragraph>
          If the list is empty (no entities assigned), warning messages appear in the
          UI and you cannot create parcels or upload photos until an administrator
          assigns at least one entity. Super Admin and Controlor are exempt from this
          requirement.
        </Paragraph>
        <SectionHeading>Changing Password</SectionHeading>
        <NumberedList
          items={[
            <>Enter your <strong>current password</strong> in the corresponding field.</>,
            <>Enter the <strong>new password</strong> (minimum 8 characters, at least one uppercase letter, one lowercase letter, one digit, and one special character).</>,
            <>Confirm the new password in the confirmation field.</>,
            <>Click <strong>Change password</strong>.</>,
          ]}
        />
        <SectionHeading>Notification Settings</SectionHeading>
        <Paragraph>
          Enable or disable email notifications using the toggle in the Notifications section.
          When enabled, you will receive email notifications about important activities related
          to your data.
        </Paragraph>
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Profil utilizator',
    ru: 'Профиль пользователя',
    en: 'User Profile',
  };

  return { id: 'profile', title: titles[lang], icon: User, content: content[lang] };
}

// ---------------------------------------------------------------------------

function architectureSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          Aceasta sectiune prezinta arhitectura tehnica a platformei PNERP pentru utilizatorii
          cu cunostinte tehnice si pentru echipa de dezvoltare.
        </Paragraph>
        <SectionHeading>Stiva tehnologica</SectionHeading>
        <DocTable
          headers={['Strat', 'Tehnologie', 'Detalii']}
          rows={[
            ['Frontend Web', 'React 19.2 + TypeScript 5.6', 'Interfata SPA cu Tailwind CSS 3.4, Zustand (state), TanStack Query (date)'],
            ['Backend API', 'Node.js 24 + Fastify 5.1', 'API RESTful cu TypeScript, validare Zod'],
            ['ORM', 'Prisma 5.22', 'Acces la baza de date cu tipuri generate automat'],
            ['Baza de date', 'PostgreSQL 18', 'Baza de date relationala cu suport GIS (cube, earthdistance, pg_trgm)'],
            ['Autentificare', 'Better Auth', 'Sesiuni securizate, hash bcrypt pentru parole, bearer token pentru mobil'],
            ['Stocare fisiere', 'Cloudflare R2 (EU)', 'Stocare compatibila S3 pentru fotografii si atasamente, regiune EU'],
            ['Aplicatie mobila', 'Expo SDK 54 / React Native 0.79', 'Aplicatie cross-platform cu suport offline si sincronizare automata'],
            ['Geo', '@turf/boolean-point-in-polygon', 'Point-in-polygon cu prag de auto-ajustare de 200m'],
            ['Teste', 'Vitest + Playwright', 'Teste unitare, de integrare si end-to-end'],
          ]}
        />
        <SectionHeading>Diagrama sistemului</SectionHeading>
        <ArchitectureDiagram />
        <SectionHeading>Infrastructura de deployment</SectionHeading>
        <BulletList
          items={[
            <><strong>Vercel</strong> — gazduirea aplicatiei web (React SPA) cu CDN global, domeniu: <Code>www.pnerp.org</Code></>,
            <><strong>Fly.io</strong> — gazduirea serverului API (Fastify 5) cu scalare automata</>,
            <><strong>Cloudflare R2 (EU)</strong> — stocare obiect compatibila S3 pentru fotografii si atasamente, regiune EU</>,
            <><strong>PostgreSQL 18</strong> — baza de date gestionata cu backup-uri automate, extensii: pgcrypto, pg_trgm, cube, earthdistance</>,
          ]}
        />
        <SectionHeading>Atribuirea automata point-in-polygon</SectionHeading>
        <Paragraph>
          Sistemul foloseste un algoritm de <strong>point-in-polygon</strong> pentru a atribui
          automat fotografiile georeferentiate la parcele. Procesul este urmatorul:
        </Paragraph>
        <NumberedList
          items={[
            <>La incarcarea unei fotografii, coordonatele GPS sunt extrase din metadatele EXIF.</>,
            <>Sistemul verifica daca punctul cade in interiorul unui poligon de parcela.</>,
            <>Daca da — fotografia primeste status <strong>verde</strong> (In poligon).</>,
            <>Daca nu, se alege cel mai apropiat poligon in raza de 200m; fotografia este atribuita automat cu status <strong>galben</strong> (Ajustat automat), iar coordonatele GPS raman neschimbate.</>,
            <>Daca nu exista niciun poligon in raza de 200m, fotografia primeste status <strong>rosu</strong> (Neasignat).</>,
          ]}
        />
        <SectionHeading>Ierarhia de roluri si middleware de acces</SectionHeading>
        <Paragraph>
          Accesul la date este controlat printr-un middleware la nivel de API care verifica
          rolul utilizatorului si proprietatea datelor. Ierarhia de 4 roluri (Super Admin &gt;
          Controlor &gt; Administrator &gt; Ajutor Administrator) determina atat vizibilitatea
          datelor, cat si operatiunile permise. Fiecare cerere API trece prin acest middleware
          inainte de a ajunge la logica de business.
        </Paragraph>
        <SectionHeading>Subpoligoane si verificarea starii</SectionHeading>
        <BulletList
          items={[
            <><strong>SubParcel</strong> (tabelul <Code>sub_parcel</Code>): subdiviziunile unui poligon, legate de <Code>Parcel</Code> si sterse odata cu el (cascade). Un singur subpoligon activ per poligon si UCF. Tine si receptiile tehnice (<Code>source = 'receptie'</Code>, cu campanie, sezon, parcele, arhiva sursa, primarie, raion si entitate); poligonul poate lipsi pentru o receptie din afara oricarui poligon. API: <Code>/api/subparcels</Code> (GeoJSON pentru harta, lista pe poligon, lista fara poligon, stergere). Subpoligonul unei fotografii se calculeaza la citire, nu se stocheaza.</>,
            <><strong>Receptii tehnice</strong>: tabelul <Code>receptie_tehnica</Code> nu mai este folosit de aplicatie; ramane o versiune ca sursa a mutarii in <Code>sub_parcel</Code>, facuta de scriptul <Code>migrate-receptii</Code> la lansare. Ruta <Code>/api/receptii</Code> a fost eliminata.</>,
            <><strong>Verificarea starii</strong>: Fly.io directioneaza traficul dupa <Code>/api/health/ready</Code>, care esueaza doar cand baza de date nu raspunde; problemele cu stocarea R2 sau partitia de audit sunt raportate ca <em>degraded</em>, fara a scoate serverul din rotatie.</>,
          ]}
        />
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Этот раздел описывает техническую архитектуру платформы PNERP для пользователей
          с техническими знаниями и для команды разработки.
        </Paragraph>
        <SectionHeading>Технологический стек</SectionHeading>
        <DocTable
          headers={['Слой', 'Технология', 'Подробности']}
          rows={[
            ['Веб-фронтенд', 'React 19.2 + TypeScript 5.6', 'SPA-интерфейс с Tailwind CSS 3.4, Zustand (состояние), TanStack Query (данные)'],
            ['API бэкенд', 'Node.js 24 + Fastify 5.1', 'RESTful API с TypeScript, валидация Zod'],
            ['ORM', 'Prisma 5.22', 'Доступ к базе данных с автогенерируемыми типами'],
            ['База данных', 'PostgreSQL 18', 'Реляционная база данных с поддержкой GIS (cube, earthdistance, pg_trgm)'],
            ['Аутентификация', 'Better Auth', 'Защищённые сессии, хеширование bcrypt для паролей, bearer-токен для мобильных'],
            ['Хранение файлов', 'Cloudflare R2 (EU)', 'S3-совместимое хранилище для фотографий и вложений, регион EU'],
            ['Мобильное приложение', 'Expo SDK 54 / React Native 0.79', 'Кроссплатформенное приложение с офлайн-поддержкой и автосинхронизацией'],
            ['Гео', '@turf/boolean-point-in-polygon', 'Point-in-polygon с порогом автокоррекции 200 м'],
            ['Тесты', 'Vitest + Playwright', 'Модульные, интеграционные и end-to-end тесты'],
          ]}
        />
        <SectionHeading>Диаграмма системы</SectionHeading>
        <ArchitectureDiagram />
        <SectionHeading>Инфраструктура развёртывания</SectionHeading>
        <BulletList
          items={[
            <><strong>Vercel</strong> — хостинг веб-приложения (React SPA) с глобальным CDN, домен: <Code>www.pnerp.org</Code></>,
            <><strong>Fly.io</strong> — хостинг API-сервера (Fastify 5) с автоматическим масштабированием</>,
            <><strong>Cloudflare R2 (EU)</strong> — объектное хранилище, совместимое с S3, для фотографий и вложений, регион EU</>,
            <><strong>PostgreSQL 18</strong> — управляемая база данных с автоматическими резервными копиями, расширения: pgcrypto, pg_trgm, cube, earthdistance</>,
          ]}
        />
        <SectionHeading>Автоматическая привязка point-in-polygon</SectionHeading>
        <Paragraph>
          Система использует алгоритм <strong>point-in-polygon</strong> для автоматической
          привязки геореференцированных фотографий к участкам. Процесс следующий:
        </Paragraph>
        <NumberedList
          items={[
            <>При загрузке фотографии GPS-координаты извлекаются из метаданных EXIF.</>,
            <>Система проверяет, попадает ли точка внутрь полигона участка.</>,
            <>Если да — фотография получает статус <strong>зелёный</strong> (In poligon).</>,
            <>Если нет, выбирается ближайший полигон в радиусе 200 м; фотография привязывается автоматически со статусом <strong>жёлтый</strong> (Ajustat automat), GPS-координаты не меняются.</>,
            <>Если в радиусе 200 м нет ни одного полигона, фотография получает статус <strong>красный</strong> (Neasignat).</>,
          ]}
        />
        <SectionHeading>Иерархия ролей и middleware доступа</SectionHeading>
        <Paragraph>
          Доступ к данным контролируется middleware на уровне API, который проверяет роль
          пользователя и принадлежность данных. Иерархия из 4 ролей (Super Admin &gt;
          Controlor &gt; Administrator &gt; Ajutor Administrator) определяет как видимость
          данных, так и разрешённые операции. Каждый API-запрос проходит через этот middleware
          перед достижением бизнес-логики.
        </Paragraph>
        <SectionHeading>Подполигоны и проверка состояния</SectionHeading>
        <BulletList
          items={[
            <><strong>SubParcel</strong> (таблица <Code>sub_parcel</Code>): части полигона, связанные с <Code>Parcel</Code> и удаляемые вместе с ним (cascade). Один активный подполигон на полигон и UCF. Хранит и технические приёмки (<Code>source = 'receptie'</Code>, с кампанией, сезоном, участками, исходным архивом, примэрией, районом и организацией); полигон может отсутствовать у приёмки вне любого полигона. API: <Code>/api/subparcels</Code> (GeoJSON для карты, список по полигону, список без полигона, удаление). Подполигон фотографии вычисляется при чтении и не хранится.</>,
            <><strong>Технические приёмки</strong>: таблица <Code>receptie_tehnica</Code> приложением больше не используется; она остаётся на одну версию как источник переноса в <Code>sub_parcel</Code>, который выполняет скрипт <Code>migrate-receptii</Code> при выпуске. Маршрут <Code>/api/receptii</Code> удалён.</>,
            <><strong>Проверка состояния</strong>: Fly.io направляет трафик по <Code>/api/health/ready</Code>, который падает только когда база данных не отвечает; проблемы с хранилищем R2 или партицией аудита отмечаются как <em>degraded</em>, не выводя сервер из ротации.</>,
          ]}
        />
      </>
    ),
    en: (
      <>
        <Paragraph>
          This section presents the technical architecture of the PNERP platform for technically
          knowledgeable users and the development team.
        </Paragraph>
        <SectionHeading>Technology Stack</SectionHeading>
        <DocTable
          headers={['Layer', 'Technology', 'Details']}
          rows={[
            ['Web Frontend', 'React 19.2 + TypeScript 5.6', 'SPA interface with Tailwind CSS 3.4, Zustand (state), TanStack Query (data)'],
            ['Backend API', 'Node.js 24 + Fastify 5.1', 'RESTful API with TypeScript, Zod validation'],
            ['ORM', 'Prisma 5.22', 'Database access with auto-generated types'],
            ['Database', 'PostgreSQL 18', 'Relational database with GIS support (cube, earthdistance, pg_trgm)'],
            ['Authentication', 'Better Auth', 'Secure sessions, bcrypt password hashing, bearer token for mobile'],
            ['File Storage', 'Cloudflare R2 (EU)', 'S3-compatible storage for photos and attachments, EU region'],
            ['Mobile App', 'Expo SDK 54 / React Native 0.79', 'Cross-platform app with offline support and automatic sync'],
            ['Geo', '@turf/boolean-point-in-polygon', 'Point-in-polygon with 200m auto-adjustment threshold'],
            ['Tests', 'Vitest + Playwright', 'Unit, integration, and end-to-end tests'],
          ]}
        />
        <SectionHeading>System Diagram</SectionHeading>
        <ArchitectureDiagram />
        <SectionHeading>Deployment Infrastructure</SectionHeading>
        <BulletList
          items={[
            <><strong>Vercel</strong> — web app hosting (React SPA) with global CDN, domain: <Code>www.pnerp.org</Code></>,
            <><strong>Fly.io</strong> — API server hosting (Fastify 5) with auto-scaling</>,
            <><strong>Cloudflare R2 (EU)</strong> — S3-compatible object storage for photos and attachments, EU region</>,
            <><strong>PostgreSQL 18</strong> — managed database with automatic backups, extensions: pgcrypto, pg_trgm, cube, earthdistance</>,
          ]}
        />
        <SectionHeading>Automatic Point-in-Polygon Assignment</SectionHeading>
        <Paragraph>
          The system uses a <strong>point-in-polygon</strong> algorithm to automatically assign
          georeferenced photos to parcels. The process is as follows:
        </Paragraph>
        <NumberedList
          items={[
            <>When a photo is uploaded, GPS coordinates are extracted from EXIF metadata.</>,
            <>The system checks whether the point falls inside a parcel polygon.</>,
            <>If yes — the photo receives <strong>green</strong> status (In polygon).</>,
            <>If not, the nearest polygon within 200m is chosen; the photo is automatically assigned with <strong>yellow</strong> status (Auto-adjusted) and its GPS coordinates stay unchanged.</>,
            <>If there is no polygon within 200m, the photo receives <strong>red</strong> status (Unassigned).</>,
          ]}
        />
        <SectionHeading>Role Hierarchy and Access Middleware</SectionHeading>
        <Paragraph>
          Data access is controlled by API-level middleware that checks the user's role and data
          ownership. The 4-role hierarchy (Super Admin &gt; Controlor &gt; Administrator &gt;
          Ajutor Administrator) determines both data visibility and permitted operations. Every
          API request passes through this middleware before reaching business logic.
        </Paragraph>
        <SectionHeading>Subpolygons and health check</SectionHeading>
        <BulletList
          items={[
            <><strong>SubParcel</strong> (table <Code>sub_parcel</Code>): subdivisions of a polygon, linked to <Code>Parcel</Code> and deleted with it (cascade). One live subpolygon per polygon and UCF. It also holds technical receptions (<Code>source = 'receptie'</Code>, with campaign, season, parcels, source archive, mayoralty, district and entity); the polygon may be empty for a reception outside every polygon. API: <Code>/api/subparcels</Code> (map GeoJSON, per-polygon list, no-polygon list, delete). A photo's subpolygon is computed on read, not stored.</>,
            <><strong>Technical receptions</strong>: the <Code>receptie_tehnica</Code> table is no longer used by the app; it stays one release as the source of the move into <Code>sub_parcel</Code>, done by the <Code>migrate-receptii</Code> script at release. The <Code>/api/receptii</Code> route was removed.</>,
            <><strong>Health check</strong>: Fly.io routes traffic by <Code>/api/health/ready</Code>, which fails only when the database is unreachable; problems with R2 storage or the audit partition are reported as <em>degraded</em> without taking the server out of rotation.</>,
          ]}
        />
      </>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Arhitectura tehnica',
    ru: 'Техническая архитектура',
    en: 'Technical Architecture',
  };

  return { id: 'architecture', title: titles[lang], icon: Server, content: content[lang] };
}

// ---------------------------------------------------------------------------

function faqSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <div className="space-y-6">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">De ce fotografia mea are status rosu?</h3>
          <Paragraph>
            Statusul rosu (<em>Neasignat</em>) inseamna ca coordonatele GPS ale fotografiei
            nu se afla in interiorul niciunui poligon de parcela si nici in raza de 200m de
            vreun poligon. Cauze posibile:
          </Paragraph>
          <BulletList
            items={[
              'GPS-ul dispozitivului nu a fost suficient de precis in momentul capturii',
              'Parcela corespunzatoare nu a fost inca importata in sistem',
              'Fotografia a fost facuta intr-o zona fara parcele inregistrate',
            ]}
          />
          <Paragraph>
            <strong>Solutie:</strong> un administrator poate atribui manual o parcela fotografiei
            din pagina Baza de date, folosind optiunea <em>Atribuie parcela</em>.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Ce inseamna mesajele de eroare la conectare?</h3>
          <BulletList
            items={[
              <><strong>„Prea multe încercări de conectare. Reîncercați peste câteva minute."</strong>: dupa 5 incercari esuate in 15 minute conectarea este blocata temporar. Asteptati cateva minute si verificati parola.</>,
              <><strong>„Serverul nu este disponibil momentan. Reîncercați mai târziu."</strong>: problema este pe server, nu in contul dumneavoastra. Reincercati mai tarziu.</>,
              <><strong>„Credențiale incorecte"</strong>: numele de utilizator sau parola sunt gresite.</>,
            ]}
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Cum schimb limba interfetei?</h3>
          <Paragraph>Exista doua modalitati de a schimba limba:</Paragraph>
          <NumberedList
            items={[
              <>Din <strong>selectorul de limba</strong> din antetul paginii — schimba limba imediat pentru sesiunea curenta.</>,
              <>Din pagina <strong>Profil</strong>, sectiunea <em>Date personale</em>, campul <em>Limba preferata</em> — salveaza preferinta permanent in cont.</>,
            ]}
          />
          <Paragraph>Limbile disponibile sunt: Romana, Rusa si Engleza.</Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Ce fac daca nu am internet in teren?</h3>
          <Paragraph>
            Aplicatia mobila PNERP include un <strong>mod offline</strong> complet functional:
          </Paragraph>
          <BulletList
            items={[
              'Puteti captura fotografii fara conexiune la internet',
              'Fotografiile sunt salvate local pe dispozitiv cu toate metadatele (coordonate GPS, data, ora)',
              'Cand conexiunea este restabilita, fotografiile se sincronizeaza automat cu serverul',
              'Puteti configura sincronizarea sa functioneze doar pe WiFi din setarile aplicatiei',
            ]}
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Cum import date din Excel?</h3>
          <Paragraph>
            Accesati pagina <strong>Import</strong> din meniul lateral si urmati asistentul
            in 4 pasi:
          </Paragraph>
          <NumberedList
            items={[
              <>Selectati fisierul Excel (<Code>meta_3.xlsx</Code> pentru parcele sau <Code>meta_3_photo.xlsx</Code> pentru fotografii)</>,
              'Verificati previzualizarea datelor',
              'Confirmati maparea coloanelor',
              'Apasati Confirma pentru a finaliza importul',
            ]}
          />
          <Paragraph>
            Puteti descarca sabloanele Excel de pe pagina de import pentru a va asigura ca
            structura fisierului este corecta.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Cine poate sterge parcele sau fotografii?</h3>
          <Paragraph>Operatiunea de stergere este restrictionata la urmatoarele roluri:</Paragraph>
          <BulletList
            items={[
              <><strong>Super Admin</strong> — poate sterge orice parcela sau fotografie din sistem</>,
              <><strong>Administrator</strong> — poate sterge doar parcelele si fotografiile proprii sau ale ajutorilor sai</>,
              <><strong>Ajutor Administrator</strong> — poate sterge datele create in cadrul entitatii administratorului parinte</>,
            ]}
          />
          <WarningBox>
            Rolul de <strong>Controlor</strong> nu are dreptul de stergere, indiferent de
            circumstante. Aceasta restrictie protejeaza integritatea datelor in procesul
            de verificare.
          </WarningBox>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Ce este Entitatea silvica si de ce am nevoie de ea?</h3>
          <Paragraph>
            <strong>Entitatea silvica</strong> este unitatea organizationala (de exemplu,
            un ocol silvic sau o directie silvica) la care apartineti. Aceasta determina:
          </Paragraph>
          <BulletList
            items={[
              'Ce date puteti vedea si gestiona',
              'Valorile disponibile in listele derulante (raioane, primarii, ocoale silvice)',
              'Perimetrul de date pentru import si creare',
            ]}
          />
          <Paragraph>
            Entitatea silvica este atribuita de un administrator. Fara aceasta atribuire,
            nu puteti crea parcele, importa date sau sincroniza fotografii.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Cum pot vedea toate fotografiile unei parcele?</h3>
          <Paragraph>
            Navigati la pagina <strong>Parcele</strong>, gasiti parcela dorita in tabel si
            deschideti meniul de actiuni. Selectati optiunea de vizualizare detalii — in
            pagina de detalii veti gasi toate fotografiile asociate parcelei respective,
            afisate ca miniaturi cu informatii despre coordonate si data capturii.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Pot partaja un filtru cu un coleg?</h3>
          <Paragraph>
            Da! Filtrele de pe pagina <strong>Filtrare si harta</strong> sunt sincronizate
            cu URL-ul paginii. Pur si simplu copiati adresa din bara de adrese a browserului
            si trimiteti-o colegului. Acesta va vedea exact aceleasi filtre aplicate cand
            deschide link-ul (presupunand ca are drepturile de acces necesare).
          </Paragraph>
        </div>
      </div>
    ),
    ru: (
      <div className="space-y-6">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Почему у моей фотографии красный статус?</h3>
          <Paragraph>
            Красный статус (<em>Neasignat</em>) означает, что GPS-координаты фотографии
            не попадают внутрь ни одного полигона участка и не находятся в радиусе 200 м ни
            от одного полигона. Возможные причины:
          </Paragraph>
          <BulletList
            items={[
              'GPS устройства был недостаточно точным в момент съёмки',
              'Соответствующий участок ещё не был импортирован в систему',
              'Фотография была сделана в зоне без зарегистрированных участков',
            ]}
          />
          <Paragraph>
            <strong>Решение:</strong> администратор может вручную привязать участок к фотографии
            со страницы База данных, используя опцию <em>Привязать участок</em>.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Что означают сообщения об ошибке при входе?</h3>
          <BulletList
            items={[
              <><strong>«Слишком много попыток входа. Повторите через несколько минут.»</strong>: после 5 неудачных попыток за 15 минут вход временно блокируется. Подождите несколько минут и проверьте пароль.</>,
              <><strong>«Сервер временно недоступен. Повторите попытку позже.»</strong>: проблема на сервере, а не в вашей учётной записи. Повторите позже.</>,
              <><strong>«Неверные учётные данные»</strong>: неверное имя пользователя или пароль.</>,
            ]}
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Как сменить язык интерфейса?</h3>
          <Paragraph>Есть два способа сменить язык:</Paragraph>
          <NumberedList
            items={[
              <>Из <strong>переключателя языка</strong> в заголовке страницы — меняет язык немедленно для текущей сессии.</>,
              <>На странице <strong>Профиль</strong>, раздел <em>Личные данные</em>, поле <em>Предпочитаемый язык</em> — сохраняет предпочтение постоянно в учётной записи.</>,
            ]}
          />
          <Paragraph>Доступные языки: Румынский, Русский и Английский.</Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Что делать, если в поле нет интернета?</h3>
          <Paragraph>
            Мобильное приложение PNERP включает полностью функциональный <strong>офлайн-режим</strong>:
          </Paragraph>
          <BulletList
            items={[
              'Вы можете делать фотографии без подключения к интернету',
              'Фотографии сохраняются локально на устройстве со всеми метаданными (GPS-координаты, дата, время)',
              'При восстановлении подключения фотографии автоматически синхронизируются с сервером',
              'Вы можете настроить синхронизацию только по WiFi в настройках приложения',
            ]}
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Как импортировать данные из Excel?</h3>
          <Paragraph>
            Откройте страницу <strong>Импорт</strong> в боковом меню и следуйте мастеру
            из 4 шагов:
          </Paragraph>
          <NumberedList
            items={[
              <>Выберите файл Excel (<Code>meta_3.xlsx</Code> для участков или <Code>meta_3_photo.xlsx</Code> для фотографий)</>,
              'Проверьте предпросмотр данных',
              'Подтвердите сопоставление столбцов',
              'Нажмите Подтвердить для завершения импорта',
            ]}
          />
          <Paragraph>
            Вы можете скачать шаблоны Excel на странице импорта, чтобы убедиться в
            правильности структуры файла.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Кто может удалять участки или фотографии?</h3>
          <Paragraph>Операция удаления ограничена следующими ролями:</Paragraph>
          <BulletList
            items={[
              <><strong>Super Admin</strong> — может удалять любой участок или фотографию в системе</>,
              <><strong>Administrator</strong> — может удалять только собственные участки и фотографии или своих помощников</>,
              <><strong>Ajutor Administrator</strong> — может удалять данные, созданные в рамках организации родительского администратора</>,
            ]}
          />
          <WarningBox>
            Роль <strong>Controlor</strong> не имеет права удаления ни при каких
            обстоятельствах. Это ограничение защищает целостность данных в процессе
            проверки.
          </WarningBox>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Что такое лесная организация и зачем она нужна?</h3>
          <Paragraph>
            <strong>Лесная организация</strong> — это организационная единица (например,
            лесничество или лесная дирекция), к которой вы принадлежите. Она определяет:
          </Paragraph>
          <BulletList
            items={[
              'Какие данные вы можете просматривать и управлять',
              'Доступные значения в выпадающих списках (районы, примэрии, лесничества)',
              'Периметр данных для импорта и создания',
            ]}
          />
          <Paragraph>
            Лесная организация назначается администратором. Без этого назначения вы не сможете
            создавать участки, импортировать данные или синхронизировать фотографии.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Как увидеть все фотографии участка?</h3>
          <Paragraph>
            Перейдите на страницу <strong>Участки</strong>, найдите нужный участок в таблице и
            откройте меню действий. Выберите опцию просмотра деталей — на странице деталей вы
            найдёте все фотографии, связанные с данным участком, отображаемые в виде миниатюр
            с информацией о координатах и дате съёмки.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Могу ли я поделиться фильтром с коллегой?</h3>
          <Paragraph>
            Да! Фильтры на странице <strong>Фильтрация и карта</strong> синхронизированы
            с URL страницы. Просто скопируйте адрес из адресной строки браузера и отправьте
            коллеге. Он увидит точно такие же применённые фильтры, когда откроет ссылку
            (при условии, что у него есть необходимые права доступа).
          </Paragraph>
        </div>
      </div>
    ),
    en: (
      <div className="space-y-6">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Why does my photo have red status?</h3>
          <Paragraph>
            Red status (<em>Unassigned</em>) means the photo's GPS coordinates do not fall
            inside any parcel polygon and are not within 200m of any polygon. Possible
            causes:
          </Paragraph>
          <BulletList
            items={[
              'The device GPS was not accurate enough at the time of capture',
              'The corresponding parcel has not yet been imported into the system',
              'The photo was taken in an area without registered parcels',
            ]}
          />
          <Paragraph>
            <strong>Solution:</strong> an administrator can manually assign a parcel to the photo
            from the Database page using the <em>Assign parcel</em> option.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">What do the sign-in error messages mean?</h3>
          <BulletList
            items={[
              <><strong>"Too many sign-in attempts. Try again in a few minutes."</strong>: after 5 failed attempts in 15 minutes, sign-in is blocked temporarily. Wait a few minutes and check your password.</>,
              <><strong>"The server is temporarily unavailable. Please try again later."</strong>: the problem is on the server, not with your account. Try again later.</>,
              <><strong>"Invalid credentials"</strong>: the username or password is wrong.</>,
            ]}
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">How do I change the interface language?</h3>
          <Paragraph>There are two ways to change the language:</Paragraph>
          <NumberedList
            items={[
              <>From the <strong>language selector</strong> in the page header — changes the language immediately for the current session.</>,
              <>From the <strong>Profile</strong> page, <em>Personal data</em> section, <em>Preferred language</em> field — saves the preference permanently to your account.</>,
            ]}
          />
          <Paragraph>Available languages are: Romanian, Russian, and English.</Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">What do I do if there is no internet in the field?</h3>
          <Paragraph>
            The PNERP mobile app includes a fully functional <strong>offline mode</strong>:
          </Paragraph>
          <BulletList
            items={[
              'You can capture photos without an internet connection',
              'Photos are saved locally on the device with all metadata (GPS coordinates, date, time)',
              'When the connection is restored, photos sync automatically with the server',
              'You can configure sync to work only on WiFi from the app settings',
            ]}
          />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">How do I import data from Excel?</h3>
          <Paragraph>
            Go to the <strong>Import</strong> page from the sidebar menu and follow the 4-step
            wizard:
          </Paragraph>
          <NumberedList
            items={[
              <>Select the Excel file (<Code>meta_3.xlsx</Code> for parcels or <Code>meta_3_photo.xlsx</Code> for photos)</>,
              'Review the data preview',
              'Confirm column mapping',
              'Click Confirm to complete the import',
            ]}
          />
          <Paragraph>
            You can download Excel templates from the import page to ensure your file structure
            is correct.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Who can delete parcels or photos?</h3>
          <Paragraph>The delete operation is restricted to the following roles:</Paragraph>
          <BulletList
            items={[
              <><strong>Super Admin</strong> — can delete any parcel or photo in the system</>,
              <><strong>Administrator</strong> — can only delete their own parcels and photos or those of their assistants</>,
              <><strong>Ajutor Administrator</strong> — can delete data created within the parent administrator's entity</>,
            ]}
          />
          <WarningBox>
            The <strong>Controlor</strong> role does not have delete rights regardless of
            circumstances. This restriction protects data integrity during the verification
            process.
          </WarningBox>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">What is a forestry entity and why do I need one?</h3>
          <Paragraph>
            A <strong>forestry entity</strong> is the organizational unit (for example, a forestry
            office or forestry directorate) you belong to. It determines:
          </Paragraph>
          <BulletList
            items={[
              'What data you can view and manage',
              'Available values in dropdowns (districts, municipalities, forestry offices)',
              'Data scope for import and creation',
            ]}
          />
          <Paragraph>
            The forestry entity is assigned by an administrator. Without this assignment, you
            cannot create parcels, import data, or sync photos.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">How can I see all photos of a parcel?</h3>
          <Paragraph>
            Navigate to the <strong>Parcels</strong> page, find the desired parcel in the table,
            and open the actions menu. Select the view details option — on the details page you
            will find all photos associated with that parcel, displayed as thumbnails with
            coordinate and capture date information.
          </Paragraph>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Can I share a filter with a colleague?</h3>
          <Paragraph>
            Yes! Filters on the <strong>Filters & Map</strong> page are synchronized with the
            page URL. Simply copy the address from the browser's address bar and send it to your
            colleague. They will see exactly the same filters applied when they open the link
            (assuming they have the necessary access rights).
          </Paragraph>
        </div>
      </div>
    ),
  };

  const titles: Record<Lang, string> = {
    ro: 'Intrebari frecvente',
    ru: 'Часто задаваемые вопросы',
    en: 'FAQ',
  };

  return { id: 'faq', title: titles[lang], icon: HelpCircle, content: content[lang] };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Video instructions content. Fetches the catalogue from /api/videos
 * (auth-required list) and renders inline `<video>` players with the
 * title, description, and a copyable public URL — the URL itself is
 * an R2 CDN link that works without auth.
 */
function VideoInstructionsContent({ lang }: { lang: Lang }) {
  const labels = videoLabels[lang];
  const { data, isLoading } = useQuery<{ items: VideoInstructionDto[] }>({
    queryKey: ['videos'],
    queryFn: () => api.get('videos').json<{ items: VideoInstructionDto[] }>(),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <Paragraph>{labels.intro}</Paragraph>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <InfoBox>{labels.empty}</InfoBox>
      ) : (
        <div className="space-y-6">
          {items.map((v) => (
            <article
              key={v.id}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
            >
              <video
                src={v.publicUrl}
                controls
                preload="metadata"
                className="w-full bg-black"
                style={{ maxHeight: 420 }}
              />
              <div className="space-y-2 p-4">
                <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {v.title}
                </h4>
                {v.description ? (
                  <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {v.description}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                  <a
                    href={v.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-forest-700 hover:underline dark:text-forest-300"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {labels.openLink}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(v.publicUrl);
                    }}
                    className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
                  >
                    <Copy className="h-3 w-3" />
                    {labels.copyLink}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const videoLabels: Record<
  Lang,
  { intro: string; empty: string; openLink: string; copyLink: string }
> = {
  ro: {
    intro:
      'Video-uri educaționale despre utilizarea platformei. Pot fi vizionate aici sau partajate prin linkul public — linkul deschide video-ul direct, fără autentificare.',
    empty: 'Niciun video încărcat momentan. Reveniți mai târziu.',
    openLink: 'Deschide linkul public',
    copyLink: 'Copiază linkul',
  },
  ru: {
    intro:
      'Обучающие видео по работе с платформой. Можно смотреть здесь или поделиться через публичную ссылку — она открывается напрямую, без авторизации.',
    empty: 'Видео ещё не загружены. Вернитесь позже.',
    openLink: 'Открыть публичную ссылку',
    copyLink: 'Скопировать ссылку',
  },
  en: {
    intro:
      'Tutorial videos about using the platform. Watch them here or share via the public URL — the link opens the video directly, without authentication.',
    empty: 'No videos uploaded yet. Check back later.',
    openLink: 'Open public link',
    copyLink: 'Copy link',
  },
};

function videoInstructionsSection(lang: Lang): DocSection {
  return {
    id: 'video-instructions',
    title:
      lang === 'ro'
        ? 'Video instrucțiuni'
        : lang === 'ru'
          ? 'Видео-инструкции'
          : 'Video instructions',
    icon: Film,
    content: <VideoInstructionsContent lang={lang} />,
  };
}

function whatsNewSection(lang: Lang): DocSection {
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          <strong>Versiunea 0.12.0</strong> (2026-09-26) aduce{' '}
          <strong>subpoligoanele</strong>, reimportul care actualizează poligoanele
          existente și o legătură mai strânsă între fotografii și poligoane.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Subpoligoane</strong>: subdiviziuni ale unui poligon, cu contur,
            suprafață și UCF complet (<Code>1A1</Code> = poligonul <Code>1A</Code>,
            subpoligonul <Code>1</Code>). Se importă dintr-un shapefile (cardul nou
            <strong> Subpoligoane</strong> pe pagina Import), apar cu{' '}
            <strong>portocaliu</strong> pe toate hărțile și sunt listate în fereastra
            Harta a poligonului.
          </li>
          <li>
            <strong>Recepțiile tehnice sunt subpoligoane</strong>: pagina Recepții tehnice și
            stratul albastru au fost eliminate. Fiecare recepție a devenit subpoligonul
            poligonului care o acoperă cel mai mult, numerotat în ordinea campaniilor{' '}
            (<Code>3A1</Code>, <Code>3A2</Code>); cele din afara oricărui poligon apar la{' '}
            <strong>Fără poligon</strong>. Importul arhivelor se face din cardul{' '}
            <strong>Subpoligoane din recepții</strong>. În lista <strong>Poligoane</strong>,
            un rând cu subpoligoane se deschide ca să le arate, iar căutarea și un filtru nou
            lucrează și pe subpoligoane.
          </li>
          <li>
            <strong>Reimport</strong>: un poligon sau subpoligon importat din nou își
            actualizează conturul și suprafața din fișierul nou, în loc să fie sărit ca
            „există deja"; rezultatul arată separat rândurile noi, actualizate și
            neschimbate.
          </li>
          <li>
            <strong>Baza de date</strong>: coloane noi <strong>Suprafață (ha)</strong> (a
            poligonului) și <strong>Subpoligon</strong>; căutarea și filtrele țin cont și de
            datele poligonului; link direct <Code>?photo=</Code> către o fotografie.
          </li>
          <li>
            <strong>Editarea fotografiilor</strong>: Administratorul editează toate
            fotografiile din entitățile sale, Ajutorul doar pe cele încărcate de el.
          </li>
          <li>
            <strong>Poligoane</strong>: UCF-ul este stocat complet (<Code>3A</Code>, nu doar{' '}
            <Code>A</Code>); fotografiile de lângă margine sunt legate de poligonul cel mai
            apropiat (sub 200 m) fără a li se muta coordonatele; harta poligonului arată
            fotografiile lui, cu autor, an, SB, anul culturii și ultima lucrare.
          </li>
          <li>
            <strong>Conectare</strong>: mesaje clare când sunt prea multe încercări sau când
            serverul nu este disponibil, în loc de „Bad request".
          </li>
        </BulletList>
        <Paragraph>
          Detaliile versiunii anterioare (0.11.0) urmează mai jos.
        </Paragraph>
        <Paragraph>
          <strong>Versiunea 0.11.0</strong> aduce modulul{' '}
          <strong>Recepții tehnice</strong>, importuri noi de poligoane și
          hărți care urmează filtrele.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Recepții tehnice</strong>: modul nou, cu import dintr-un
            folder de arhive <Code>.zip</Code>, pagină proprie cu filtre și
            hartă, iar poligoanele apar <strong>pe toate hărțile</strong>, cu
            <strong> albastru</strong>, distinct de verdele poligoanelor.
          </li>
          <li>
            <strong>Import poligoane</strong>: straturile livrate din
            GeoPackage se importă corect (anterior nu trecea niciun rând), iar
            terenurile deja acoperite de subdiviziuni UCF sunt sărite, ca
            suprafețele să nu fie numărate de două ori.
          </li>
          <li>
            <strong>Vizibilitate</strong>: poligoanele importate ajung acum la
            utilizatorii entității silvice căreia îi aparțin, indiferent de
            varianta de scriere din fișier.
          </li>
          <li>
            <strong>Filtrare & hartă</strong>: cercurile se actualizează la
            orice filtru, inclusiv cele care privesc doar fotografiile, și
            dispar când nu mai au rezultate. Filtrul „Fără poligon"
            funcționează.
          </li>
          <li>
            <strong>Interfață</strong>: mai multe rânduri pe ecran pe
            Poligoane / Baza de date / Lucrări, hartă pe ecran complet și
            coloană nouă <strong>Nr.</strong> pe Baza de date.
          </li>
        </BulletList>
        <Paragraph>
          Detaliile versiunii anterioare (0.10.0) urmează mai jos.
        </Paragraph>
        <Paragraph>
          <strong>Versiunea 0.10.0</strong> este o actualizare majoră:
          aduce un sistem de notificări (web + push pe mobil), câmpuri
          silvice noi obligatorii la încărcarea fotografiei, pagina
          Utilizatori grupată pe roluri și administratori, plus numeroase
          rafinări pe teren.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Notificări</strong>: clopoțel în antet (web) + push pe
            mobil; Super Administratorul poate trimite notificări (după rol,
            entitate, raion sau utilizatori anume), plus notificări automate
            (comentariu nou, modificări de cont, versiune nouă).
          </li>
          <li>
            <strong>Câmpuri silvice pe fotografie</strong>: Primarii_Raion_Cod
            (auto din poligon), Specia de Bază (SB), Anul creării culturii
            silvice, Ultima lucrare executată și un comentariu obligatoriu —
            cu tabele de referință pentru specii și tipuri de lucrări.
          </li>
          <li>
            <strong>Baza de date</strong>: coloana Primarii_Raion_Cod în loc
            de numele fișierului, fără latitudine/longitudine, coloană
            Comentarii cu contor + modal, plus coloanele SB / An cultură /
            Ultima lucrare.
          </li>
          <li>
            <strong>Utilizatori</strong>: grupați pe Super Administratori,
            Controlori și fiecare Administrator cu ajutoarele sale; inactivii
            sunt ascunși implicit.
          </li>
          <li>
            <strong>Pe teren</strong>: fotografia se deschide ca overlay peste
            panoul poligonului (cu „Înapoi la poligon"), corecție GPS pentru
            poziții învechite, versiunea + platforma în overlay-ul foto,
            pictogramă nouă, antet de tabel fix.
          </li>
        </BulletList>
        <Paragraph>
          Mai jos rămân detaliile versiunii anterioare (0.9.6).
        </Paragraph>
        <SectionHeading>Rebrand „Pnerp"</SectionHeading>
        <Paragraph>
          Aplicația web, aplicația mobilă și metadatele iOS folosesc acum
          numele <strong>Pnerp</strong> (fost „Forest Moldova").
        </Paragraph>
        <SectionHeading>Straturi de referință ICAS pe hartă</SectionHeading>
        <BulletList>
          <li>
            Trei straturi noi, comutabile din widget-ul de straturi
            (dreapta-sus pe fiecare hartă): <strong>Entități</strong> (23
            limite de gestiune), <strong>Trupuri</strong> (3.601 trupuri de
            pădure) și <strong>Arborete</strong> (89.635 poligoane cu
            inventar de specii).
          </li>
          <li>
            Trupuri și Arborete se încarcă pe <strong>zone vizibile</strong>
            (dale vectoriale) — stratul se activează aproape instant chiar și
            cu zeci de mii de poligoane.
          </li>
          <li>
            <strong>Popup cu atribute complete</strong> la click pe orice
            strat; alegerea straturilor este memorată între file și sesiuni.
          </li>
        </BulletList>
        <SectionHeading>Import din shapefile</SectionHeading>
        <Paragraph>
          Pagina Import acceptă acum <strong>shapefile PNERP</strong> (3.530
          parcele) pe lângă xlsx — reproiectare automată a coordonatelor pe
          server, previzualizare și commit în 3 pași.
        </Paragraph>
        <SectionHeading>Filtrare & hartă reconstruite</SectionHeading>
        <BulletList>
          <li>
            Harta și tabelul de rezultate <strong>urmează acum
            filtrele</strong>; poligoanele se randează ca dale vectoriale
            rapide, cu <strong>bule de cluster pe entitate silvică</strong>
            la zoom mic.
          </li>
          <li>
            Filtre noi: <strong>Autor</strong> (multi-select),
            <strong> cod proiect + UCF</strong> pe Poligoane, coloană și
            filtru <strong>Entitate silvică</strong> pe Baza de date.
          </li>
          <li>
            <strong>Ultimul filtru este salvat</strong> și restaurat când
            reveniți pe pagină — până la Reset sau timp de o oră.
          </li>
          <li>
            Clickul pe o fotografie nu mai deschide și panoul poligonului;
            panoul de parcelă afișează mai multe detalii.
          </li>
        </BulletList>
        <SectionHeading>Entitate silvică — date canonice</SectionHeading>
        <Paragraph>
          Entitatea silvică folosește acum tabelul de referință drept sursă
          de adevăr (26 entități canonice). Filtrele arată pentru
          ADMINISTRATOR / AJUTOR doar valorile din entitățile proprii.
          CONTROLOR poate edita și atribui date pe toți utilizatorii, dar
          ștergerea rămâne doar pe rândurile proprii.
        </Paragraph>
        <SectionHeading>Aplicația mobilă pe teren</SectionHeading>
        <BulletList>
          <li>
            <strong>GPS mai precis</strong>: warm-up + prag de acuratețe
            înainte de captură (de la 50-100 m la 3-15 m); raza de acuratețe
            (±N m) este salvată și afișată pe fotografie și pe hartă.
          </li>
          <li>
            Overlay foto: eticheta <strong>„GPS:"</strong> + acuratețea
            <strong> ±N m</strong> pe aceeași linie cu coordonatele.
          </li>
          <li>
            <strong>Mod offline extins</strong> — cache pentru regiuni,
            cascade și opțiuni de entitate, astfel încât încărcarea
            funcționează fără rețea.
          </li>
          <li>
            Autocompletare „soft" a suprafeței din cea mai apropiată parcelă
            când punctul GPS e în afara oricărui poligon.
          </li>
        </BulletList>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          <strong>Версия 0.12.0</strong> (2026-09-26) добавляет{' '}
          <strong>подполигоны</strong>, повторный импорт, обновляющий существующие
          полигоны, и более тесную связь фотографий с полигонами.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Подполигоны</strong>: части полигона со своим контуром, площадью и
            полным UCF (<Code>1A1</Code> = полигон <Code>1A</Code>, подполигон{' '}
            <Code>1</Code>). Импортируются из shapefile (новая карточка{' '}
            <strong>Подполигоны</strong> на странице Импорт), отображаются{' '}
            <strong>оранжевым</strong> на всех картах и перечислены в окне Карта
            полигона.
          </li>
          <li>
            <strong>Технические приёмки стали подполигонами</strong>: страница Технические
            приёмки и синий слой удалены. Каждая приёмка стала подполигоном полигона, который
            покрывает её больше всего, с номером в порядке кампаний (<Code>3A1</Code>,{' '}
            <Code>3A2</Code>); приёмки вне любого полигона собраны в <strong>Без полигона</strong>.
            Архивы импортируются через карточку <strong>Подполигоны из приёмок</strong>. В
            списке <strong>Полигоны</strong> строка с подполигонами раскрывается, а поиск и
            новый фильтр работают и по подполигонам.
          </li>
          <li>
            <strong>Повторный импорт</strong>: повторно импортированный полигон или
            подполигон обновляет контур и площадь из нового файла вместо пропуска как
            «уже существует»; результат отдельно показывает новые, обновлённые и
            неизменённые строки.
          </li>
          <li>
            <strong>База данных</strong>: новые колонки <strong>Площадь (га)</strong> (полигона)
            и <strong>Подполигон</strong>; поиск и фильтры учитывают данные полигона; прямая
            ссылка <Code>?photo=</Code> на фотографию.
          </li>
          <li>
            <strong>Редактирование фотографий</strong>: Administrator редактирует все
            фотографии своих организаций, Ajutor только загруженные им самим.
          </li>
          <li>
            <strong>Полигоны</strong>: UCF хранится полностью (<Code>3A</Code>, а не только{' '}
            <Code>A</Code>); фотографии у границы привязываются к ближайшему полигону (до
            200 м) без смещения координат; карта полигона показывает его фотографии с
            автором, годом, SB, годом культуры и последней работой.
          </li>
          <li>
            <strong>Вход</strong>: понятные сообщения при слишком большом числе попыток и
            при недоступности сервера вместо «Bad request».
          </li>
        </BulletList>
        <Paragraph>
          Подробности предыдущей версии (0.11.0) приведены ниже.
        </Paragraph>
        <Paragraph>
          <strong>Версия 0.11.0</strong> добавляет модуль{' '}
          <strong>Технические приёмки</strong>, новые импорты полигонов и
          карты, которые следуют за фильтрами.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Технические приёмки</strong>: новый модуль с импортом из
            папки архивов <Code>.zip</Code>, отдельная страница с фильтрами и
            картой; полигоны отображаются <strong>на всех картах</strong>{' '}
            <strong>синим</strong>, отдельно от зелёных полигонов.
          </li>
          <li>
            <strong>Импорт полигонов</strong>: слои из GeoPackage теперь
            импортируются (раньше не проходила ни одна строка), а участки,
            уже покрытые подразделениями UCF, пропускаются, чтобы площади не
            считались дважды.
          </li>
          <li>
            <strong>Видимость</strong>: импортированные полигоны доходят до
            пользователей своей лесной организации независимо от написания в
            файле.
          </li>
          <li>
            <strong>Фильтры и карта</strong>: круги обновляются при любом
            фильтре, включая относящиеся только к фотографиям, и исчезают,
            когда результатов нет. Фильтр «Без полигона» работает.
          </li>
          <li>
            <strong>Интерфейс</strong>: больше строк на экране на страницах
            Полигоны / База данных / Работы, полноэкранная карта и новый
            столбец <strong>№</strong> в Базе данных.
          </li>
        </BulletList>
        <Paragraph>Детали предыдущей версии (0.10.0) — ниже.</Paragraph>
        <Paragraph>
          <strong>Версия 0.10.0</strong> — крупное обновление: система
          уведомлений (веб + push на мобильном), новые обязательные
          лесоводственные поля при загрузке фотографии, страница
          «Пользователи» с группировкой по ролям и администраторам, а также
          множество полевых улучшений.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Уведомления</strong>: колокольчик в шапке (веб) + push на
            мобильном; Супер-администратор может рассылать уведомления (по
            роли, лесному субъекту, району или конкретным пользователям), плюс
            автоматические уведомления (новый комментарий, изменения учётной
            записи, новая версия).
          </li>
          <li>
            <strong>Лесоводственные поля фотографии</strong>:
            Primarii_Raion_Cod (авто из полигона), основная порода (ОП), год
            создания лесной культуры, последняя выполненная работа и
            обязательный комментарий — со справочниками пород и видов работ.
          </li>
          <li>
            <strong>База данных</strong>: колонка Primarii_Raion_Cod вместо
            имени файла, без широты/долготы, колонка «Комментарии» со
            счётчиком и окном, а также колонки ОП / Год культуры / Последняя
            работа.
          </li>
          <li>
            <strong>Пользователи</strong>: сгруппированы по супер-админам,
            контролёрам и каждому администратору с его помощниками; неактивные
            скрыты по умолчанию.
          </li>
          <li>
            <strong>В поле</strong>: фотография открывается поверх панели
            полигона (с кнопкой «Назад к полигону»), исправление устаревшего
            GPS-фикса, версия + платформа в наложении фото, новая иконка,
            фиксированная шапка таблицы.
          </li>
        </BulletList>
        <Paragraph>
          Ниже — детали предыдущей версии (0.9.6).
        </Paragraph>
        <SectionHeading>Ребрендинг «Pnerp»</SectionHeading>
        <Paragraph>
          Веб-приложение, мобильное приложение и метаданные iOS теперь
          используют название <strong>Pnerp</strong> (ранее «Forest
          Moldova»).
        </Paragraph>
        <SectionHeading>Справочные слои ICAS на карте</SectionHeading>
        <BulletList>
          <li>
            Три новых слоя, переключаемых из виджета слоёв (вверху справа на
            каждой карте): <strong>Организации</strong> (23 границы
            управления), <strong>Урочища</strong> (3601 лесных урочищ) и
            <strong> Насаждения</strong> (89 635 полигонов с инвентаризацией
            пород).
          </li>
          <li>
            Урочища и Насаждения загружаются по <strong>видимой
            области</strong> (векторные тайлы) — слой включается почти
            мгновенно даже с десятками тысяч полигонов.
          </li>
          <li>
            <strong>Всплывающее окно с полными атрибутами</strong> при клике
            на любой слой; выбор слоёв запоминается между вкладками и
            сессиями.
          </li>
        </BulletList>
        <SectionHeading>Импорт из shapefile</SectionHeading>
        <Paragraph>
          Страница импорта теперь принимает <strong>shapefile PNERP</strong>
          (3530 участков) наряду с xlsx — автоматическая перепроекция
          координат на сервере, предпросмотр и коммит в 3 шага.
        </Paragraph>
        <SectionHeading>Переработанные фильтры и карта</SectionHeading>
        <BulletList>
          <li>
            Карта и таблица результатов <strong>теперь следуют за
            фильтрами</strong>; полигоны отрисовываются как быстрые векторные
            тайлы с <strong>кластерными пузырями по лесной организации</strong>
            при малом масштабе.
          </li>
          <li>
            Новые фильтры: <strong>Автор</strong> (мультивыбор),
            <strong> код проекта + UCF</strong> на Полигонах, колонка и фильтр
            <strong> Лесная организация</strong> в Базе данных.
          </li>
          <li>
            <strong>Последний фильтр сохраняется</strong> и восстанавливается
            при возврате на страницу — до сброса или в течение одного часа.
          </li>
          <li>
            Клик по фотографии больше не открывает панель полигона; панель
            участка показывает больше деталей.
          </li>
        </BulletList>
        <SectionHeading>Лесная организация — канонические данные</SectionHeading>
        <Paragraph>
          Лесная организация теперь использует справочную таблицу как
          источник истины (26 канонических организаций). Фильтры показывают
          для ADMINISTRATOR / AJUTOR только значения из их организаций.
          CONTROLOR может редактировать и назначать данные по всем
          пользователям, но удаление остаётся только для собственных строк.
        </Paragraph>
        <SectionHeading>Мобильное приложение для полевой работы</SectionHeading>
        <BulletList>
          <li>
            <strong>Более точный GPS</strong>: прогрев + порог точности перед
            съёмкой (с 50-100 м до 3-15 м); радиус точности (±N м)
            сохраняется и отображается на фото и на карте.
          </li>
          <li>
            Наложение на фото: метка <strong>«GPS:»</strong> + точность
            <strong> ±N м</strong> на одной строке с координатами.
          </li>
          <li>
            <strong>Расширенный офлайн-режим</strong> — кэш регионов, каскадов
            и опций организаций, так что загрузка работает без сети.
          </li>
          <li>
            «Мягкое» автозаполнение площади из ближайшего участка, когда точка
            GPS вне любого полигона.
          </li>
        </BulletList>
      </>
    ),
    en: (
      <>
        <Paragraph>
          <strong>Version 0.12.0</strong> (2026-09-26) adds{' '}
          <strong>subpolygons</strong>, re-imports that refresh existing polygons, and a
          closer link between photos and polygons.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Subpolygons</strong>: subdivisions of a polygon with their own outline,
            area and full UCF (<Code>1A1</Code> = polygon <Code>1A</Code>, subpolygon{' '}
            <Code>1</Code>). Imported from a shapefile (new <strong>Subpolygons</strong> card
            on the Import page), shown in <strong>orange</strong> on every map, and listed in
            the polygon's Map window.
          </li>
          <li>
            <strong>Technical receptions are subpolygons</strong>: the Technical receptions page
            and the blue layer are gone. Each reception became a subpolygon of the polygon
            covering most of it, numbered in campaign order (<Code>3A1</Code>,{' '}
            <Code>3A2</Code>); those in no polygon are listed under <strong>No polygon</strong>.
            Archives are imported through the <strong>Subpolygons from receptions</strong> card.
            In the <strong>Polygons</strong> list a row with subpolygons expands to show them,
            and search plus a new filter work on subpolygons too.
          </li>
          <li>
            <strong>Re-import</strong>: a polygon or subpolygon imported again takes its
            outline and area from the new file instead of being skipped as "already
            exists"; the result shows new, updated and unchanged rows separately.
          </li>
          <li>
            <strong>Database</strong>: new <strong>Area (ha)</strong> (the polygon's) and{' '}
            <strong>Subpolygon</strong> columns; search and filters also use the polygon's
            data; a direct <Code>?photo=</Code> link to a photo.
          </li>
          <li>
            <strong>Editing photos</strong>: an Administrator edits every photo in their
            entities, an Ajutor only the ones they uploaded.
          </li>
          <li>
            <strong>Polygons</strong>: the UCF is stored in full (<Code>3A</Code>, not just{' '}
            <Code>A</Code>); photos near a border are linked to the nearest polygon (within
            200 m) without moving their coordinates; the polygon map shows its photos with
            author, year, SB, culture year and last operation.
          </li>
          <li>
            <strong>Sign-in</strong>: clear messages for too many attempts and for a server
            outage, instead of "Bad request".
          </li>
        </BulletList>
        <Paragraph>
          Details of the previous version (0.11.0) follow below.
        </Paragraph>
        <Paragraph>
          <strong>Version 0.11.0</strong> adds the{' '}
          <strong>Technical receptions</strong> module, new polygon imports,
          and maps that follow the filters.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Technical receptions</strong>: a new module importing a
            folder of <Code>.zip</Code> archives, its own page with filters
            and a map, and polygons shown <strong>on every map</strong> in{' '}
            <strong>blue</strong>, distinct from the green parcels.
          </li>
          <li>
            <strong>Polygon import</strong>: GeoPackage-exported layers now
            import (previously not a single row got through), and land plots
            already covered by their UCF subdivisions are skipped so hectares
            are not counted twice.
          </li>
          <li>
            <strong>Visibility</strong>: imported polygons now reach the users
            of the forestry entity they belong to, whatever spelling the file
            used.
          </li>
          <li>
            <strong>Filters and map</strong>: the bubbles update on any
            filter, including photo-only ones, and disappear when they have no
            results. The "Without polygon" filter works.
          </li>
          <li>
            <strong>Interface</strong>: more rows on screen across Polygons /
            Database / Works, a fullscreen map, and a new <strong>No.</strong>{' '}
            column on the Database.
          </li>
        </BulletList>
        <Paragraph>The previous release (0.10.0) details follow below.</Paragraph>
        <Paragraph>
          <strong>Version 0.10.0</strong> is a major update: a notification
          system (web + mobile push), new required silviculture fields on
          photo upload, a grouped Users page (by role and administrator),
          plus many field-workflow refinements.
        </Paragraph>
        <BulletList>
          <li>
            <strong>Notifications</strong>: a header bell (web) + mobile push;
            the Super Admin can broadcast notifications (by role, forestry
            entity, district, or specific users), plus automatic ones (new
            comment, account changes, new version).
          </li>
          <li>
            <strong>Photo silviculture fields</strong>: Primarii_Raion_Cod
            (auto-filled from the polygon), Base species (SB), culture-creation
            year, last operation performed, and a required comment — backed by
            new species + work-type reference tables.
          </li>
          <li>
            <strong>Database</strong>: a Primarii_Raion_Cod column in place of
            the file name, no latitude/longitude, a Comments column with a
            count + modal, plus SB / Culture year / Last operation columns.
          </li>
          <li>
            <strong>Users</strong>: grouped into Super Admins, Controllers, and
            each Administrator with their assistants; inactive users hidden by
            default.
          </li>
          <li>
            <strong>In the field</strong>: a photo opens as an overlay above
            the polygon panel (with "Back to polygon"), a stale-GPS-fix
            correction, app version + platform burned into the photo overlay, a
            new app icon, and a fixed table header.
          </li>
        </BulletList>
        <Paragraph>
          The previous release (0.9.6) details follow below.
        </Paragraph>
        <SectionHeading>"Pnerp" rebrand</SectionHeading>
        <Paragraph>
          The web app, mobile app, and iOS metadata now use the name
          <strong> Pnerp</strong> (formerly "Forest Moldova").
        </Paragraph>
        <SectionHeading>ICAS reference layers on the map</SectionHeading>
        <BulletList>
          <li>
            Three new layers, toggleable from the layers widget (top-right
            on every map): <strong>Entities</strong> (23 management
            boundaries), <strong>Forest bodies</strong> (3,601 "trupuri")
            and <strong>Stands</strong> (89,635 polygons with species
            inventory).
          </li>
          <li>
            Forest bodies and Stands load <strong>per visible area</strong>
            (vector tiles) — the layer turns on almost instantly even with
            tens of thousands of polygons.
          </li>
          <li>
            <strong>Full attribute popup</strong> on click for any layer;
            the layer choice is remembered across tabs and sessions.
          </li>
        </BulletList>
        <SectionHeading>Shapefile import</SectionHeading>
        <Paragraph>
          The Import page now accepts a <strong>PNERP shapefile</strong>
          (3,530 parcels) alongside xlsx — automatic server-side coordinate
          reprojection, preview, and a 3-step commit.
        </Paragraph>
        <SectionHeading>Rebuilt filters and map</SectionHeading>
        <BulletList>
          <li>
            The map and the results table <strong>now follow the active
            filters</strong>; polygons render as fast vector tiles with
            <strong> cluster bubbles by forestry entity</strong> at low zoom.
          </li>
          <li>
            New filters: <strong>Author</strong> (multi-select),
            <strong> project code + UCF</strong> on Polygons, an
            <strong> Forestry entity</strong> column and filter on the
            Database.
          </li>
          <li>
            <strong>The latest filter is saved</strong> and restored when you
            return to the page — until Reset or for one hour.
          </li>
          <li>
            Clicking a photo no longer also opens the polygon panel; the
            parcel panel shows more detail.
          </li>
        </BulletList>
        <SectionHeading>Forestry entity — canonical data</SectionHeading>
        <Paragraph>
          The forestry entity now uses the reference table as the source of
          truth (26 canonical entities). Filters show only the values from
          their own entities for ADMINISTRATOR / AJUTOR. CONTROLOR can edit
          and assign data across all users, but deletion stays own-rows-only.
        </Paragraph>
        <SectionHeading>Mobile app for field work</SectionHeading>
        <BulletList>
          <li>
            <strong>More accurate GPS</strong>: warm-up + accuracy gate
            before capture (from 50-100 m down to 3-15 m); the accuracy
            radius (±N m) is saved and shown on the photo and the map.
          </li>
          <li>
            Photo overlay: the <strong>"GPS:"</strong> label + accuracy
            <strong> ±N m</strong> on the same line as the coordinates.
          </li>
          <li>
            <strong>Extended offline mode</strong> — caches regions,
            cascades, and entity options so uploads work without a network.
          </li>
          <li>
            "Soft" area autofill from the nearest parcel when the GPS point
            is outside any polygon.
          </li>
        </BulletList>
      </>
    ),
  };
  return {
    id: 'whats-new',
    title:
      lang === 'ro'
        ? 'Ce este nou în 0.12.0'
        : lang === 'ru'
          ? 'Что нового в 0.12.0'
          : "What's new in 0.12.0",
    icon: Rocket,
    content: content[lang],
  };
}


// ---------------------------------------------------------------
// Recepții tehnice
// ---------------------------------------------------------------
function receptiiSection(lang: Lang): DocSection {
  const titles: Record<Lang, string> = {
    ro: 'Recepții tehnice (subpoligoane)',
    ru: 'Технические приёмки (подполигоны)',
    en: 'Technical receptions (subpolygons)',
  };
  const content: Record<Lang, ReactNode> = {
    ro: (
      <>
        <Paragraph>
          O recepție tehnică este poligonul acceptat de comisie la recepția pe teren, într-o
          campanie de <strong>primăvară</strong> sau <strong>toamnă</strong>. Din 2026-09-27
          recepțiile <strong>sunt subpoligoane</strong>: apar sub poligonul în care au fost
          făcute, în lista <strong>Poligoane</strong>, și cu portocaliu pe hărți. Pagina separată
          Recepții tehnice și stratul albastru nu mai există; vechiul link duce la Poligoane.
        </Paragraph>
        <InfoBox>
          Recepțiile deja importate au fost mutate automat. Fiecare a primit poligonul care
          acoperă <strong>cea mai mare parte</strong> din ea (recepțiile ies adesea puțin din
          poligon sau au în numele arhivei UCF-ul unui vecin, deci nici conturul, nici numele nu
          decid singure) și un număr în acel poligon, în ordinea campaniilor, de la cea mai veche:{' '}
          <Code>3A1</Code>, <Code>3A2</Code>. UCF-ul se ia de la poligon, nu din numele arhivei.
          Campania, parcelele, arhiva sursă, primăria, raionul, entitatea și ambele suprafețe s-au
          păstrat.
        </InfoBox>
        <SectionHeading>Cum se importă</SectionHeading>
        <NumberedList
          items={[
            <>Mergeți la <strong>Import</strong> și alegeți cartonașul <strong>Subpoligoane din recepții</strong>.</>,
            <>Apăsați <strong>Alege folder</strong> și selectați folderul campaniei (ex. <Code>Comrat rec. tehn. pr.2024</Code>) sau folderul care le conține pe toate.</>,
            <>Verificați previzualizarea: câte arhive sunt valide, câte sunt deja importate (acestea se vor actualiza) și care au fost sărite, cu motivul.</>,
            <>Apăsați <strong>Confirmă</strong>.</>,
          ]}
        />
        <Paragraph>
          Datele nu se completează manual: se citesc din numele folderului și al
          arhivei. Din <Code>Comrat rec. tehn. pr.2024/Cazaclia_7A_537-551_3,0.zip</Code>{' '}
          rezultă campania <em>primăvara 2024</em>, primăria <em>Cazaclia</em>,
          UCF <em>7A</em>, parcelele <em>537-551</em> și <em>3,0 ha</em>.
        </Paragraph>
        <WarningBox>
          Selectați întotdeauna un <strong>folder</strong>, nu fișiere separate:
          anul și sezonul există doar în numele folderului. Dacă alegeți fișiere
          răzlețe, va trebui să indicați manual anul și sezonul.
        </WarningBox>
        <SectionHeading>Ce se întâmplă cu fiecare arhivă</SectionHeading>
        <BulletList
          items={[
            <><strong>Recepție nouă</strong>: devine subpoligonul poligonului care o acoperă cel mai mult și primește următorul număr liber din acel poligon.</>,
            <><strong>Recepție deja importată</strong> (aceeași arhivă): își actualizează conturul și suprafața din arhiva nouă; poligonul și numărul rămân aceleași.</>,
            <><strong>Recepție în afara oricărui poligon</strong>: rămâne subpoligon <em>fără poligon</em>, cu UCF-ul din numele arhivei și fără număr, desenată punctat pe hartă. Butonul <em>Fără poligon (N)</em> de pe pagina Poligoane le listează, cu hartă. După importul poligonului, reimportați arhiva și subpoligonul se atașează automat.</>,
          ]}
        />
        <SectionHeading>Cele două suprafețe</SectionHeading>
        <Paragraph>
          Fiecare subpoligon din recepție păstrează <strong>suprafața declarată</strong> (din
          numele arhivei) și <strong>suprafața calculată</strong> din conturul desenat. Fereastra
          de pe hartă o arată pe cea calculată când diferă de cea declarată; diferența este un caz
          de verificat, nu o eroare a aplicației.
        </Paragraph>
        <SectionHeading>Arhive sărite</SectionHeading>
        <Paragraph>
          O arhivă care conține doar traseu GPS (puncte sau linii), fără contur
          închis, este raportată ca sărită. Nu este o eroare de import: arhiva
          nu conține nicio suprafață.
        </Paragraph>
      </>
    ),
    ru: (
      <>
        <Paragraph>
          Техническая приёмка это полигон, принятый комиссией на местности в{' '}
          <strong>весеннюю</strong> или <strong>осеннюю</strong> кампанию. С 2026-09-27 приёмки{' '}
          <strong>являются подполигонами</strong>: они показываются под полигоном, в котором были
          сделаны, в списке <strong>Полигоны</strong>, и оранжевым цветом на картах. Отдельной
          страницы Технические приёмки и синего слоя больше нет; старая ссылка ведёт на Полигоны.
        </Paragraph>
        <InfoBox>
          Уже импортированные приёмки перенесены автоматически. Каждая получила полигон, который
          покрывает <strong>большую её часть</strong> (приёмки часто немного выходят за полигон
          или несут в имени архива UCF соседа, поэтому ни контур, ни имя сами по себе не решают), и
          номер в этом полигоне в порядке кампаний, начиная с самой старой: <Code>3A1</Code>,{' '}
          <Code>3A2</Code>. UCF берётся у полигона, а не из имени архива. Кампания, участки,
          исходный архив, примэрия, район, организация и обе площади сохранены.
        </InfoBox>
        <SectionHeading>Как импортировать</SectionHeading>
        <NumberedList
          items={[
            <>Откройте <strong>Импорт</strong> и выберите карточку <strong>Подполигоны из приёмок</strong>.</>,
            <>Нажмите <strong>Выбрать папку</strong> и укажите папку кампании (напр. <Code>Comrat rec. tehn. pr.2024</Code>) или папку, содержащую все кампании.</>,
            <>Проверьте предпросмотр: сколько архивов пригодны, сколько уже импортировано (они будут обновлены) и какие пропущены, с причиной.</>,
            <>Нажмите <strong>Подтвердить</strong>.</>,
          ]}
        />
        <Paragraph>
          Данные не вводятся вручную: они читаются из имён папки и архива. Из{' '}
          <Code>Comrat rec. tehn. pr.2024/Cazaclia_7A_537-551_3,0.zip</Code>{' '}
          получаются кампания <em>весна 2024</em>, примэрия <em>Cazaclia</em>,
          UCF <em>7A</em>, участки <em>537-551</em> и <em>3,0 га</em>.
        </Paragraph>
        <WarningBox>
          Всегда выбирайте <strong>папку</strong>, а не отдельные файлы: год и
          сезон есть только в имени папки. При выборе отдельных файлов год и
          сезон придётся указать вручную.
        </WarningBox>
        <SectionHeading>Что происходит с каждым архивом</SectionHeading>
        <BulletList
          items={[
            <><strong>Новая приёмка</strong>: становится подполигоном того полигона, который покрывает её больше всего, и получает следующий свободный номер в этом полигоне.</>,
            <><strong>Уже импортированная приёмка</strong> (тот же архив): обновляет контур и площадь из нового архива; полигон и номер остаются прежними.</>,
            <><strong>Приёмка вне любого полигона</strong>: остаётся подполигоном <em>без полигона</em>, с UCF из имени архива и без номера, и рисуется на карте пунктиром. Кнопка <em>Без полигона (N)</em> на странице Полигоны показывает их список с картой. После импорта полигона импортируйте архив повторно, и подполигон прикрепится автоматически.</>,
          ]}
        />
        <SectionHeading>Две площади</SectionHeading>
        <Paragraph>
          Каждый подполигон из приёмки хранит <strong>заявленную площадь</strong> (из имени
          архива) и <strong>рассчитанную</strong> по контуру. Окно на карте показывает
          рассчитанную, если она отличается от заявленной; расхождение это повод проверить, а не
          ошибка приложения.
        </Paragraph>
        <SectionHeading>Пропущенные архивы</SectionHeading>
        <Paragraph>
          Архив, содержащий только GPS-трек (точки или линии) без замкнутого
          контура, отмечается как пропущенный. Это не ошибка импорта: в архиве
          нет площади.
        </Paragraph>
      </>
    ),
    en: (
      <>
        <Paragraph>
          A technical reception is the polygon a commission accepted on the ground, in a{' '}
          <strong>spring</strong> or <strong>autumn</strong> campaign. Since 2026-09-27 receptions{' '}
          <strong>are subpolygons</strong>: they show under the polygon they were surveyed in, in
          the <strong>Polygons</strong> list, and in orange on the maps. The separate Technical
          receptions page and the blue layer are gone; the old link leads to Polygons.
        </Paragraph>
        <InfoBox>
          Receptions already imported were moved automatically. Each got the polygon covering{' '}
          <strong>most of it</strong> (receptions often overhang their polygon a little or carry a
          neighbour's UCF in the archive name, so neither the outline nor the name decides alone)
          and a number in that polygon in campaign order, oldest first: <Code>3A1</Code>,{' '}
          <Code>3A2</Code>. The UCF comes from the polygon, not from the archive name. The
          campaign, parcels, source archive, mayoralty, district, entity and both areas were kept.
        </InfoBox>
        <SectionHeading>How to import</SectionHeading>
        <NumberedList
          items={[
            <>Go to <strong>Import</strong> and pick the <strong>Subpolygons from receptions</strong> card.</>,
            <>Press <strong>Choose folder</strong> and select the campaign folder (e.g. <Code>Comrat rec. tehn. pr.2024</Code>) or the folder holding all of them.</>,
            <>Check the preview: how many archives are valid, how many are already imported (those will be updated), and which were skipped, with the reason.</>,
            <>Press <strong>Confirm</strong>.</>,
          ]}
        />
        <Paragraph>
          Nothing is typed in by hand: the data is read from the folder and
          archive names. <Code>Comrat rec. tehn. pr.2024/Cazaclia_7A_537-551_3,0.zip</Code>{' '}
          yields the <em>spring 2024</em> campaign, the <em>Cazaclia</em>{' '}
          mayoralty, UCF <em>7A</em>, parcels <em>537-551</em> and{' '}
          <em>3.0 ha</em>.
        </Paragraph>
        <WarningBox>
          Always pick a <strong>folder</strong>, not loose files: the year and
          season exist only in the folder name. If you select loose files you
          will have to state the year and season by hand.
        </WarningBox>
        <SectionHeading>What happens to each archive</SectionHeading>
        <BulletList
          items={[
            <><strong>New reception</strong>: becomes a subpolygon of the polygon covering most of it and gets the next free number in that polygon.</>,
            <><strong>Reception already imported</strong> (same archive): its outline and area are refreshed from the new archive; its polygon and number stay the same.</>,
            <><strong>Reception outside every polygon</strong>: stays a subpolygon with <em>no polygon</em>, keeping the UCF from the archive name and no number, drawn dashed on the map. The <em>No polygon (N)</em> button on the Polygons page lists them, with a map. Once the polygon is imported, import the archive again and the subpolygon attaches automatically.</>,
          ]}
        />
        <SectionHeading>The two areas</SectionHeading>
        <Paragraph>
          Every subpolygon from a reception keeps the <strong>declared area</strong> (from the
          archive name) and the <strong>computed area</strong> from the drawn outline. The map
          popup shows the computed one when it differs from the declared one; a difference is
          something to check, not an application error.
        </Paragraph>
        <SectionHeading>Skipped archives</SectionHeading>
        <Paragraph>
          An archive holding only a GPS trace (points or lines) with no closed
          outline is reported as skipped. That is not an import error: the
          archive contains no area.
        </Paragraph>
      </>
    ),
  };
  return { id: 'receptii', title: titles[lang], icon: ClipboardCheck, content: content[lang] };
}

export function getDocSections(lang: Lang): DocSection[] {
  return [
    overviewSection(lang),
    videoInstructionsSection(lang),
    whatsNewSection(lang),
    gettingStartedSection(lang),
    rolesSection(lang),
    parcelsSection(lang),
    photosSection(lang),
    importSection(lang),
    receptiiSection(lang),
    filterMapSection(lang),
    mobileSection(lang),
    adminSection(lang),
    profileSection(lang),
    architectureSection(lang),
    faqSection(lang),
  ];
}

// Page-level translated strings
export function getPageStrings(lang: Lang) {
  const strings: Record<Lang, {
    pageTitle: string;
    pageSubtitle: string;
    sidebarTitle: string;
    searchPlaceholder: string;
    searchPlaceholderShort: string;
    copyLinkTitle: string;
    footerLine1: string;
    footerLine2: string;
  }> = {
    ro: {
      pageTitle: 'Documentatie PNERP',
      pageSubtitle: 'Ghid complet pentru utilizarea platformei Programului National de Extindere a Resurselor de Paduri',
      sidebarTitle: 'Documentatie',
      searchPlaceholder: 'Cauta in documentatie...',
      searchPlaceholderShort: 'Cauta...',
      copyLinkTitle: 'Copiaza link-ul sectiunii',
      footerLine1: 'PNERP — Programul National de Extindere a Resurselor de Paduri',
      footerLine2: 'Documentatie actualizata: Aprilie 2026',
    },
    ru: {
      pageTitle: 'Документация PNERP',
      pageSubtitle: 'Полное руководство по использованию платформы Национальной программы расширения лесных ресурсов',
      sidebarTitle: 'Документация',
      searchPlaceholder: 'Поиск в документации...',
      searchPlaceholderShort: 'Поиск...',
      copyLinkTitle: 'Копировать ссылку на раздел',
      footerLine1: 'PNERP — Национальная программа расширения лесных ресурсов',
      footerLine2: 'Документация обновлена: Апрель 2026',
    },
    en: {
      pageTitle: 'PNERP Documentation',
      pageSubtitle: 'Complete guide for using the National Program for Forest Resource Expansion platform',
      sidebarTitle: 'Documentation',
      searchPlaceholder: 'Search documentation...',
      searchPlaceholderShort: 'Search...',
      copyLinkTitle: 'Copy section link',
      footerLine1: 'PNERP — National Program for Forest Resource Expansion',
      footerLine2: 'Documentation updated: April 2026',
    },
  };

  return strings[lang];
}
